import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, subtleItemVariants } from './dashboardMotion';
import { database, auth, storage } from '../firebaseConfig';
import { ref, onValue, update, set, get, remove, serverTimestamp } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { normalizeReport } from '../Research/reportSchema';
import {
  WARMUP_RAMP_DAYS,
  warmupDateKey,
  warmupDayIndex,
  warmupWorkdayDate,
  warmupSubmittedLate,
  isWarmupWeekend,
  fmtWarmupDate,
} from '../Research/warmupUtils';
import { computeReportMetrics, formatMoneyCompact } from '../Research/reportMetrics';
import {
  PROTOCOOL_LINKS,
  getProtocoolForm,
  allFormFields,
  formCompletion,
  isFieldFilled,
  buildProtocoolShareMessage,
} from '../Protocool/protocoolConfig';
import {
  PHASES,
  LOST_PHASE,
  LOST_SUPER_STATUS_IDS,
  isLostSuperStatus,
  LOST_REASON_CUSTOM,
  isCustomLostReason,
  COLUMNS,
  CHECKLISTS,
  buildCustomerObject,
  phaseForSuperStatus,
  checklistItems,
  cumulativeChecklistItems,
  computeAutoChecklist,
  computeStatusDurations,
  formatCountdown,
  formatDuration,
  DEFAULT_SUPER_STATUS,
  FINAL_SUPER_STATUS,
  SALES_PHASE_ID,
  PRESENTATION_SUPER_STATUS,
  OFFER_SIGNED_SUPER_STATUS,
  PREPARATION_PHASE_ID,
  SETUP_SUPER_STATUS,
  KICKOFF_SUPER_STATUS,
  OPERATIVE_PHASE_ID,
  quickActionsFor,
  deriveDealFlags,
  suggestedLostStatus,
  hasReachedSuperStatus,
} from './structureConfig';
import {
  nextDuePeriod,
  contractRunning,
  memberMonthlyPayout,
  monthsElapsed,
  fmtEur as fmtEuro,
  fmtDate as fmtDay,
  taxBreakdown,
  taxModeInfo,
  taxModeForCountry,
  countryFromWebsite,
  countryName,
  COUNTRY_OPTIONS,
  reportingPeriods,
  reportingId,
  COMMISSION_ROLES,
  COMMISSION_PLAN_BASE,
  commissionForMember,
  commissionRoleForMember,
  customerRoleOverride,
  commissionBaseFor,
  appointmentPriceFor,
  includedAppointmentsFor,
} from './paymentEngine';
import {
  EMAIL_TEMPLATES_PATH,
  normalizeTemplates,
  templatesForSuperStatus,
  normalizeGeneratedInstances,
} from './emailTemplatesConfig';
import reportingMail from './reportingMailCore';
import OneTimePayments from './OneTimePayments';
import { SETUP_PACKAGES_PATH } from './setupPackagesCore';
import './StructureBoard.css';

const REPORTS_PATH = 'salesgod-research/reports';
const SECRETS_PATH = 'salesgod-research/reportSecrets';
const REPORTINGS_PATH = 'salesgod-research/reportings';
const WARMUPS_PATH = 'salesgod-research/warmups';
const CUSTOMERS_PATH = 'salesgod-research/customers';
// Präfix für Lite-Vormerkungen: leichte, selbst angelegte Kontakt-Platzhalter in
// der Kennenlern-Phase (keine Research-Seite). Liegen im selben customers-Pfad,
// sind aber über das Flag `lite: true` klar von echten Research-Kontakten getrennt.
const LITE_SLUG_PREFIX = 'lite-';
const PAYMENTS_PATH = 'salesgod-research/payments';
const TEAM_PATH = 'salesgod-research/team';

// Provisionsstaffel je Kunde. 'none' = bewusst keine Staffel; dann bleibt es bei
// der klassischen Vergütung (Fix/Prozent + Pauschalbonus) aus den Team-Settings.
// Beschriftung kommt aus dem Vergütungsplan, damit Pill und Staffel-Tabelle
// dieselbe Rolle nie unterschiedlich nennen.
const CUSTOMER_ROLE_OPTIONS = [
  { id: 'outreach_manager', label: COMMISSION_ROLES.outreach_manager.label },
  { id: 'team_leader', label: COMMISSION_ROLES.team_leader.label },
  { id: 'none', label: 'keine Staffel' },
];

// Rollen-Abgleich tolerant gegenüber Alt-/Varianten-Werten: Groß-/Kleinschreibung,
// führende/folgende Leerzeichen sowie Leerzeichen vs. Bindestrich/Unterstrich werden
// vereinheitlicht, damit z. B. "Outreach Manager" weiterhin als "Outreach-Manager"
// erkannt wird (sonst zeigt die Pipeline ihn, das Protocool aber nicht).
const normalizeRole = (role) =>
  (role == null ? '' : String(role)).trim().toLowerCase().replace(/[\s_-]+/g, '-');
const roleMatches = (role, target) => normalizeRole(role) === normalizeRole(target);
// Cloud Function, die die "Letzte-Infos-vor-dem-Kick-Off"-E-Mail aus dem Research
// generiert (schaut, was wir schon haben und was noch fehlt).
// Cloud Function, die einen passwortgeschuetzten Protocool-Share-Link erstellt
// (bzw. ein bestehendes Passwort zurueckgibt). Wird auch direkt aus dem
// Pipeline-Drawer heraus genutzt, um den Link ohne Umweg ueber den Protocool-Tab
// zu starten.
const PROTOCOOL_SHARE_FN_URL =
  process.env.REACT_APP_PROTOCOOL_SHARE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/setProtocoolShare';
// Cloud Function, die die (client-unlesbaren) Research-Passwoerter liefert –
// fuer den Secret-Key in der Copy-Paste-Nachricht.
const RESEARCH_PASSWORDS_FN_URL =
  process.env.REACT_APP_RESEARCH_PASSWORDS_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/getResearchPasswords';
// Cloud Function fuer die Research-Generierung. Wird hier zum ENTSPERREN eines
// Research-Previews genutzt: mit completeSlug + einem 2. Info-Dump generiert sie
// den kompletten Report nach und schaltet researchMode auf 'full' (Analyse-Tab,
// Slug und Passwort bleiben erhalten).
const GENERATE_RESEARCH_FN_URL =
  process.env.REACT_APP_GENERATE_RESEARCH_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateResearchReport';
const KICKOFF_EMAIL_FN_URL =
  process.env.REACT_APP_KICKOFF_EMAIL_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateKickoffEmail';
// Cloud Function für die Angebots-E-Mail nach dem Präsentations-Call (kurz, knackig,
// kleines Protokoll). Speichert das Ergebnis am Kundenobjekt unter offerEmail.
const OFFER_EMAIL_FN_URL =
  process.env.REACT_APP_OFFER_EMAIL_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateOfferEmail';
// Cloud Function für den "Call-Info-Drop": organisiert rohe Call-Infos zu
// kundensicheren Anmerkungen und legt sie (mit Stage-Tag) im Research ab.
const CALL_INFO_DROP_FN_URL =
  process.env.REACT_APP_CALL_INFO_DROP_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/dropCallInfo';
// Cloud Function für den "Setup-Info-Drop" (Vorbereitungsphase): ordnet einen
// rohen Dump (z. B. die per Mail gelieferten Antworten auf die Setup-Checkliste)
// den benötigten Infos (kickoff.details) im Research zu und füllt/ergänzt sie.
const SETUP_INFO_DROP_FN_URL =
  process.env.REACT_APP_SETUP_INFO_DROP_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/dropSetupInfo';
// Cloud Function für das Daten-Scraping-Briefing (Setup-Phase): erstellt aus dem
// Research einen Überblick für den Outreach-Daten-Spezialisten – welche Daten wir
// brauchen, wo man sie findet, ähnliche Quellen. Sprache (de|en) wählbar.
const DATA_BRIEFING_FN_URL =
  process.env.REACT_APP_DATA_BRIEFING_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateDataBriefing';
// Cloud Function für das Präsentations-Briefing (Kennenlern-Phase): erstellt aus
// dem Research eine kurze, interne E-Mail an das Sales-Teammitglied, das den
// Präsentations-Call führt – mit den wichtigsten Daten & Fakten sowie Link +
// Passwort zur Research-Seite. Speichert das Ergebnis am Kundenobjekt (salesBriefing).
const PRESENTATION_BRIEFING_FN_URL =
  process.env.REACT_APP_PRESENTATION_BRIEFING_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generatePresentationBriefing';
// Cloud Function für die Zugangs-Mail zur Research-Seite (Setup-Phase): kurze,
// freundliche Mail an den Ansprechpartner mit Link + Passwort zum Kunden-Portal
// (Status, nächste Schritte, erste Mail-Ideen, Feedback). Holt das Passwort
// serverseitig aus reportSecrets.
const ACCESS_EMAIL_FN_URL =
  process.env.REACT_APP_ACCESS_EMAIL_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateAccessEmail';
// Cloud Function für die Setup-Rechnungs-Mail (Phase "Angebot unterzeichnet"):
// kurze Mail, dass wir mit dem Setup starten und die Setup-Rechnung senden; die
// monatliche Fee startet erst nach dem Kick-off-Call. Setup-Betrag kommt aus dem
// Vertrag (serverseitig).
const SETUP_FEE_EMAIL_FN_URL =
  process.env.REACT_APP_SETUP_FEE_EMAIL_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateSetupFeeEmail';
// Cloud Function für den generischen, vorlagen-gesteuerten E-Mail-Generator
// (E-Mail-Vorlagen aus den Settings). Erzeugt je Vorlage mehrere Versionen im
// Kontext des jeweiligen Kontakts (Report + Ansprechpartner).
const TEMPLATED_EMAIL_FN_URL =
  process.env.REACT_APP_TEMPLATED_EMAIL_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateTemplatedEmail';
// Konfiguration je E-Mail-Art: welche Function, welches Feld am Kundenobjekt.
const EMAIL_KINDS = {
  kickoff: { url: KICKOFF_EMAIL_FN_URL, field: 'kickoffEmail' },
  offer: { url: OFFER_EMAIL_FN_URL, field: 'offerEmail' },
  access: { url: ACCESS_EMAIL_FN_URL, field: 'accessEmail' },
  setupfee: { url: SETUP_FEE_EMAIL_FN_URL, field: 'setupFeeEmail' },
};
// Feld am Kundenobjekt, das den Versand-Zeitpunkt je Mail-Art hält (null = nicht
// gesendet). Wird beim Wechsel in den nächsten Pipeline-Step zurückgesetzt.
const EMAIL_SENT_FIELD = { kickoff: 'kickoffEmailSentAt', offer: 'offerEmailSentAt', access: 'accessEmailSentAt', setupfee: 'setupFeeEmailSentAt' };
// Ab dieser Zeit ohne Reaktion auf eine gesendete Mail wird zum Nachhaken gewarnt.
const FOLLOWUP_WARN_MS = 3 * 24 * 60 * 60 * 1000;

// Ansprechpartner robust normalisieren: Firebase liefert ein Array je nach Keys
// mal als Array, mal als Objekt ({0:…,1:…}). Wir geben immer eine saubere Liste
// mit String-Feldern + owner-Flag zurueck (leere/kaputte Eintraege fliegen raus).
const normalizeContacts = (raw) => {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : Object.values(raw);
  return arr
    .filter((c) => c && typeof c === 'object')
    .map((c) => ({
      name: String(c.name || ''),
      role: String(c.role || ''),
      email: String(c.email || ''),
      phone: String(c.phone || ''),
      note: String(c.note || ''),
      owner: c.owner === true,
    }))
    .filter((c) => c.name || c.email || c.phone || c.role || c.note);
};

// Kurzlabel fuer einen Kontakt (Name, sonst E-Mail/Telefon).
const contactLabel = (c) => (c && (c.name || c.email || c.phone || c.role)) || '';

// Kompakte, kollisionsarme ID fuer frei angelegte Eintraege (Abschnitte, Punkte,
// Notizen, Alerts). Reicht voellig fuer die kleinen Listen je Kontakt.
const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

// Frei anlegbare Checklisten-Abschnitte je Kontakt. Der erledigt-Status der Items
// lebt – wie bei den Phasen-Checklisten – im gemeinsamen checklist-Map (per Item-ID),
// damit Toggle, Fortschritt & Speicherung identisch funktionieren.
const normalizeCustomSections = (raw) => {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : Object.values(raw);
  return arr
    .filter((s) => s && typeof s === 'object')
    .map((s) => ({
      id: String(s.id || genId()),
      title: String(s.title || ''),
      items: (Array.isArray(s.items) ? s.items : (s.items ? Object.values(s.items) : []))
        .filter((it) => it && typeof it === 'object')
        .map((it) => ({ id: String(it.id || genId()), label: String(it.label || '') }))
        .filter((it) => it.label),
    }))
    .filter((s) => s.title || s.items.length);
};

// Frei anlegbare Notizen & Alerts. kind 'note' = Notiz (Ueberblick unten + Verlauf),
// kind 'alert' = datierter Hinweis (zeigt sich ab showAt bei den naechsten Schritten).
const normalizeNoteEntries = (raw) => {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : Object.values(raw);
  return arr
    .filter((n) => n && typeof n === 'object')
    .map((n) => ({
      id: String(n.id || genId()),
      kind: n.kind === 'alert' ? 'alert' : 'note',
      title: String(n.title || ''),
      body: String(n.body || ''),
      showAt: typeof n.showAt === 'number' ? n.showAt : null,
      done: n.done === true,
      createdAt: typeof n.createdAt === 'number' ? n.createdAt : null,
    }))
    .filter((n) => n.body || n.title);
};

const StructureBoard = ({ onOpenReporting } = {}) => {
  const [reports, setReports] = useState({}); // { slug: {companyName, createdAt, source, createdBy} }
  const [reportData, setReportData] = useState({}); // { slug: normalizedReport }
  const [reportings, setReportings] = useState({}); // { slug__periodKey: reportingObj }
  const [warmups, setWarmups] = useState({}); // { slug: warmupObj } – Launch-Phase-Tagesdaten
  const [customers, setCustomers] = useState({}); // { slug: customerObj }
  const [customersLoaded, setCustomersLoaded] = useState(false); // customers-Listener hat min. 1x geantwortet
  const [team, setTeam] = useState({}); // { memberId: memberObj }
  const [setupPackages, setSetupPackages] = useState({}); // { packageId: paketObj }
  const [mailSettings, setMailSettings] = useState(() => reportingMail.reportingMailSettings(null)); // Reporting-Erinnerungen (Settings)
  const [emailTemplates, setEmailTemplates] = useState([]); // E-Mail-Vorlagen (Settings)
  const [tplBusy, setTplBusy] = useState({}); // { 'slug:tplId': true } – Vorlage generiert gerade
  const [loading, setLoading] = useState(true);
  const [dragSlug, setDragSlug] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [openSlug, setOpenSlug] = useState(null); // Detail-Drawer
  const [openTab, setOpenTab] = useState('overview'); // gewünschter Start-Tab im Drawer
  const [openLiteSlug, setOpenLiteSlug] = useState(null); // Lite-Vormerkung-Editor
  const [now, setNow] = useState(Date.now()); // tickt für den Countdown
  const [showLost, setShowLost] = useState(false); // Lost-Phase horizontal ein-/ausklappbar
  const backfilledRef = useRef(new Set());
  const autoInitRef = useRef(new Set());
  // Merkt, welche Kontakte bereits automatisch in den Präsentations-Call
  // verschoben wurden – damit ein manuelles Zurückziehen nicht sofort wieder
  // überschrieben wird (kein Tauziehen).
  const autoPresentRef = useRef(new Set());
  // Analog für Setup (Angebot unterzeichnet + Kick-off-Termin -> Setup) und für
  // den Kick-off-Call (Setup + Kick-off-Termin abgelaufen -> Kick-off-Call).
  const autoSetupRef = useRef(new Set());
  const autoKickoffRef = useRef(new Set());

  // Countdown jede Minute aktualisieren.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // Monatliche Reportings live – für die Reporting-Infobox auf den Karten der aktiven Phase.
  useEffect(() => {
    const unsub = onValue(ref(database, REPORTINGS_PATH), (snap) => setReportings(snap.val() || {}));
    return () => unsub();
  }, []);

  // Globale Einstellungen der Reporting-Erinnerungen – der Drawer zeigt damit an,
  // ob und wann tatsächlich Mails rausgehen (Quelle: Settings-Tab).
  useEffect(() => {
    const unsub = onValue(ref(database, reportingMail.REPORTING_MAIL_SETTINGS_PATH), (snap) => {
      setMailSettings(reportingMail.reportingMailSettings(snap.val()));
    });
    return () => unsub();
  }, []);

  // Launch-Phase (Warm-up) live – für die Tages-Übersicht im Detail-Drawer.
  useEffect(() => {
    const unsub = onValue(ref(database, WARMUPS_PATH), (snap) => setWarmups(snap.val() || {}));
    return () => unsub();
  }, []);

  // Reports live (inkl. Report-JSON für die Auto-Erkennung)
  useEffect(() => {
    const unsub = onValue(ref(database, REPORTS_PATH), (snap) => {
      const val = snap.val() || {};
      const meta = {};
      const data = {};
      Object.keys(val).forEach((k) => {
        const e = val[k] && typeof val[k] === 'object' ? val[k] : {};
        let parsed = null;
        if (typeof e.json === 'string') {
          try { parsed = JSON.parse(e.json); } catch (_) { parsed = null; }
        } else if (e.json && typeof e.json === 'object') {
          parsed = e.json;
        }
        const name = e.companyName || parsed?.company?.name || '(ohne Name)';
        meta[k] = {
          companyName: name,
          createdAt: typeof e.createdAt === 'number' ? e.createdAt : null,
          createdBy: e.createdBy || '—',
          source: e.source || null,
        };
        if (parsed) {
          try { data[k] = normalizeReport(parsed); } catch (_) { data[k] = parsed; }
        }
      });
      setReports(meta);
      setReportData(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Kundenobjekte live
  useEffect(() => {
    const unsub = onValue(ref(database, CUSTOMERS_PATH), (snap) => {
      setCustomers(snap.val() || {});
      // Erst wenn die Kundenobjekte mindestens einmal vollständig geladen sind,
      // darf der Backfill laufen. Sonst würde er beim Seitenstart (wenn der
      // reports-Listener vor dem customers-Listener antwortet) existierende
      // Kunden mit einem frischen Default-Objekt überschreiben -> Phase/Notizen weg.
      setCustomersLoaded(true);
    });
    return () => unsub();
  }, []);

  // Team (Mitarbeiter aus den Settings) live
  useEffect(() => {
    const unsub = onValue(ref(database, TEAM_PATH), (snap) => {
      setTeam(snap.val() || {});
    });
    return () => unsub();
  }, []);

  // Setup-Pakete (Settings) live – Vorlagen für Einmalzahlungen am Kunden.
  useEffect(() => {
    const unsub = onValue(ref(database, SETUP_PACKAGES_PATH), (snap) => {
      setSetupPackages(snap.val() || {});
    });
    return () => unsub();
  }, []);

  // E-Mail-Vorlagen (Settings) live – steuern die generischen Mail-Generatoren
  // (Quick-Buttons auf der Karte + Generier-Panels im Detail).
  useEffect(() => {
    const unsub = onValue(ref(database, EMAIL_TEMPLATES_PATH), (snap) => {
      setEmailTemplates(normalizeTemplates(snap.val() || {}));
    });
    return () => unsub();
  }, []);

  // Backfill: jeder Report ohne Kundenobjekt bekommt eines.
  // WICHTIG: läuft erst, wenn die Kundenobjekte einmal geladen sind, sonst würde
  // ein noch leerer customers-State existierende Kunden überschreiben.
  useEffect(() => {
    if (!customersLoaded) return;
    Object.keys(reports).forEach((slug) => {
      if (customers[slug] || backfilledRef.current.has(slug)) return;
      backfilledRef.current.add(slug);
      const r = reports[slug];
      // Zusätzliche Sicherung gegen Races: vor dem Anlegen serverseitig prüfen,
      // ob wirklich noch kein Kundenobjekt existiert. So kann ein veralteter
      // lokaler State niemals einen echten Kunden mit Default-Werten plattmachen.
      get(ref(database, `${CUSTOMERS_PATH}/${slug}`))
        .then((snap) => {
          if (snap.exists()) return undefined;
          return set(ref(database, `${CUSTOMERS_PATH}/${slug}`), {
            ...buildCustomerObject({ slug, companyName: r.companyName, createdBy: r.createdBy, source: r.source || 'backfill' }),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            statusEnteredAt: serverTimestamp(),
          });
        })
        .catch((e) => console.error('Backfill Kundenobjekt fehlgeschlagen:', slug, e));
    });
  }, [reports, customers, customersLoaded]);

  // Auto-Checkliste: prüft anhand der Report-Daten, was schon vorhanden ist, und
  // füllt fehlende (noch nie gesetzte) Häkchen einmalig vor. Manuelle Änderungen
  // werden nie überschrieben (nur undefined-Items werden gesetzt).
  useEffect(() => {
    Object.keys(customers).forEach((slug) => {
      const cust = customers[slug];
      const report = reportData[slug];
      if (!cust || !report) return;
      const superStatus = cust.superStatus || DEFAULT_SUPER_STATUS;
      const key = `${slug}:${superStatus}`;
      if (autoInitRef.current.has(key)) return;
      const auto = computeAutoChecklist(superStatus, report);
      const existing = cust.checklist || {};
      const patch = {};
      Object.keys(auto).forEach((itemId) => {
        if (existing[itemId] === undefined) {
          patch[`checklist/${itemId}`] = auto[itemId];
          patch[`checklistAuto/${itemId}`] = auto[itemId];
        }
      });
      autoInitRef.current.add(key);
      if (Object.keys(patch).length === 0) return;
      patch.updatedAt = serverTimestamp();
      update(ref(database, `${CUSTOMERS_PATH}/${slug}`), patch)
        .catch((e) => console.error('Auto-Checkliste fehlgeschlagen:', slug, e));
    });
  }, [customers, reportData]);

  const progressFor = useCallback((slug) => {
    const cust = customers[slug] || {};
    const items = checklistItems(cust.superStatus || DEFAULT_SUPER_STATUS);
    if (items.length === 0) return { done: 0, total: 0 };
    const done = items.filter((it) => (cust.checklist || {})[it.id]).length;
    return { done, total: items.length };
  }, [customers]);

  // Detail-Drawer öffnen – optional direkt auf einem bestimmten Tab (z. B. der
  // Checkliste, wenn man von der Karte aus zu den offenen Punkten springen will).
  const openDrawer = useCallback((slug, tab = 'overview') => {
    setOpenTab(tab);
    setOpenSlug(slug);
  }, []);

  const cardsByColumn = useMemo(() => {
    const grouped = {};
    COLUMNS.forEach((c) => { grouped[c.superStatusId] = []; });
    Object.keys(customers).forEach((slug) => {
      const c = customers[slug] || {};
      const superStatus = grouped[c.superStatus] ? c.superStatus : DEFAULT_SUPER_STATUS;
      // Lite-Vormerkung: eigene, schlanke Karte – nur die selbst gepflegten Felder,
      // KEINE Report-/Metrik-Logik (es gibt keine Research-Seite dazu).
      if (c.lite) {
        grouped[superStatus].push({
          slug,
          lite: true,
          superStatus,
          companyName: c.companyName || '',
          industry: c.industry || '',
          website: c.website || '',
          audience: c.audience || '',
          notes: c.notes || '',
          meetingAt: typeof c.meetingAt === 'number' ? c.meetingAt : null,
          createdAt: typeof c.createdAt === 'number' ? c.createdAt : null,
          updatedAt: c.updatedAt || c.createdAt || 0,
        });
        return;
      }
      const rep = reportData[slug];
      // Deal-Flags werden aus dem Pipeline-Fortschritt abgeleitet (nicht mehr manuell).
      const dealFlags = deriveDealFlags(c);
      // Daten-Briefing (Setup): liegt schon eins vor? In welcher Sprache?
      const brief = c.dataBriefing || null;
      const hasBriefing = hasDataBriefing(brief);
      // Kumulierte Checkliste über ALLE bisherigen Phasen (nicht nur die aktuelle),
      // damit die Karte zeigt, wie viele Punkte insgesamt noch offen sind.
      const cumItems = cumulativeChecklistItems(superStatus);
      const cl = c.checklist || {};
      const cumDone = cumItems.filter((it) => cl[it.id]).length;
      // Ansprechpartner: Owner (bzw. erster Kontakt) + Gesamtzahl für die Karte.
      const contacts = normalizeContacts(c.contacts);
      const ownerContact = contacts.find((ct) => ct.owner) || contacts[0] || null;
      // Offene Alerts (frei angelegte, datierte Hinweise) – für die Karten-Vorschau.
      // Erledigte werden ausgeblendet; das „seit/geplant ab"-Datum wird im Render
      // gegen die aktuelle Zeit ausgewertet.
      const openAlerts = normalizeNoteEntries(c.noteEntries).filter((n) => n.kind === 'alert' && !n.done);
      grouped[superStatus].push({
        slug,
        superStatus,
        contract: c.contract && c.contract.active ? c.contract : null,
        companyName: c.companyName || reports[slug]?.companyName || '(ohne Name)',
        industry: rep?.company?.industry || '',
        contactCount: contacts.length,
        ownerLabel: ownerContact ? contactLabel(ownerContact) : '',
        ownerRole: ownerContact ? ownerContact.role : '',
        subStatus: c.subStatus || '',
        paid: dealFlags.paid,
        offerSent: dealFlags.offerSent,
        offerUrl: c.offerUrl || '',
        offerFile: c.offerFile && c.offerFile.url ? c.offerFile : null,
        hasKickoffEmail: !!(c.kickoffEmail && c.kickoffEmail.body),
        hasOfferEmail: !!(c.offerEmail && c.offerEmail.body),
        hasSetupFeeEmail: !!(c.setupFeeEmail && c.setupFeeEmail.body),
        hasAccessEmail: !!(c.accessEmail && c.accessEmail.body),
        offerEmailSentAt: typeof c.offerEmailSentAt === 'number' ? c.offerEmailSentAt : null,
        kickoffEmailSentAt: typeof c.kickoffEmailSentAt === 'number' ? c.kickoffEmailSentAt : null,
        setupFeeEmailSentAt: typeof c.setupFeeEmailSentAt === 'number' ? c.setupFeeEmailSentAt : null,
        accessEmailSentAt: typeof c.accessEmailSentAt === 'number' ? c.accessEmailSentAt : null,
        setupFeeEmailDismissed: !!c.setupFeeEmailDismissed,
        presentationAt: typeof c.presentationAt === 'number' ? c.presentationAt : null,
        kickoffAt: typeof c.kickoffAt === 'number' ? c.kickoffAt : null,
        kickoffWaiting: c.kickoffWaiting && c.kickoffWaiting.active ? c.kickoffWaiting : null,
        hasBriefing,
        briefingLang: hasBriefing ? (brief.lang === 'en' ? 'en' : 'de') : null,
        cumDone,
        cumTotal: cumItems.length,
        alerts: openAlerts,
        createdAt: typeof c.createdAt === 'number' ? c.createdAt : null,
        updatedAt: c.updatedAt || c.createdAt || 0,
        monthlyOverride: typeof c.monthlyOverride === 'number' ? c.monthlyOverride : null,
        teamCount: c.team ? Object.keys(c.team).filter((k) => c.team[k] != null).length : 0,
        reportSlug: c.reportSlug || slug,
        detailingCallAt: typeof c.detailingCallAt === 'number' ? c.detailingCallAt : null,
        assignedTeam: c.team || null,
        contractActive: !!(c.contract && c.contract.active),
        contractMonthly: c.contract && c.contract.active ? (Number(c.contract.monthlyAmount) || 0) : 0,
        metrics: computeReportMetrics(rep),
      });
    });
    Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)));
    return grouped;
  }, [customers, reports, reportData]);

  const patchCustomer = useCallback((slug, patch) => {
    update(ref(database, `${CUSTOMERS_PATH}/${slug}`), { ...patch, updatedAt: serverTimestamp() })
      .catch((e) => {
        console.error('Update Kundenobjekt fehlgeschlagen:', slug, e);
        // Hintergrund-Speicherung sichtbar machen, damit ein Fehler (z.B. fehlende
        // Berechtigung) sofort auffällt und nicht erst beim Refresh als Datenverlust.
        setToast({ text: 'Speichern fehlgeschlagen: ' + (e?.message || 'unbekannter Fehler'), tone: 'error' });
      });
  }, []);

  // Kontakt komplett loeschen: entfernt Pipeline-Kunde, Research-Report, Passwort und
  // alle daran haengenden Zahlungen. Der Report MUSS mitgeloescht werden, sonst legt der
  // Backfill-Effekt den Pipeline-Kontakt sofort wieder an.
  const deleteCustomer = useCallback(async (slug) => {
    if (!slug) return;
    try {
      // Verhindern, dass der Backfill den Kontakt nach dem Loeschen neu anlegt.
      backfilledRef.current.add(slug);
      await remove(ref(database, `${REPORTS_PATH}/${slug}`));
      try { await remove(ref(database, `${SECRETS_PATH}/${slug}`)); } catch (e) { /* Secret evtl. nicht vorhanden */ }
      await remove(ref(database, `${CUSTOMERS_PATH}/${slug}`));
      try {
        const paySnap = await get(ref(database, PAYMENTS_PATH));
        const payments = paySnap.val() || {};
        await Promise.all(
          Object.keys(payments)
            .filter((id) => payments[id]?.customerSlug === slug || id.startsWith(`${slug}__`))
            .map((id) => remove(ref(database, `${PAYMENTS_PATH}/${id}`)))
        );
      } catch (e) { /* Zahlungen evtl. nicht vorhanden */ }
      setOpenSlug(null);
      setToast({ text: 'Kontakt gelöscht – auch aus Research entfernt.', tone: 'ok' });
    } catch (e) {
      setToast({ text: 'Löschen fehlgeschlagen: ' + e.message, tone: 'error' });
    }
  }, []);

  // Lite-Vormerkung anlegen: ein schlanker, selbst gepflegter Platzhalter-Kontakt in
  // der Kennenlern-Phase – ohne Research-Seite. Direkt nach dem Anlegen öffnet sich
  // der kleine Editor, damit Titel & Co. eingetragen werden können.
  const createLiteCard = useCallback(() => {
    const id = `${LITE_SLUG_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set(ref(database, `${CUSTOMERS_PATH}/${id}`), {
      lite: true,
      companyName: '',
      notes: '',
      meetingAt: null,
      website: '',
      industry: '',
      audience: '',
      phase: phaseForSuperStatus(DEFAULT_SUPER_STATUS),
      superStatus: DEFAULT_SUPER_STATUS,
      subStatus: '',
      source: 'lite',
      createdBy: (auth.currentUser && (auth.currentUser.email || auth.currentUser.uid)) || 'unknown',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      statusEnteredAt: serverTimestamp(),
    })
      .then(() => setOpenLiteSlug(id))
      .catch((e) => setToast({ text: 'Anlegen fehlgeschlagen: ' + (e?.message || 'unbekannter Fehler'), tone: 'error' }));
  }, []);

  // Lite-Vormerkung entfernen: hat keine Research-Seite/Zahlungen – es reicht, den
  // schlanken Kunden-Eintrag zu löschen (kein Backfill, da kein Report existiert).
  const deleteLiteCard = useCallback(async (slug) => {
    if (!slug) return;
    try {
      backfilledRef.current.add(slug);
      await remove(ref(database, `${CUSTOMERS_PATH}/${slug}`));
      setOpenLiteSlug(null);
      setToast({ text: 'Vormerkung entfernt.', tone: 'ok' });
    } catch (e) {
      setToast({ text: 'Löschen fehlgeschlagen: ' + e.message, tone: 'error' });
    }
  }, []);

  // Kick-Off-E-Mail: kleiner Toast für Feedback + Merker, welche Slugs gerade generieren.
  const [emailBusy, setEmailBusy] = useState({}); // { slug: true }
  const [dropBusy, setDropBusy] = useState({}); // { slug: true } – Call-Info-Drop läuft
  const [briefingBusy, setBriefingBusy] = useState({}); // { slug: true } – Daten-Briefing generiert
  const [toast, setToast] = useState(null); // { text, tone }
  // Research-Preview entsperren: Modal fragt einen 2. Info-Dump ab und generiert
  // daraus den kompletten Report nach. { slug, companyName } | null.
  const [unlockModal, setUnlockModal] = useState(null);
  const [unlockBusy, setUnlockBusy] = useState(false);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const copyText = useCallback(async (text, okMsg = 'Kopiert.') => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ text: okMsg, tone: 'ok' });
    } catch (_) {
      setToast({ text: 'Kopieren nicht möglich (Zwischenablage gesperrt).', tone: 'error' });
    }
  }, []);

  // Formatierte Kopie: legt HTML UND Plaintext in die Zwischenablage, damit beim
  // Einfügen in eine E-Mail (Gmail/Outlook) die Fett-Formatierung erhalten bleibt
  // und einfache Textfelder trotzdem den reinen Text bekommen.
  const copyRich = useCallback(async (html, plain, okMsg = 'Kopiert.') => {
    try {
      if (navigator.clipboard && typeof window.ClipboardItem === 'function') {
        // Explizit dunkle Schriftfarbe + Standard-Schriftart für die Zwischenablage,
        // damit der Text beim Einfügen (z. B. Apple Mail, weißer Hintergrund) sichtbar
        // ist und NICHT die helle Dark-Mode-Farbe der Oberfläche erbt.
        const darkHtml = String(html).replace(/<p style="margin:0 0 12px;">/g, '<p style="margin:0 0 12px;color:#111111;">');
        const wrapped = `<div style="color:#111111;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;">${darkHtml}</div>`;
        const item = new window.ClipboardItem({
          'text/html': new Blob([wrapped], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      setToast({ text: okMsg, tone: 'ok' });
    } catch (_) {
      try {
        await navigator.clipboard.writeText(plain);
        setToast({ text: okMsg, tone: 'ok' });
      } catch (e) {
        setToast({ text: 'Kopieren nicht möglich (Zwischenablage gesperrt).', tone: 'error' });
      }
    }
  }, []);

  // Generiert eine E-Mail (kind = 'kickoff' | 'offer') über die jeweilige Cloud
  // Function, speichert sie AUTOMATISCH am Kundenobjekt (kickoffEmail/offerEmail)
  // und gibt sie zurück. Danach ist sie dauerhaft gespeichert und abrufbar.
  // meetingNotes (optional, nur bei der Angebots-Mail): frische Infos aus dem Call,
  // die in die Mail einfließen UND – kundensicher gefiltert – als Notizen ins
  // Research (Kunden-Brain) übernommen werden.
  const generateEmail = useCallback(async (slug, kind, meetingNotes, stage) => {
    const cfg = EMAIL_KINDS[kind];
    const busyKey = `${slug}:${kind}`;
    if (!slug || !cfg || emailBusy[busyKey]) return null;
    setEmailBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const dump = typeof meetingNotes === 'string' ? meetingNotes.trim() : '';
      // Echter Link in die Setup-Details (Research-Seite), den die Kick-off-Mail
      // statt [Formular-Link] einsetzt – der Kunde trägt seine Infos direkt im
      // System ein. Sprung direkt auf den Setup-Tab.
      const formLink = `${window.location.origin}/research/${slug}?tab=setup`;
      // Reiner Portal-/Report-Link (ohne Tab) für die Zugangs-Mail an den Kunden.
      const reportLink = `${window.location.origin}/research/${slug}`;
      const resp = await fetch(cfg.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, ...(kind === 'kickoff' ? { formLink } : {}), ...(kind === 'access' ? { reportLink } : {}), ...(dump ? { meetingNotes: dump, ...(stage ? { stage } : {}) } : {}) }),
      });
      let data = {};
      try { data = await resp.json(); } catch (_) { /* nicht-JSON */ }
      if (!resp.ok || !data.ok) {
        setToast({ text: data.error || `Generierung fehlgeschlagen (HTTP ${resp.status}).`, tone: 'error' });
        return null;
      }
      const email = { subject: data.subject || '', body: data.body || '', generatedAt: Date.now() };
      // Frisch (neu) generierte Mail ist noch nicht versendet -> "gesendet" zurücksetzen.
      patchCustomer(slug, { [cfg.field]: email, [EMAIL_SENT_FIELD[kind]]: null });
      const added = Number(data.notesAdded) || 0;
      setToast(added > 0
        ? { text: `E-Mail erstellt · ${added} ${added === 1 ? 'Notiz' : 'Notizen'} ins Research übernommen.`, tone: 'ok' }
        : { text: 'E-Mail erstellt.', tone: 'ok' });
      return email;
    } catch (e) {
      setToast({ text: 'Netzwerk-/Funktionsfehler: ' + e.message, tone: 'error' });
      return null;
    } finally {
      setEmailBusy((b) => { const n = { ...b }; delete n[busyKey]; return n; });
    }
  }, [emailBusy, patchCustomer]);

  // Card-Shortcut: die Detail-Sidebar IMMER sofort öffnen. Ist die E-Mail schon
  // da, wird sie direkt angezeigt; sonst öffnet sich das Side-Window sofort und
  // zeigt im großen Container die Lade-Animation, während im Hintergrund generiert
  // wird (das Ergebnis erscheint dann automatisch im selben Panel).
  const handleCardEmail = useCallback((slug, kind) => {
    const field = EMAIL_KINDS[kind] && EMAIL_KINDS[kind].field;
    const existing = field && customers[slug] && customers[slug][field];
    openDrawer(slug, 'overview');
    if (existing && existing.body) return;
    generateEmail(slug, kind);
  }, [customers, generateEmail, openDrawer]);

  // Generischer, vorlagen-gesteuerter Mail-Generator: schickt die gedroppten Infos
  // (inputs) + die Vorlage an die Cloud Function, bekommt mehrere Versionen zurück
  // und legt sie als neue Instanz am Kundenobjekt ab (Update 1, 2, 3 …). Gibt true
  // bei Erfolg zurück (das Panel leert dann seine Eingabe).
  const generateTemplatedEmail = useCallback(async (slug, template, inputs, recipient, tone) => {
    if (!slug || !template || !template.id) return false;
    const busyKey = `${slug}:${template.id}`;
    if (tplBusy[busyKey]) return false;
    // Empfänger (gewählter Ansprechpartner) sauber als Strings aufbereiten.
    const rcp = recipient && typeof recipient === 'object' ? {
      name: String(recipient.name || '').trim(),
      role: String(recipient.role || '').trim(),
      email: String(recipient.email || '').trim(),
    } : null;
    const hasRcp = rcp && (rcp.name || rcp.email);
    // Ansprache (kontaktabhängig, beim Generieren gewählt).
    const toneVal = tone === 'sie' ? 'sie' : 'du';
    setTplBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(TEMPLATED_EMAIL_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, templateId: template.id, inputs: inputs || {}, tone: toneVal, ...(hasRcp ? { recipient: rcp } : {}) }),
      });
      let data = {};
      try { data = await resp.json(); } catch (_) { /* nicht-JSON */ }
      if (!resp.ok || !data.ok) {
        setToast({ text: data.error || `Generierung fehlgeschlagen (HTTP ${resp.status}).`, tone: 'error' });
        return false;
      }
      const versions = (Array.isArray(data.versions) ? data.versions : [])
        .filter((v) => v && typeof v === 'object')
        .map((v) => ({ subject: String(v.subject || ''), body: String(v.body || '') }))
        .filter((v) => v.subject || v.body);
      if (!versions.length) {
        setToast({ text: 'Keine E-Mail erhalten.', tone: 'error' });
        return false;
      }
      // Laufende Nummer = bestehende Instanzen + 1 (für „Update 1/2/3 …").
      const existing = normalizeGeneratedInstances(
        customers[slug] && customers[slug].generatedEmails && customers[slug].generatedEmails[template.id]
      );
      const instanceId = genId();
      const instance = {
        number: existing.length + 1,
        inputs: inputs && typeof inputs === 'object' ? inputs : {},
        versions,
        selectedVersion: 0,
        done: false,
        generatedAt: Date.now(),
        tone: toneVal,
        ...(hasRcp ? { recipient: rcp } : {}),
      };
      await set(ref(database, `${CUSTOMERS_PATH}/${slug}/generatedEmails/${template.id}/${instanceId}`), instance);
      setToast({ text: `${versions.length} ${versions.length === 1 ? 'Version' : 'Versionen'} erstellt.`, tone: 'ok' });
      return true;
    } catch (e) {
      setToast({ text: 'Netzwerk-/Funktionsfehler: ' + e.message, tone: 'error' });
      return false;
    } finally {
      setTplBusy((b) => { const n = { ...b }; delete n[busyKey]; return n; });
    }
  }, [tplBusy, customers]);

  // Eine generierte Instanz patchen (z. B. Erledigt-Status, gewählte Version).
  const patchGeneratedInstance = useCallback((slug, templateId, instanceId, patch) => {
    update(ref(database, `${CUSTOMERS_PATH}/${slug}/generatedEmails/${templateId}/${instanceId}`), patch)
      .catch((e) => setToast({ text: 'Speichern fehlgeschlagen: ' + e.message, tone: 'error' }));
  }, []);

  // Eine generierte Instanz löschen.
  const deleteGeneratedInstance = useCallback((slug, templateId, instanceId) => {
    remove(ref(database, `${CUSTOMERS_PATH}/${slug}/generatedEmails/${templateId}/${instanceId}`))
      .catch((e) => setToast({ text: 'Löschen fehlgeschlagen: ' + e.message, tone: 'error' }));
  }, []);

  // Call-Info-Drop: rohe Infos aus einem Call an die Cloud Function schicken. Dort
  // werden sie zu kundensicheren Anmerkungen organisiert und – mit der Stage
  // markiert, aus der gedroppt wurde – im Research (kickoff.notes) abgelegt.
  // Gibt true zurück, wenn der Drop erfolgreich war.
  const dropCallInfo = useCallback(async (slug, dump, stage) => {
    const text = typeof dump === 'string' ? dump.trim() : '';
    if (!slug || text.length < 10 || dropBusy[slug]) return false;
    // Im Kick-off-Call zusätzlich die offenen nächsten Schritte aus dem Dump
    // ableiten und sauber in die Kick-off-Warteliste übernehmen (ohne Timer).
    const isKickoff = (customers[slug] && customers[slug].superStatus) === KICKOFF_SUPER_STATUS;
    setDropBusy((b) => ({ ...b, [slug]: true }));
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(CALL_INFO_DROP_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, dump: text, ...(stage ? { stage } : {}), ...(isKickoff ? { extractNextSteps: true } : {}) }),
      });
      let data = {};
      try { data = await resp.json(); } catch (_) { /* nicht-JSON */ }
      if (!resp.ok || !data.ok) {
        setToast({ text: data.error || `Drop fehlgeschlagen (HTTP ${resp.status}).`, tone: 'error' });
        return false;
      }
      const added = Number(data.notesAdded) || 0;

      // Abgeleitete To-dos in die Kick-off-Warteliste übernehmen: an bestehende
      // Schritte anhängen, Dubletten (gleicher Titel) überspringen, KEIN Timer.
      let stepsAdded = 0;
      if (isKickoff && Array.isArray(data.nextSteps) && data.nextSteps.length) {
        const c = customers[slug] || {};
        const w = c.kickoffWaiting || {};
        const existing = normalizeKickoffSteps(w);
        const have = new Set(existing.map((s) => s.title.trim().toLowerCase()));
        const toAdd = data.nextSteps
          .filter((s) => s && typeof s.title === 'string' && s.title.trim() && !have.has(s.title.trim().toLowerCase()))
          .map((s) => ({ id: kickoffStepId(), title: String(s.title).trim(), desc: typeof s.desc === 'string' ? s.desc.trim() : '', done: false }));
        if (toAdd.length) {
          stepsAdded = toAdd.length;
          patchCustomer(slug, {
            kickoffWaiting: {
              active: true,
              missing: w.missing || '',
              steps: existing.concat(toAdd),
              ...(typeof w.dueAt === 'number' ? { dueAt: w.dueAt } : {}),
              setAt: w.setAt || Date.now(),
            },
          });
        }
      }

      if (added > 0 || stepsAdded > 0) {
        const parts = [];
        if (added > 0) parts.push(`${added} ${added === 1 ? 'Anmerkung' : 'Anmerkungen'} ins Research`);
        if (stepsAdded > 0) parts.push(`${stepsAdded} ${stepsAdded === 1 ? 'nächster Schritt' : 'nächste Schritte'} in die Warteliste`);
        setToast({ text: `${parts.join(' und ')} übernommen${stage ? ` (${stage})` : ''}.`, tone: 'ok' });
      } else {
        setToast({ text: 'Keine übernehmbaren Infos gefunden – nichts übernommen.', tone: 'error' });
      }
      return added > 0 || stepsAdded > 0;
    } catch (e) {
      setToast({ text: 'Netzwerk-/Funktionsfehler: ' + e.message, tone: 'error' });
      return false;
    } finally {
      setDropBusy((b) => { const n = { ...b }; delete n[slug]; return n; });
    }
  }, [customers, dropBusy, patchCustomer]);

  // Setup-Info-Drop (Vorbereitungsphase): ordnet einen rohen Dump – typischerweise
  // die per Mail gelieferten Antworten des Kunden auf die Setup-Checkliste – den
  // benötigten Infos (Setup-Details) im Research zu. Befüllt leere Felder, ergänzt
  // vorhandene. Erkennt die Function im Text einen Anhang/eine Liste (z. B.
  // Blacklist), wird das Feld als „per Mail übermittelt" vermerkt. Gibt true zurück,
  // wenn mindestens ein Feld aktualisiert wurde.
  const dropSetupInfo = useCallback(async (slug, dump) => {
    const text = typeof dump === 'string' ? dump.trim() : '';
    if (!slug || text.length < 10 || dropBusy[slug]) return false;
    setDropBusy((b) => ({ ...b, [slug]: true }));
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(SETUP_INFO_DROP_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, dump: text }),
      });
      let data = {};
      try { data = await resp.json(); } catch (_) { /* nicht-JSON */ }
      if (!resp.ok || !data.ok) {
        setToast({ text: data.error || `Drop fehlgeschlagen (HTTP ${resp.status}).`, tone: 'error' });
        return false;
      }
      const fields = Array.isArray(data.updatedFields) ? data.updatedFields : [];
      if (fields.length > 0) {
        const head = fields.slice(0, 3).join(', ');
        const more = fields.length > 3 ? ` +${fields.length - 3} weitere` : '';
        setToast({ text: `${fields.length} ${fields.length === 1 ? 'Info' : 'Infos'} in die Setup-Details übernommen: ${head}${more}.`, tone: 'ok' });
      } else {
        setToast({ text: 'Keine passenden Setup-Infos erkannt – nichts übernommen.', tone: 'error' });
      }
      return fields.length > 0;
    } catch (e) {
      setToast({ text: 'Netzwerk-/Funktionsfehler: ' + e.message, tone: 'error' });
      return false;
    } finally {
      setDropBusy((b) => { const n = { ...b }; delete n[slug]; return n; });
    }
  }, [dropBusy]);

  // Daten-Scraping-Briefing (Setup-Phase): erstellt über die Cloud Function aus dem
  // Research einen Überblick für den Outreach-Daten-Spezialisten und speichert ihn
  // am Kundenobjekt (dataBriefing). lang = 'de' | 'en'.
  const generateDataBriefing = useCallback(async (slug, lang) => {
    if (!slug || briefingBusy[slug]) return null;
    const language = lang === 'en' ? 'en' : 'de';
    setBriefingBusy((b) => ({ ...b, [slug]: true }));
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(DATA_BRIEFING_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, lang: language }),
      });
      let data = {};
      try { data = await resp.json(); } catch (_) { /* nicht-JSON */ }
      if (!resp.ok || !data.ok || !data.briefing) {
        setToast({ text: data.error || `Briefing fehlgeschlagen (HTTP ${resp.status}).`, tone: 'error' });
        return null;
      }
      const briefing = { ...data.briefing, lang: data.lang || language, generatedAt: Date.now() };
      // Das Erstellen ist zugleich das Abhaken: Checklisten-Punkt automatisch setzen.
      // Das Briefing selbst (dataBriefing) bleibt unabhaengig vom Haken gespeichert.
      patchCustomer(slug, {
        dataBriefing: briefing,
        'checklist/setup_data_briefing': true,
        'checklistAuto/setup_data_briefing': false,
      });
      setToast({ text: 'Daten-Briefing erstellt.', tone: 'ok' });
      return briefing;
    } catch (e) {
      setToast({ text: 'Netzwerk-/Funktionsfehler: ' + e.message, tone: 'error' });
      return null;
    } finally {
      setBriefingBusy((b) => { const n = { ...b }; delete n[slug]; return n; });
    }
  }, [briefingBusy, patchCustomer]);

  // Research-Preview ENTSPERREN: generiert aus einem 2. Info-Dump den kompletten
  // Report nach (completeSlug). Der Analyse-Tab, der Slug und das Passwort bleiben
  // erhalten; researchMode wird serverseitig auf 'full' gesetzt (der Listener
  // aktualisiert die UI danach automatisch). Bei leerem Dump bricht die Function ab.
  const researchUnlock = useCallback(async (slug, dump) => {
    if (!slug || unlockBusy) return;
    const infoDump = String(dump || '').trim();
    if (infoDump.length < 20) {
      setToast({ text: 'Info-Dump ist zu kurz (min. 20 Zeichen).', tone: 'error' });
      return;
    }
    setUnlockBusy(true);
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(GENERATE_RESEARCH_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ completeSlug: slug, infoDump, save: true }),
      });
      let data = {};
      try { data = await resp.json(); } catch (_) { /* nicht-JSON */ }
      if (!resp.ok || !data.ok) {
        setToast({ text: data.error || `Entsperren fehlgeschlagen (HTTP ${resp.status}).`, tone: 'error' });
        return;
      }
      setToast({ text: 'Research komplett generiert und freigeschaltet.', tone: 'ok' });
      setUnlockModal(null);
    } catch (e) {
      setToast({ text: 'Netzwerk-/Funktionsfehler: ' + e.message, tone: 'error' });
    } finally {
      setUnlockBusy(false);
    }
  }, [unlockBusy]);

  // Card-Shortcut fürs Daten-Briefing (Setup-Spalte): öffnet den Drawer und – falls
  // noch kein Briefing existiert – stößt direkt die Generierung in der gewählten
  // Sprache an. Liegt schon eins vor, wird es einfach angezeigt.
  const handleCardBriefing = useCallback((slug, lang) => {
    const brief = customers[slug] && customers[slug].dataBriefing;
    const has = hasDataBriefing(brief);
    openDrawer(slug, 'overview');
    if (has) return;
    generateDataBriefing(slug, lang);
  }, [customers, generateDataBriefing, openDrawer]);

  // Präsentations-Briefing (Kennenlern-Phase): erstellt über die Cloud Function aus
  // dem Research eine interne E-Mail an das gewählte Sales-Teammitglied, das den
  // Präsentations-Call führt – mit den wichtigsten Daten & Fakten plus Link +
  // Passwort zur Research-Seite. Steht noch kein Präsentationstermin, fragt die Mail
  // aktiv danach. Speichert das Ergebnis am Kundenobjekt (salesBriefing) und nutzt
  // denselben Busy-State wie das Daten-Briefing (andere Phase, kein Konflikt).
  const generateSalesBriefing = useCallback(async (slug, recipient) => {
    if (!slug || briefingBusy[slug]) return null;
    const c = customers[slug] || {};
    setBriefingBusy((b) => ({ ...b, [slug]: true }));
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const reportLink = `${window.location.origin}/research/${slug}`;
      const presentationAt = typeof c.presentationAt === 'number' ? c.presentationAt : null;
      const resp = await fetch(PRESENTATION_BRIEFING_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          slug,
          reportLink,
          recipientName: recipient && recipient.name ? recipient.name : '',
          hasPresentationDate: !!presentationAt,
          presentationDateText: presentationAt ? new Date(presentationAt).toLocaleString('de-DE', { dateStyle: 'full', timeStyle: 'short' }) : '',
        }),
      });
      let data = {};
      try { data = await resp.json(); } catch (_) { /* nicht-JSON */ }
      if (!resp.ok || !data.ok) {
        setToast({ text: data.error || `Generierung fehlgeschlagen (HTTP ${resp.status}).`, tone: 'error' });
        return null;
      }
      const briefing = {
        subject: data.subject || '',
        body: data.body || '',
        generatedAt: Date.now(),
        recipientName: recipient && recipient.name ? recipient.name : '',
        recipientEmail: recipient && recipient.email ? recipient.email : '',
      };
      // Neu generiert -> noch nicht versendet (salesBriefingAt zurücksetzen).
      patchCustomer(slug, { salesBriefing: briefing, salesBriefingAt: null });
      setToast({ text: 'Präsentations-Briefing erstellt.', tone: 'ok' });
      return briefing;
    } catch (e) {
      setToast({ text: 'Netzwerk-/Funktionsfehler: ' + e.message, tone: 'error' });
      return null;
    } finally {
      setBriefingBusy((b) => { const n = { ...b }; delete n[slug]; return n; });
    }
  }, [customers, briefingBusy, patchCustomer]);

  // Status wechseln MIT Zeitbuchung: bucht die im alten Status verbrachte Zeit
  // auf statusDurations und startet den Timer für den neuen Status. Beim Erreichen
  // des Endstatus wird der "erster Kunde"-Zeitpunkt festgehalten.
  const changeStatus = useCallback((slug, superStatusId) => {
    const c = customers[slug] || {};
    const old = c.superStatus || DEFAULT_SUPER_STATUS;
    if (old === superStatusId) return;
    // Schutz: Ein zahlender Kunde (aktiver Vertrag) ist in der operativen Phase und
    // darf nicht zurück in eine frühere Phase (Sales/Vorbereitung). Churn (Lost) bleibt
    // erlaubt; für einen echten Rückschritt erst die Zahlungen pausieren (Deal & Team).
    const isPayer = !!(c.contract && c.contract.active);
    if (isPayer && !isLostSuperStatus(superStatusId) && phaseForSuperStatus(superStatusId) !== OPERATIVE_PHASE_ID) {
      setToast({ text: 'Zahlender Kunde – kann nicht zurück vor die operative Phase. Zum Zurückstufen erst die Zahlungen pausieren (Deal & Team).', tone: 'error' });
      return;
    }
    const nowMs = Date.now();
    const enteredAt = typeof c.statusEnteredAt === 'number'
      ? c.statusEnteredAt
      : (typeof c.createdAt === 'number' ? c.createdAt : nowMs);
    const durations = { ...(c.statusDurations || {}) };
    durations[old] = (durations[old] || 0) + Math.max(0, nowMs - enteredAt);
    const patch = {
      superStatus: superStatusId,
      phase: phaseForSuperStatus(superStatusId),
      subStatus: '',
      statusEnteredAt: nowMs,
      statusDurations: durations,
      // Beim Wechsel in einen neuen Step die "gesendet"-Markierungen zurücksetzen:
      // ab hier gehen wir davon aus, dass eine Reaktion kam / der Schritt erledigt ist.
      offerEmailSentAt: null,
      kickoffEmailSentAt: null,
    };
    if (superStatusId === FINAL_SUPER_STATUS && !c.firstCustomerAt) {
      patch.firstCustomerAt = nowMs;
    }
    patchCustomer(slug, patch);
  }, [customers, patchCustomer]);

  // Kontakt als Lost markieren: verschiebt ihn in den gewählten Lost-Überstatus
  // (mit Zeitbuchung über changeStatus) und schreibt den konkreten Grund als
  // Substatus. Der Grund ist Pflicht und ersetzt das frühere freie Substatus-Feld.
  const declareLost = useCallback((slug, lostStatusId, reason) => {
    if (!slug || !lostStatusId) return;
    changeStatus(slug, lostStatusId);
    if (reason) patchCustomer(slug, { subStatus: reason });
  }, [changeStatus, patchCustomer]);

  // Auto-Move: Sobald für einen Kontakt in der Kennenlern-Phase ein fester
  // Präsentationstermin vorliegt, rutscht er automatisch in den
  // "Präsentations-Call" (mit Countdown). So gilt: jeder mit fixem Termin
  // steht im Präsentations-Call. Pro Kontakt nur einmal pro Session – ein
  // manuelles Zurückschieben bleibt danach erhalten.
  useEffect(() => {
    Object.keys(customers).forEach((slug) => {
      const c = customers[slug];
      if (!c || autoPresentRef.current.has(slug)) return;
      const superStatus = c.superStatus || DEFAULT_SUPER_STATUS;
      const hasAppointment = typeof c.presentationAt === 'number';
      if (superStatus === DEFAULT_SUPER_STATUS && hasAppointment) {
        autoPresentRef.current.add(slug);
        changeStatus(slug, PRESENTATION_SUPER_STATUS);
      }
    });
  }, [customers, changeStatus]);

  // Auto-Move: Sobald für einen Kontakt in "Angebot unterzeichnet" ein Kick-off-
  // Termin feststeht (Vertrag unterschrieben + Kick-off vereinbart), rutscht er
  // ins "Setup". Dort läuft der Countdown und das technische Setup wird erledigt.
  // Pro Kontakt nur einmal pro Session – ein manuelles Zurückschieben bleibt erhalten.
  useEffect(() => {
    Object.keys(customers).forEach((slug) => {
      const c = customers[slug];
      if (!c || autoSetupRef.current.has(slug)) return;
      const superStatus = c.superStatus || DEFAULT_SUPER_STATUS;
      if (superStatus === OFFER_SIGNED_SUPER_STATUS && typeof c.kickoffAt === 'number') {
        autoSetupRef.current.add(slug);
        changeStatus(slug, SETUP_SUPER_STATUS);
      }
    });
  }, [customers, changeStatus]);

  // Auto-Move: Sobald der Kick-off-Termin erreicht/abgelaufen ist und der Kontakt
  // noch im Setup steht, springt er automatisch in den "Kick-off-Call". Damit er
  // dort nicht hängen bleibt, gibt es im Kick-off-Call Quick-Actions (weiter in die
  // operative Phase). Pro Kontakt nur einmal – manuelles Zurückziehen bleibt erhalten.
  useEffect(() => {
    Object.keys(customers).forEach((slug) => {
      const c = customers[slug];
      if (!c || autoKickoffRef.current.has(slug)) return;
      const superStatus = c.superStatus || DEFAULT_SUPER_STATUS;
      if (superStatus === SETUP_SUPER_STATUS && typeof c.kickoffAt === 'number' && c.kickoffAt <= now) {
        autoKickoffRef.current.add(slug);
        changeStatus(slug, KICKOFF_SUPER_STATUS);
      }
    });
  }, [customers, now, changeStatus]);

  // Darf dieser Kontakt in diese Spalte gezogen werden? Zahlende Kunden (aktiver
  // Vertrag) bleiben in der operativen Phase – nur Churn (Lost) oder operative
  // Spalten sind als Ziel erlaubt.
  const canDrop = (slug, superStatusId) => {
    const c = customers[slug] || {};
    if (!(c.contract && c.contract.active)) return true;
    if (isLostSuperStatus(superStatusId)) return true;
    return phaseForSuperStatus(superStatusId) === OPERATIVE_PHASE_ID;
  };
  const onDragStart = (slug) => setDragSlug(slug);
  const onDragEnd = () => { setDragSlug(null); setDragOverCol(null); };
  const onDrop = (superStatusId) => { if (dragSlug && canDrop(dragSlug, superStatusId)) changeStatus(dragSlug, superStatusId); onDragEnd(); };

  const reportUrl = (slug) => `${window.location.origin}/research/${slug}`;
  const lostCount = LOST_SUPER_STATUS_IDS.reduce((sum, id) => sum + (cardsByColumn[id]?.length || 0), 0);

  return (
    <div className="sb-wrap">
      {loading ? (
        <div className="sb-empty">Laden…</div>
      ) : (
        <motion.div className="sb-board" variants={staggerContainer} initial="initial" animate="animate">
          {(showLost ? [...PHASES, LOST_PHASE] : PHASES).map((phase) => (
            <motion.section
              key={phase.id}
              variants={subtleItemVariants}
              className={`sb-phase ${phase.id === 'lost' ? 'sb-phase-lost' : ''}`}
              style={{ '--phase-accent': phase.accent, '--phase-soft': phase.accentSoft }}
            >
              <header className="sb-phase-head">
                <span className="sb-phase-bar" />
                <span className="sb-phase-label">{phase.label}</span>
              </header>
              <div className="sb-phase-cols">
                {phase.superStatuses.map((s) => {
                  const cards = cardsByColumn[s.id] || [];
                  const isOver = dragOverCol === s.id;
                  return (
                    <div
                      key={s.id}
                      className={`sb-col ${isOver ? 'sb-col-over' : ''} ${dragSlug && !canDrop(dragSlug, s.id) ? 'sb-col-nodrop' : ''}`}
                      onDragOver={(e) => {
                        // Verbotene Spalte (zahlender Kunde -> zurück): keinen Drop zulassen.
                        if (dragSlug && !canDrop(dragSlug, s.id)) { e.dataTransfer.dropEffect = 'none'; return; }
                        e.preventDefault();
                        setDragOverCol(s.id);
                      }}
                      onDragLeave={() => setDragOverCol((c) => (c === s.id ? null : c))}
                      onDrop={() => onDrop(s.id)}
                    >
                      <div className="sb-col-head">
                        <span className="sb-col-title">{s.label}</span>
                        <span className="sb-col-head-right">
                          {s.id === DEFAULT_SUPER_STATUS && (
                            <button
                              type="button"
                              className="sb-col-add"
                              title="Vormerkung hinzufügen – schlanker Lite-Kontakt ohne Research-Seite"
                              onClick={() => createLiteCard()}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            </button>
                          )}
                          <span className="sb-col-count">{cards.length}</span>
                        </span>
                      </div>
                      <div className="sb-col-body">
                        {cards.length === 0 && <div className="sb-col-empty">—</div>}
                        {cards.map((card) => {
                          if (card.lite) {
                            return (
                              <LiteCard
                                key={card.slug}
                                card={card}
                                now={now}
                                isDragging={dragSlug === card.slug}
                                onDragStart={() => onDragStart(card.slug)}
                                onDragEnd={onDragEnd}
                                onOpen={() => setOpenLiteSlug(card.slug)}
                                onSetMeeting={(ms) => patchCustomer(card.slug, { meetingAt: ms })}
                              />
                            );
                          }
                          const prog = progressFor(card.slug);
                          // Präsentations-Countdown/-Warnung nur in der Sales-Phase
                          // (Kennenlern + Präsentations-Call). Ab der Vorbereitungs-
                          // phase ist der Termin Geschichte und nur noch Lärm.
                          const cd = phase.id === SALES_PHASE_ID ? formatCountdown(card.presentationAt, now) : null;
                          // Kick-off-Countdown: nur in der Setup-Spalte (Rennen gegen die
                          // Zeit bis zum Kick-off-Call). Im Kick-off-Call selbst zeigen wir
                          // stattdessen die Quick-Actions zum Weitergehen.
                          const kcd = s.id === SETUP_SUPER_STATUS ? formatCountdown(card.kickoffAt, now) : null;
                          // Kick-off-Call: wartet der Kontakt (selbst definierter nächster
                          // Schritt), zeigen wir statt der Startklar-Action einen Waiting-Hinweis.
                          const kickoffWaiting = s.id === KICKOFF_SUPER_STATUS ? card.kickoffWaiting : null;
                          // Shortcuts: im Präsentations-Call bei abgelaufenem Termin, und im
                          // Kick-off-Call die Startklar-Action (solange er nicht wartet).
                          // „Zu Lost" wird bewusst herausgefiltert – das ist kein positiver
                          // Schritt und steht im Detail-Drawer unten („Als Lost markieren").
                          // Wie viele Tage liegt der Präsentations-Call schon zurück?
                          // (nur relevant im Präsentations-Call mit abgelaufenem Termin)
                          const presOverDays = (s.id === PRESENTATION_SUPER_STATUS && cd && typeof cd.dayDiff === 'number' && cd.dayDiff < 0)
                            ? -cd.dayDiff
                            : 0;
                          // „Zu Lost" auf der Karte nur anbieten, wenn der Call schon
                          // länger als 10 Tage her ist (Kontakt hängt zu lange fest).
                          const allowLostAction = presOverDays > 10;
                          const quickActions = ((s.id === PRESENTATION_SUPER_STATUS && cd && cd.tone === 'over')
                            ? quickActionsFor(s.id)
                            : (s.id === KICKOFF_SUPER_STATUS && !kickoffWaiting)
                              ? quickActionsFor(s.id)
                              : []).filter((a) => a.tone !== 'lost' || allowLostAction);
                          const daysInFunnel = card.createdAt ? Math.max(0, Math.floor((now - card.createdAt) / 86400000)) : null;
                          const effMonthly = card.monthlyOverride != null ? card.monthlyOverride : card.metrics?.ourMonthly;
                          return (
                            <article
                              key={card.slug}
                              className={`sb-card ${dragSlug === card.slug ? 'sb-card-dragging' : ''} ${cd && cd.tone === 'over' ? 'sb-card-overdue' : ''}`}
                              draggable
                              onDragStart={() => onDragStart(card.slug)}
                              onDragEnd={onDragEnd}
                              onClick={() => openDrawer(card.slug, 'overview')}
                            >
                              <div className="sb-card-top">
                                <span className="sb-card-name" title={card.companyName}>{card.companyName}</span>
                                <a
                                  className="sb-card-link"
                                  href={reportUrl(card.slug)}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Report-Details öffnen"
                                  onClick={(e) => e.stopPropagation()}
                                >↗</a>
                              </div>

                              {card.industry && <span className="sb-card-industry" title={card.industry}>{card.industry}</span>}

                              {card.ownerLabel && (
                                <span
                                  className="sb-card-contact"
                                  title={`Ansprechpartner: ${card.ownerLabel}${card.ownerRole ? ` (${card.ownerRole})` : ''}${card.contactCount > 1 ? ` · +${card.contactCount - 1} weitere` : ''}`}
                                >
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                  <span className="sb-card-contact-name">{card.ownerLabel}</span>
                                  {card.contactCount > 1 && <span className="sb-card-contact-more">+{card.contactCount - 1}</span>}
                                </span>
                              )}

                              {card.metrics && (
                                <div className="sb-card-stats">
                                  <div className="sb-stat" title={`SalesGod-Fit: ${card.metrics.totalScore}/100 · ${card.metrics.verdict.label}`}>
                                    <span className="sb-stat-dot" style={{ background: card.metrics.verdict.color }} />
                                    <span className="sb-stat-val">{card.metrics.totalScore}</span>
                                    <span className="sb-stat-lbl">Fit</span>
                                  </div>
                                  <div className={`sb-stat ${card.monthlyOverride != null ? 'sb-stat-manual' : ''}`} title={card.monthlyOverride != null ? `Wert / Monat: ${formatMoneyCompact(effMonthly)} (manuell gesetzt)` : `Wert für uns: ${formatMoneyCompact(card.metrics.ourMonthly)} / Monat · ${formatMoneyCompact(card.metrics.ourAnnual)} / Jahr`}>
                                    <span className="sb-stat-val">{formatMoneyCompact(effMonthly)}</span>
                                    <span className="sb-stat-lbl">/mt{card.monthlyOverride != null ? ' ✎' : ''}</span>
                                  </div>
                                  <div className="sb-stat" title={`${card.metrics.emailCount} E-Mail-Texte gespeichert`}>
                                    <i className="fa-solid fa-envelope sb-stat-icon" aria-hidden="true"></i>
                                    <span className="sb-stat-val">{card.metrics.emailCount}</span>
                                  </div>
                                  {card.cumTotal > 0 && (
                                    <div className={`sb-stat ${card.cumDone === card.cumTotal ? 'sb-stat-ok' : ''}`} title={`Checkliste: ${card.cumDone}/${card.cumTotal} erledigt${card.cumTotal - card.cumDone > 0 ? ` · ${card.cumTotal - card.cumDone} offen` : ''}`}>
                                      <i className="fa-solid fa-list-check sb-stat-icon" aria-hidden="true"></i>
                                      <span className="sb-stat-val">{card.cumDone}/{card.cumTotal}</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              {card.subStatus && <span className="sb-card-substatus">{card.subStatus}</span>}

                              {/* Alerts – frei angelegte Hinweise vom Kontakt-Detail.
                                  Aktive zuerst (Datum erreicht / ohne Datum), geplante
                                  gedämpft. Komplette Liste landet im title-Tooltip. */}
                              {card.alerts && card.alerts.length > 0 && (() => {
                                const sorted = [...card.alerts].sort((a, b) => {
                                  const ap = typeof a.showAt === 'number' && a.showAt > now;
                                  const bp = typeof b.showAt === 'number' && b.showAt > now;
                                  if (ap !== bp) return ap ? 1 : -1;
                                  return (a.showAt || a.createdAt || 0) - (b.showAt || b.createdAt || 0);
                                });
                                const top = sorted[0];
                                const pending = typeof top.showAt === 'number' && top.showAt > now;
                                const more = sorted.length - 1;
                                const label = top.title || top.body || 'Alert';
                                const tip = sorted
                                  .map((a) => {
                                    const d = typeof a.showAt === 'number'
                                      ? ` (${a.showAt > now ? 'geplant ab ' : 'seit '}${new Date(a.showAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })})`
                                      : '';
                                    return `• ${a.title || a.body || 'Alert'}${d}`;
                                  })
                                  .join('\n');
                                return (
                                  <div
                                    className={`sb-card-alert ${pending ? 'sb-card-alert-pending' : ''}`}
                                    title={`Alert${sorted.length > 1 ? `s (${sorted.length})` : ''}:\n${tip}`}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                                    <span className="sb-card-alert-label">{label}</span>
                                    {more > 0 && <span className="sb-card-alert-more">+{more}</span>}
                                  </div>
                                );
                              })()}

                              {/* Präsentations-Call: zeigt am Anfang der Action-Items,
                                  WANN der Call war (Datum + „vor X Tagen"). Ab >10 Tagen
                                  rot ("hängt"). */}
                              {s.id === PRESENTATION_SUPER_STATUS && card.presentationAt && cd && cd.tone === 'over' && (
                                <div
                                  className={`sb-card-callinfo ${presOverDays > 10 ? 'sb-card-callinfo-warn' : ''}`}
                                  title={`Präsentations-Call war am ${new Date(card.presentationAt).toLocaleString('de-DE', { dateStyle: 'full', timeStyle: 'short' })}`}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                  <span className="sb-cd-label">Termin: {new Date(card.presentationAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}{presOverDays > 0 ? ` · vor ${presOverDays} ${presOverDays === 1 ? 'Tag' : 'Tagen'}` : ' · heute'}</span>
                                  {presOverDays > 10 && <span className="sb-card-callinfo-flag">hängt</span>}
                                </div>
                              )}

                              {/* Setup-Info-Mail-Shortcut (Phase „Setup"): bewusst GANZ OBEN
                                  über den Quick-Actions. Die Angebots-Mail im Präsentations-Call
                                  läuft jetzt über eine dynamische E-Mail-Vorlage (Settings),
                                  nicht mehr hart gecodet. */}
                              {s.id === SETUP_SUPER_STATUS && (() => {
                                const kind = 'kickoff';
                                const has = card.hasKickoffEmail;
                                const busy = !!emailBusy[`${card.slug}:${kind}`];
                                const labelGen = 'Setup-Info-Mail generieren';
                                const labelView = 'Setup-Info-Mail ansehen';
                                const titleGen = 'Setup-Info-Mail generieren & im Detail anzeigen';
                                const titleView = 'Setup-Info-Mail im Detail ansehen';
                                const sentAt = card.kickoffEmailSentAt;
                                const overdue = typeof sentAt === 'number' && (now - sentAt) > FOLLOWUP_WARN_MS;
                                const sentDays = typeof sentAt === 'number' ? Math.floor((now - sentAt) / 86400000) : 0;
                                return (
                                  <>
                                    <button
                                      type="button"
                                      className={`sb-card-email ${has ? 'sb-card-email-muted' : ''}`}
                                      onClick={(e) => { e.stopPropagation(); handleCardEmail(card.slug, kind); }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      disabled={busy}
                                      title={has ? titleView : titleGen}
                                    >
                                      {busy ? (
                                        <span className="sb-card-email-spin" aria-hidden="true" />
                                      ) : has ? (
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                      ) : (
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>
                                      )}
                                      <span>{busy ? 'Generiere…' : (has ? labelView : labelGen)}</span>
                                    </button>
                                    {overdue && (
                                      <div className="sb-card-followup" title={`Mail vor ${sentDays} Tag(en) gesendet, noch keine Reaktion`}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
                                        <span>Nachhaken · seit {sentDays} {sentDays === 1 ? 'Tag' : 'Tagen'}</span>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              {/* Sekundäre Mail-Shortcuts in der Setup-Phase: Setup-Fee-Mail
                                  und Zugangs-Mail. Als Action-Button prominent, solange nicht
                                  generiert – danach bleibt der Button da, nur dezenter. */}
                              {(() => {
                                const secMails = s.id === SETUP_SUPER_STATUS
                                  ? [
                                      ...(card.setupFeeEmailDismissed ? [] : [{ kind: 'setupfee', has: card.hasSetupFeeEmail, sentAt: card.setupFeeEmailSentAt, labelGen: 'Setup-Fee-Mail generieren', labelView: 'Setup-Fee-Mail ansehen' }]),
                                      { kind: 'access', has: card.hasAccessEmail, sentAt: card.accessEmailSentAt, labelGen: 'Zugangs-Mail generieren', labelView: 'Zugangs-Mail ansehen' },
                                    ]
                                  : [];
                                if (!secMails.length) return null;
                                return secMails.map((secMail) => {
                                  const busy = !!emailBusy[`${card.slug}:${secMail.kind}`];
                                  const overdue = typeof secMail.sentAt === 'number' && (now - secMail.sentAt) > FOLLOWUP_WARN_MS;
                                  const sentDays = typeof secMail.sentAt === 'number' ? Math.floor((now - secMail.sentAt) / 86400000) : 0;
                                  return (
                                    <React.Fragment key={secMail.kind}>
                                      <button
                                        type="button"
                                        className={`sb-card-email sb-card-email-secondary ${secMail.has ? 'sb-card-email-muted' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); handleCardEmail(card.slug, secMail.kind); }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        disabled={busy}
                                        title={secMail.has ? secMail.labelView : secMail.labelGen}
                                      >
                                        {busy ? (
                                          <span className="sb-card-email-spin" aria-hidden="true" />
                                        ) : secMail.has ? (
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                        ) : (
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>
                                        )}
                                        <span>{busy ? 'Generiere…' : (secMail.has ? secMail.labelView : secMail.labelGen)}</span>
                                      </button>
                                      {overdue && (
                                        <div className="sb-card-followup" title={`Mail vor ${sentDays} Tag(en) gesendet, noch keine Reaktion`}>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
                                          <span>Nachhaken · seit {sentDays} {sentDays === 1 ? 'Tag' : 'Tagen'}</span>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  );
                                });
                              })()}

                              {/* Generische Vorlagen-Buttons (E-Mail-Vorlagen aus den
                                  Settings): erscheinen je nach Phase (showIn). Zustand:
                                  generieren / ansehen / erledigt (durchgestrichen). */}
                              {templatesForSuperStatus(emailTemplates, s.id).filter((t) => t.quickButton).map((tpl) => {
                                const insts = normalizeGeneratedInstances(customers[card.slug] && customers[card.slug].generatedEmails && customers[card.slug].generatedEmails[tpl.id]);
                                const count = insts.length;
                                const allDone = count > 0 && insts.every((i) => i.done);
                                const busy = !!tplBusy[`${card.slug}:${tpl.id}`];
                                const label = count === 0 ? `${tpl.name} generieren` : (tpl.numbered ? `${tpl.name} (${count})` : `${tpl.name} ansehen`);
                                return (
                                  <button
                                    key={tpl.id}
                                    type="button"
                                    className={`sb-card-email sb-card-email-secondary ${count ? 'sb-card-email-muted' : ''} ${allDone ? 'sb-card-email-done' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); openDrawer(card.slug, 'overview'); }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    disabled={busy}
                                    title={count ? `${tpl.name} im Detail ansehen` : `${tpl.name} im Detail generieren`}
                                  >
                                    {busy ? (
                                      <span className="sb-card-email-spin" aria-hidden="true" />
                                    ) : allDone ? (
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    ) : (
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>
                                    )}
                                    <span>{busy ? 'Generiere…' : label}</span>
                                  </button>
                                );
                              })}

                              {quickActions.length > 0 ? (
                                <div
                                  className="sb-card-actions"
                                  title={s.id === KICKOFF_SUPER_STATUS
                                    ? 'Kick-off-Call – nächster Schritt:'
                                    : `Präsentationstermin abgelaufen (${card.presentationAt ? new Date(card.presentationAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}) – nächster Schritt:`}
                                >
                                  {quickActions.map((a) => (
                                    <button
                                      key={a.id}
                                      type="button"
                                      className={`sb-card-action sb-card-action-${a.tone}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Kick-off „Startklar" startet den monatlichen Retainer –
                                        // das wird im Detail bestätigt (Betrag), nicht blind per Karte.
                                        if (s.id === KICKOFF_SUPER_STATUS) { openDrawer(card.slug, 'overview'); return; }
                                        changeStatus(card.slug, a.to);
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      {a.tone === 'lost' ? (
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                      ) : (
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                      )}
                                      <span>{a.label}</span>
                                    </button>
                                  ))}
                                </div>
                              ) : (s.id === DEFAULT_SUPER_STATUS) ? (
                                // Kennenlern-Phase: Präsentationstermin festlegen + Briefing
                                // ("Daten & Fakten") ans Sales-Team senden, damit die Person
                                // im Präsentations-Call alle Infos hat. Das Briefing ist ein
                                // reiner Erledigt-Schalter: einmal klicken = erledigt.
                                <div className="sb-card-kennenlern">
                                  {!card.presentationAt ? (
                                    <CardDateButton
                                      label="Präsentationstermin festlegen"
                                      title="Präsentationstermin festlegen – der nächste Schritt"
                                      onSet={(ms) => patchCustomer(card.slug, { presentationAt: ms, presentationSource: 'manual' })}
                                    />
                                  ) : (
                                    <div className="sb-card-appt" title={`Präsentationstermin steht: ${new Date(card.presentationAt).toLocaleString('de-DE', { dateStyle: 'full', timeStyle: 'short' })}`}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><polyline points="9 15 11 17 15 13" /></svg>
                                      <span className="sb-cd-label">Termin: {new Date(card.presentationAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                  )}
                                  {card.salesBriefingAt ? (
                                    <button
                                      type="button"
                                      className="sb-card-action sb-card-action-done sb-card-briefing-btn"
                                      title={`Präsentation ans Sales-Team gesendet am ${new Date(card.salesBriefingAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })} – zum Zurücksetzen klicken`}
                                      onClick={(e) => { e.stopPropagation(); patchCustomer(card.slug, { salesBriefingAt: null }); }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                      <span>Präsentation an Sales gesendet</span>
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="sb-card-action sb-card-action-ghost sb-card-briefing-btn"
                                      title="Präsentation (alle Vorbereitungen & Infos) für den Präsentations-Call ans Sales-Team senden, damit die präsentierende Person alles zur Hand hat. Klick markiert es als erledigt."
                                      onClick={(e) => { e.stopPropagation(); patchCustomer(card.slug, { salesBriefingAt: Date.now() }); }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                                      <span>Präsentation an Sales senden</span>
                                    </button>
                                  )}
                                </div>
                              ) : cd ? (
                                <div className={`sb-card-countdown sb-cd-${cd.tone}`} title={`Präsentationstermin: ${new Date(card.presentationAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}`}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                                  <span className="sb-cd-label">{cd.tone === 'over' ? 'Termin vorbei' : `Präsentation ${cd.label}`}</span>
                                </div>
                              ) : kcd ? (
                                <div className={`sb-card-countdown sb-cd-${kcd.tone}`} title={`Kick-off-Termin: ${card.kickoffAt ? new Date(card.kickoffAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}`}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                                  <span className="sb-cd-label">{kcd.tone === 'over' ? 'Kick-off jetzt' : `Kick-off ${kcd.label}`}</span>
                                </div>
                              ) : null}

                              {/* Reporting-Status (aktive Phase): Countdown bis zum nächsten
                                  Reporting, ob es eingereicht oder die Frist verpasst wurde –
                                  klickbar direkt ins Reporting. */}
                              {card.contract && (s.id === 'experimental' || s.id === FINAL_SUPER_STATUS) && (() => {
                                const periods = reportingPeriods(card.contract, now);
                                if (!periods.length) return null;
                                let info = null;
                                // 1) Fällige, noch nicht eingereichte Reportings -> Handlungsbedarf.
                                for (let i = 0; i < periods.length; i++) {
                                  const p = periods[i];
                                  if (p.availableAt > now) continue;
                                  const db = reportings[reportingId(card.slug, p.periodKey)];
                                  const submitted = db && (db.status === 'submitted' || typeof db.submittedAt === 'number');
                                  if (!submitted) { info = { state: now > p.deadlineAt ? 'overdue' : 'open', target: p.deadlineAt }; break; }
                                }
                                // 2) Sonst nächstes anstehendes Reporting -> Countdown.
                                if (!info) {
                                  const next = periods.find((p) => p.availableAt > now);
                                  if (next) info = { state: 'scheduled', target: next.availableAt };
                                }
                                // 3) Sonst alles eingereicht, kein weiteres -> erledigt.
                                if (!info) info = { state: 'done', target: null };
                                const cdv = info.target ? formatCountdown(info.target, now) : null;
                                const label = info.state === 'overdue'
                                  ? 'Reporting-Frist verpasst'
                                  : info.state === 'done'
                                  ? 'Reporting eingereicht'
                                  : info.state === 'open'
                                  ? (cdv ? `Reporting fällig · Frist ${cdv.label}` : 'Reporting fällig')
                                  : (cdv ? `Nächstes Reporting ${cdv.label}` : 'Reporting geplant');
                                return (
                                  <button
                                    type="button"
                                    className={`sb-card-reporting sb-card-reporting-${info.state}`}
                                    onClick={(e) => { e.stopPropagation(); onOpenReporting && onOpenReporting(card.slug); }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    title="Zum Reporting"
                                  >
                                    {info.state === 'done' ? (
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    ) : (
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                                    )}
                                    <span className="sb-card-reporting-label">{label}</span>
                                    <span className="sb-card-reporting-arrow" aria-hidden="true">↗</span>
                                  </button>
                                );
                              })()}

                              {/* Kick-off-Call: wartet auf selbst definierte nächste
                                  Schritte – Hinweis (offene Schritte + Timer) statt Startklar-Button. */}
                              {kickoffWaiting && (() => {
                                const wSteps = normalizeKickoffSteps(kickoffWaiting);
                                const open = wSteps.filter((st) => !st.done);
                                const total = wSteps.length;
                                const doneN = total - open.length;
                                const firstOpen = open[0] || wSteps[0];
                                const label = firstOpen ? firstOpen.title : (kickoffWaiting.nextStep || 'Nächster Schritt');
                                const more = open.length > 1 ? ` +${open.length - 1}` : '';
                                const wcd = formatCountdown(kickoffWaiting.dueAt, now);
                                const titleAttr = [
                                  kickoffWaiting.missing ? `Es fehlt noch: ${kickoffWaiting.missing}` : null,
                                  total ? `Schritte: ${doneN}/${total} erledigt` : null,
                                  typeof kickoffWaiting.dueAt === 'number' ? `Startklar bis: ${new Date(kickoffWaiting.dueAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}` : null,
                                ].filter(Boolean).join(' · ') || 'Wartet auf den nächsten Schritt';
                                return (
                                  <div className="sb-card-waiting" title={titleAttr}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                                    <span className="sb-card-waiting-label">Wartet: {label}{more}</span>
                                    {total > 1 && <span className="sb-card-waiting-count">{doneN}/{total}</span>}
                                    {wcd && <span className={`sb-card-waiting-cd sb-cd-${wcd.tone}`}>{wcd.tone === 'over' ? `überfällig` : wcd.label}</span>}
                                  </div>
                                );
                              })()}

                              {s.id === SETUP_SUPER_STATUS && (() => {
                                const bbusy = !!briefingBusy[card.slug];
                                const checkOpen = card.cumTotal > 0 ? card.cumTotal - card.cumDone : 0;
                                return (
                                  <div className="sb-card-setup">
                                    {/* Daten-Briefing an den Outreach-Spezialisten: ohne Briefing
                                        zwei Sprach-Buttons (DE/EN), mit Briefing nur noch der
                                        Ansehen-Button (der jeweils andere Sprach-Button entfällt). */}
                                    {card.hasBriefing ? (
                                      <button
                                        type="button"
                                        className="sb-card-email sb-card-email-ready"
                                        onClick={(e) => { e.stopPropagation(); handleCardBriefing(card.slug); }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        disabled={bbusy}
                                        title="Daten-Briefing im Detail ansehen"
                                      >
                                        {bbusy ? (
                                          <span className="sb-card-email-spin" aria-hidden="true" />
                                        ) : (
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 14l3-3 3 3 5-5" /></svg>
                                        )}
                                        <span>Daten-Briefing ansehen ({String(card.briefingLang).toUpperCase()})</span>
                                      </button>
                                    ) : (
                                      <div className="sb-card-brief">
                                        <span className="sb-card-brief-label">
                                          {bbusy ? (
                                            <><span className="sb-card-email-spin" aria-hidden="true" /> Generiere…</>
                                          ) : (
                                            <>
                                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 14l3-3 3 3 5-5" /></svg>
                                              Daten-Briefing generieren
                                            </>
                                          )}
                                        </span>
                                        {!bbusy && (
                                          <div className="sb-card-brief-langs" role="group" aria-label="Sprache">
                                            <button type="button" className="sb-card-brief-lang" onClick={(e) => { e.stopPropagation(); handleCardBriefing(card.slug, 'de'); }} onMouseDown={(e) => e.stopPropagation()}>DE</button>
                                            <button type="button" className="sb-card-brief-lang" onClick={(e) => { e.stopPropagation(); handleCardBriefing(card.slug, 'en'); }} onMouseDown={(e) => e.stopPropagation()}>EN</button>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Kick-off-Termin festlegen, falls noch keiner eingetragen ist. */}
                                    {!card.kickoffAt && (
                                      <CardDateButton
                                        label="Kick-off-Termin setzen"
                                        title="Kick-off-Termin festlegen – startet den Countdown"
                                        onSet={(ms) => patchCustomer(card.slug, { kickoffAt: ms, kickoffSource: 'manual' })}
                                      />
                                    )}

                                    {/* Protocool-Nachrichten direkt von der Karte aus erzeugen & kopieren. */}
                                    <CardProtocoolAction card={card} team={team} />

                                    {/* Offene Checklistenpunkte über ALLE bisherigen Phasen. */}
                                    {checkOpen > 0 && (
                                      <button
                                        type="button"
                                        className="sb-card-action sb-card-action-ghost sb-card-check"
                                        onClick={(e) => { e.stopPropagation(); openDrawer(card.slug, 'checklist'); }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        title={`${checkOpen} von ${card.cumTotal} Checklistenpunkten (alle Phasen) noch offen`}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
                                        <span>Checkliste: {checkOpen} offen · {card.cumDone}/{card.cumTotal}</span>
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}

                              {prog.total > 0 && (
                                <div
                                  className={`sb-card-progress ${prog.done === prog.total ? 'sb-card-progress-full' : ''}`}
                                  title={`Checkliste: ${prog.done} von ${prog.total} erledigt`}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
                                  <div className="sb-prog-track">
                                    {Array.from({ length: prog.total }).map((_, i) => (
                                      <span key={i} className={`sb-prog-seg ${i < prog.done ? 'sb-prog-seg-done' : ''}`} />
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="sb-card-tags">
                                {card.offerSent && <span className="sb-card-tag sb-card-tag-blue" title="Angebot gesendet (aus dem Pipeline-Status abgeleitet)">Angebot <i className="fa-solid fa-circle-check sb-tag-icon" aria-hidden="true"></i></span>}
                                {(card.offerUrl || card.offerFile) && <span className="sb-card-tag sb-card-tag-blue" title={card.offerFile ? 'Angebot-Datei hochgeladen' : 'Angebot hinterlegt'}>📎 Angebot</span>}
                                {card.paid && <span className="sb-card-tag sb-card-tag-green" title={`Bezahlt · aktiver Vertrag${card.contractMonthly ? ` · ${formatMoneyCompact(card.contractMonthly)} / Monat` : ''}`}>Bezahlt ✓</span>}
                                {card.teamCount > 0 && <span className="sb-card-tag sb-card-tag-blue" title={`${card.teamCount} Mitarbeiter zugewiesen`}>👤 {card.teamCount}</span>}
                                {daysInFunnel != null && <span className="sb-card-tag sb-card-tag-muted" title="Im Funnel seit">{daysInFunnel}d</span>}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.section>
          ))}

          <button
            type="button"
            className={`sb-lost-toggle ${showLost ? 'sb-lost-toggle-open' : ''}`}
            onClick={() => setShowLost((v) => !v)}
            title={showLost ? 'Lost-Phase ausblenden' : 'Lost-Phase einblenden'}
          >
            <span className="sb-lost-toggle-chevron">{showLost ? '›' : '‹'}</span>
            <span className="sb-lost-toggle-text">
              Lost{!showLost && lostCount > 0 ? ` · ${lostCount}` : ''}
            </span>
          </button>
        </motion.div>
      )}

      {openSlug && customers[openSlug] && (
        <CustomerDrawer
          slug={openSlug}
          customer={customers[openSlug]}
          report={reportData[openSlug]}
          warmup={warmups[customers[openSlug].reportSlug || openSlug] || null}
          team={team}
          setupPackages={setupPackages}
          reportings={reportings}
          mailSettings={mailSettings}
          reportUrl={reportUrl(openSlug)}
          now={now}
          initialTab={openTab}
          emailBusy={{ kickoff: !!emailBusy[`${openSlug}:kickoff`], offer: !!emailBusy[`${openSlug}:offer`], access: !!emailBusy[`${openSlug}:access`], setupfee: !!emailBusy[`${openSlug}:setupfee`] }}
          dropBusy={!!dropBusy[openSlug]}
          briefingBusy={!!briefingBusy[openSlug]}
          emailTemplates={templatesForSuperStatus(emailTemplates, customers[openSlug].superStatus || DEFAULT_SUPER_STATUS)}
          tplBusy={tplBusy}
          onGenerateTemplatedEmail={(template, inputs, recipient, tone) => generateTemplatedEmail(openSlug, template, inputs, recipient, tone)}
          onPatchGeneratedInstance={(templateId, instanceId, patch) => patchGeneratedInstance(openSlug, templateId, instanceId, patch)}
          onDeleteGeneratedInstance={(templateId, instanceId) => deleteGeneratedInstance(openSlug, templateId, instanceId)}
          onGenerateEmail={(kind, meetingNotes, stage) => generateEmail(openSlug, kind, meetingNotes, stage)}
          onDropCallInfo={(dump, stage) => dropCallInfo(openSlug, dump, stage)}
          onDropSetupInfo={(dump) => dropSetupInfo(openSlug, dump)}
          onGenerateBriefing={(lang) => generateDataBriefing(openSlug, lang)}
          onGenerateSalesBriefing={(recipient) => generateSalesBriefing(openSlug, recipient)}
          onCopyText={copyText}
          onCopyRich={copyRich}
          onClose={() => setOpenSlug(null)}
          onPatch={(patch) => patchCustomer(openSlug, patch)}
          onRequestResearchUnlock={() => setUnlockModal({ slug: openSlug, companyName: (customers[openSlug] && customers[openSlug].companyName) || openSlug })}
          onChangeStatus={(s) => changeStatus(openSlug, s)}
          onDeclareLost={(lostId, reason) => declareLost(openSlug, lostId, reason)}
          onDelete={() => deleteCustomer(openSlug)}
        />
      )}

      {openLiteSlug && customers[openLiteSlug] && customers[openLiteSlug].lite && (
        <LiteCardDrawer
          key={openLiteSlug}
          slug={openLiteSlug}
          customer={customers[openLiteSlug]}
          now={now}
          onClose={() => setOpenLiteSlug(null)}
          onPatch={(patch) => patchCustomer(openLiteSlug, patch)}
          onDelete={() => deleteLiteCard(openLiteSlug)}
        />
      )}

      {unlockModal && (
        <ResearchUnlockModal
          companyName={unlockModal.companyName}
          busy={unlockBusy}
          onClose={() => { if (!unlockBusy) setUnlockModal(null); }}
          onSubmit={(dump) => researchUnlock(unlockModal.slug, dump)}
        />
      )}

      {toast && (
        <div className={`sb-toast sb-toast-${toast.tone || 'ok'}`} role="status">{toast.text}</div>
      )}
    </div>
  );
};

// Helfer: ms <-> Wert für <input type="datetime-local"> (lokale Zeit).
const toLocalInput = (ms) => {
  if (!ms || typeof ms !== 'number') return '';
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
};

// Helfer: ms -> Wert für <input type="date"> (lokale Zeit, yyyy-mm-dd).
const toDateInput = (ms) => {
  if (!ms || typeof ms !== 'number') return '';
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 10);
};

const parseNum = (s) => {
  const n = Number(String(s).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

// Dateigröße menschenlesbar (z. B. „1,4 MB") für die hochgeladene Angebots-Datei.
const fmtFileSize = (bytes) => {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i += 1; }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1).replace('.', ',')} ${units[i]}`;
};

// Karten-Action „Termin setzen" (Kick-off- bzw. Präsentationstermin): öffnet beim
// Klick direkt den nativen Datums-/Zeit-Picker. Eigene Komponente, damit jede Karte
// ihren eigenen Input-Ref hält (Rendering in einer Liste). onSet bekommt den ms-Wert.
const CardDateButton = ({ label, title, onSet }) => {
  const ref = useRef(null);
  const openPicker = (e) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); } catch (_) { /* showPicker ggf. nicht erlaubt */ }
    }
  };
  return (
    <div className="sb-card-datebtn">
      <button
        type="button"
        className="sb-card-action sb-card-action-primary sb-card-datebtn-trigger"
        onClick={openPicker}
        onMouseDown={(e) => e.stopPropagation()}
        title={title}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
        <span>{label}</span>
      </button>
      <input
        ref={ref}
        type="datetime-local"
        className="sb-card-datebtn-input"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return;
          const ms = new Date(v).getTime();
          if (!isNaN(ms)) onSet(ms);
        }}
      />
    </div>
  );
};

// ── Karten-Quick-Action: Protocool-Nachrichten kopieren ──
// Generiert (bei Bedarf) beide Share-Links, holt den Research-Key und baut die
// fertigen Copy-Paste-Nachrichten – direkt von der Pipeline-Karte aus, damit man
// sie schnell an Team-Copywriter & Outreach-Manager schicken kann.
const CardProtocoolAction = ({ card, team }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [keys, setKeys] = useState({ brainstorming: '', outreach_prep: '' });
  const [researchKey, setResearchKey] = useState('');
  const [copied, setCopied] = useState('');
  const [err, setErr] = useState('');

  const reportSlug = card.reportSlug || card.slug;
  const researchUrl = `${window.location.origin}/research/${reportSlug}`;

  const assignedMembers = Object.keys(card.assignedTeam || {})
    .filter((id) => (card.assignedTeam || {})[id] != null)
    .map((id) => (team || {})[id])
    .filter(Boolean);
  const firstByRole = (role) => assignedMembers.find((m) => roleMatches(m.role, role)) || null;
  const outreachManager = firstByRole('Outreach-Manager');
  const teamCopywriter = firstByRole('Team-Copywriter');

  const callMs = typeof card.detailingCallAt === 'number' ? card.detailingCallAt : card.meetingAt;
  const callDate = callMs
    ? new Date(callMs).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';
  const deadline = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  })();

  const ensure = async () => {
    setBusy(true);
    setErr('');
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const shareFor = async (linkKey) => {
        const resp = await fetch(PROTOCOOL_SHARE_FN_URL, {
          method: 'POST', headers,
          body: JSON.stringify({ slug: card.slug, linkKey, regenerate: false }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.status !== 200 || !data.ok) throw new Error(data.error || 'Link konnte nicht erstellt werden.');
        return data.password;
      };
      const [b, o] = await Promise.all([shareFor('brainstorming'), shareFor('outreach_prep')]);
      setKeys({ brainstorming: b, outreach_prep: o });
      try {
        const presp = await fetch(RESEARCH_PASSWORDS_FN_URL, { method: 'POST', headers, body: '{}' });
        const pdata = await presp.json().catch(() => ({}));
        if (presp.status === 200 && pdata.ok && pdata.passwords) setResearchKey(String(pdata.passwords[reportSlug] || ''));
      } catch (e) { /* Research-Key optional */ }
      setReady(true);
      return { brainstorming: b, outreach_prep: o };
    } catch (e) {
      setErr(e.message || 'Fehler beim Erstellen.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async () => {
    if (ready) { setOpen((o) => !o); return; }
    const k = await ensure();
    if (k) setOpen(true);
  };

  const buildMsg = (linkKey, pw) => buildProtocoolShareMessage({
    business: card.companyName || '',
    researchUrl,
    researchKey,
    researchEmailUrl: `${researchUrl}?tab=emails`,
    feedbackDeadline: deadline,
    callDate,
    otherName: linkKey === 'brainstorming'
      ? ((outreachManager && outreachManager.name) || 'dem Outreach-Manager')
      : ((teamCopywriter && teamCopywriter.name) || 'dem Team-Copywriter'),
    protocolUrl: `${window.location.origin}/protocool/${card.slug}/${linkKey}`,
    protocolKey: pw,
    nextSteps: linkKey === 'outreach_prep'
      ? { copywriterName: (teamCopywriter && teamCopywriter.name) || '', copywriterEmail: (teamCopywriter && teamCopywriter.email) || '' }
      : null,
  });

  const copyMsg = async (linkKey) => {
    try {
      await navigator.clipboard.writeText(buildMsg(linkKey, keys[linkKey]));
      setCopied(linkKey);
      setTimeout(() => setCopied(''), 1800);
    } catch (e) { /* clipboard evtl. blockiert */ }
  };

  return (
    <div className="sb-card-proto">
      <button
        type="button"
        className="sb-card-action sb-card-action-ghost sb-card-proto-btn"
        onClick={(e) => { e.stopPropagation(); handleToggle(); }}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={busy}
        title="Protocool-Nachrichten für Team-Copywriter & Outreach-Manager erzeugen und kopieren"
      >
        {busy ? (
          <span className="sb-card-email-spin" aria-hidden="true" />
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v12H5.17L4 17.17V4z" /><path d="M8 9h8" /><path d="M8 12h5" /></svg>
        )}
        <span>{busy ? 'Wird erstellt…' : 'Protocool-Nachrichten'}</span>
      </button>
      {open && ready && (
        <div className="sb-card-proto-pop" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <button type="button" className="sb-card-proto-copy" onClick={(e) => { e.stopPropagation(); copyMsg('brainstorming'); }}>
            <span className="sb-card-proto-copy-ic" aria-hidden>✍️</span>
            <span>{copied === 'brainstorming' ? 'Kopiert ✓' : `Team-Copywriter${teamCopywriter ? ` · ${teamCopywriter.name}` : ''}`}</span>
          </button>
          <button type="button" className="sb-card-proto-copy" onClick={(e) => { e.stopPropagation(); copyMsg('outreach_prep'); }}>
            <span className="sb-card-proto-copy-ic" aria-hidden>📣</span>
            <span>{copied === 'outreach_prep' ? 'Kopiert ✓' : `Outreach-Manager${outreachManager ? ` · ${outreachManager.name}` : ''}`}</span>
          </button>
          {!researchKey && <span className="sb-card-proto-hint">Research-Key fehlt – im Text als {'{KEY}'}.</span>}
        </div>
      )}
      {err && <span className="sb-card-proto-err">{err}</span>}
    </div>
  );
};

// Website hübsch & kurz fürs Anzeigen (ohne Protokoll/„www."/Trailing-Slash).
const prettyWebsite = (url) => String(url || '')
  .trim()
  .replace(/^https?:\/\//i, '')
  .replace(/^www\./i, '')
  .replace(/\/+$/, '');

// Sicheres href für eine (evtl. ohne Protokoll eingegebene) Website.
const safeHref = (url) => {
  const u = String(url || '').trim();
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
};

// ── Lite-Vormerkung: schlanke Karte für vorgemerkte Zukunfts-Kontakte ──
// Optisch an die Research-Karten angelehnt, aber klar als „Vormerkung" markiert
// (gestrichelter Rahmen + Badge). Zeigt nur die wenigen, selbst gepflegten Felder.
const LiteCard = ({ card, now, isDragging, onDragStart, onDragEnd, onOpen, onSetMeeting }) => {
  const cd = formatCountdown(card.meetingAt, now);
  const daysInFunnel = card.createdAt != null ? Math.max(0, Math.floor((now - card.createdAt) / 86400000)) : null;
  const title = card.companyName || 'Neue Vormerkung';
  return (
    <article
      className={`sb-card sb-card-lite ${isDragging ? 'sb-card-dragging' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
    >
      <div className="sb-card-top">
        <span className={`sb-card-name ${card.companyName ? '' : 'sb-card-name-empty'}`} title={title}>{title}</span>
        <span className="sb-card-lite-badge" title="Vorgemerkter Kontakt (keine Research-Seite)">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 11 3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
          Vormerkung
        </span>
      </div>

      {card.industry && <span className="sb-card-industry" title={card.industry}>{card.industry}</span>}

      {card.audience && (
        <span className="sb-card-lite-field" title={`Zielgruppe: ${card.audience}`}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="3" /><path d="M2 21v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M22 21v-1a5 5 0 0 0-3-4.58" /></svg>
          <span className="sb-card-lite-field-val">{card.audience}</span>
        </span>
      )}

      {card.website && (
        <a
          className="sb-card-lite-web"
          href={safeHref(card.website)}
          target="_blank"
          rel="noreferrer"
          title={card.website}
          onClick={(e) => e.stopPropagation()}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
          <span className="sb-card-lite-field-val">{prettyWebsite(card.website)}</span>
        </a>
      )}

      <div className="sb-card-kennenlern">
        {!card.meetingAt ? (
          <CardDateButton
            label="Kennenlerntermin festlegen"
            title="Kennenlerntermin festlegen"
            onSet={onSetMeeting}
          />
        ) : (
          <div className={`sb-card-countdown sb-cd-${cd ? cd.tone : 'far'}`} title={`Kennenlerntermin: ${new Date(card.meetingAt).toLocaleString('de-DE', { dateStyle: 'full', timeStyle: 'short' })}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            <span className="sb-cd-label">Kennenlernen {cd ? cd.label : new Date(card.meetingAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</span>
          </div>
        )}
      </div>

      {card.notes && (
        <span className="sb-card-lite-notes" title={card.notes}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></svg>
          <span className="sb-card-lite-field-val">{card.notes}</span>
        </span>
      )}

      <div className="sb-card-tags">
        {daysInFunnel != null && (
          <span className="sb-card-tag sb-card-tag-muted" title={`Vorgemerkt seit ${daysInFunnel} ${daysInFunnel === 1 ? 'Tag' : 'Tagen'}`}>
            vorgemerkt seit {daysInFunnel}d
          </span>
        )}
      </div>
    </article>
  );
};

// ── Lite-Editor (Side-Drawer): Titel, Notizen, Kennenlerntermin, Website,
// Branche, Zielgruppe – mehr nicht. Bewusst minimal, klar abgesetzt von den
// großen Research-Drawern. Speichert debounced direkt ans Kundenobjekt.
const LiteCardDrawer = ({ slug, customer, now, onClose, onPatch, onDelete }) => {
  const [form, setForm] = useState({
    companyName: customer.companyName || '',
    website: customer.website || '',
    industry: customer.industry || '',
    audience: customer.audience || '',
    notes: customer.notes || '',
  });
  const [meetingAt, setMeetingAt] = useState(typeof customer.meetingAt === 'number' ? customer.meetingAt : null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const timers = useRef({});

  useEffect(() => {
    const t = timers.current;
    return () => { Object.keys(t).forEach((k) => clearTimeout(t[k])); };
  }, []);

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    clearTimeout(timers.current[field]);
    timers.current[field] = setTimeout(() => onPatch({ [field]: value }), 450);
  };
  const flushField = (field) => {
    clearTimeout(timers.current[field]);
    onPatch({ [field]: form[field] });
  };
  const setMeeting = (e) => {
    const v = e.target.value;
    const ms = v ? new Date(v).getTime() : null;
    setMeetingAt(ms && !isNaN(ms) ? ms : null);
    onPatch({ meetingAt: ms && !isNaN(ms) ? ms : null });
  };

  const daysInFunnel = typeof customer.createdAt === 'number'
    ? Math.max(0, Math.floor((now - customer.createdAt) / 86400000))
    : null;
  const cd = formatCountdown(meetingAt, now);

  return (
    <div className="sb-drawer-overlay" onClick={onClose}>
      <div className="sb-drawer sb-drawer-lite" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="sb-drawer-head">
          <div className="sb-drawer-title">
            <span className="sb-lite-tag" title="Vorgemerkter Kontakt (keine Research-Seite)">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 11 3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              Vormerkung
            </span>
            <h2>{form.companyName || 'Neue Vormerkung'}</h2>
            {daysInFunnel != null && (
              <span className="sb-lite-since">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                Vorgemerkt seit {daysInFunnel} {daysInFunnel === 1 ? 'Tag' : 'Tagen'}
              </span>
            )}
          </div>
          {cd && (
            <span className={`sb-drawer-cd sb-cd-${cd.tone}`} title="Kennenlerntermin">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
              {cd.label}
            </span>
          )}
          <button type="button" className="sb-drawer-close" onClick={onClose} aria-label="Schließen">×</button>
        </header>

        <div className="sb-drawer-body">
          <div className="sb-lite-form">
            <label className="sb-lite-field">
              <span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2" /><path d="M9 20h6" /><path d="M12 4v16" /></svg>
                Titel
              </span>
              <input
                type="text"
                value={form.companyName}
                placeholder="Name / Firma der Vormerkung"
                autoFocus
                onChange={(e) => updateField('companyName', e.target.value)}
                onBlur={() => flushField('companyName')}
              />
            </label>

            <label className="sb-lite-field">
              <span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></svg>
                Notizen
              </span>
              <textarea
                className="sb-notes-area"
                value={form.notes}
                placeholder="Warum vormerken? Was ist wichtig?"
                onChange={(e) => updateField('notes', e.target.value)}
                onBlur={() => flushField('notes')}
              />
            </label>

            <div className="sb-lite-divider" />

            <label className="sb-lite-field">
              <span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                Website
              </span>
              <input
                type="text"
                value={form.website}
                placeholder="z. B. example.com"
                onChange={(e) => updateField('website', e.target.value)}
                onBlur={() => flushField('website')}
              />
            </label>

            <label className="sb-lite-field">
              <span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
                Branche
              </span>
              <input
                type="text"
                value={form.industry}
                placeholder="z. B. SaaS, Handwerk …"
                onChange={(e) => updateField('industry', e.target.value)}
                onBlur={() => flushField('industry')}
              />
            </label>

            <label className="sb-lite-field">
              <span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="3" /><path d="M2 21v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M22 21v-1a5 5 0 0 0-3-4.58" /></svg>
                Zielgruppe
              </span>
              <input
                type="text"
                value={form.audience}
                placeholder="Wen will der Kontakt erreichen?"
                onChange={(e) => updateField('audience', e.target.value)}
                onBlur={() => flushField('audience')}
              />
            </label>

            <label className="sb-lite-field">
              <span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                Kennenlerntermin
              </span>
              <input
                type="datetime-local"
                value={toLocalInput(meetingAt)}
                onChange={setMeeting}
              />
            </label>
          </div>
        </div>

        <footer className="sb-drawer-foot">
          <button type="button" className="sb-delete-btn" onClick={() => setConfirmDelete(true)}>
            Vormerkung entfernen
          </button>
        </footer>

        {confirmDelete && (
          <div className="sb-confirm-overlay" onClick={() => setConfirmDelete(false)}>
            <div className="sb-confirm" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 className="sb-confirm-title">Vormerkung entfernen?</h3>
              <p className="sb-confirm-text">
                <strong>{form.companyName || 'Diese Vormerkung'}</strong> wird aus der Kennenlern-Phase entfernt.
                Das kann nicht rückgängig gemacht werden.
              </p>
              <div className="sb-confirm-actions">
                <button type="button" className="sb-confirm-cancel" onClick={() => setConfirmDelete(false)}>
                  Abbrechen
                </button>
                <button type="button" className="sb-confirm-delete" onClick={() => { setConfirmDelete(false); onDelete(); }}>
                  Entfernen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── E-Mail-Rich-Text: Labels & Abschnittstitel fett, sauberes HTML ──
// Die generierten E-Mails kommen als reiner Text (mit \n / \n\n). Für die Anzeige
// und die formatierte Kopie (text/html) heben wir die wichtigen Stichwörter am
// Zeilenanfang ("Wunschkunden", "Anti-Kunden", "Hinweis:" …) sowie nummerierte
// Abschnittstitel ("1. Zielgruppen-Scouting") fett hervor. Alles wird escaped.
const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const labelWordCount = (s) => (String(s).trim().match(/\S+/g) || []).length;

// Formatiert EINE Zeile zu HTML (escaped) und hebt das führende Label fett hervor.
const formatEmailLineHtml = (rawLine) => {
  const line = String(rawLine).replace(/\s+$/, '');
  if (!line.trim()) return '';

  // Nummerierter Abschnittstitel (z. B. "1. Zielgruppen-Scouting") -> ganze Zeile fett.
  if (/^\s*\d+\.\s+\S/.test(line)) {
    return `<strong>${escapeHtml(line)}</strong>`;
  }

  // Optionaler Aufzählungs-Marker am Zeilenanfang ("- ", "• ", "* ").
  let rest = line;
  let bulletHtml = '';
  const bm = line.match(/^(\s*)([-•*])\s+/);
  if (bm) {
    rest = line.slice(bm[0].length);
    bulletHtml = `${escapeHtml(bm[1])}• `;
  }

  // Ein echtes Label ist ein kurzes Stichwort (max 3 Wörter, kein Satz), gefolgt
  // von einer Beschreibung, die mit Großbuchstabe/Ziffer beginnt. So bleiben
  // normale Einleitungssätze (z. B. "Danke für deinen Auftrag - wir sind …")
  // unangetastet.
  const looksLikeLabel = (label) => labelWordCount(label) <= 3 && label.trim().length <= 28 && !/[.!?:]$/.test(label.trim());
  const restStartsStrong = (s) => /^[A-ZÄÖÜ0-9]/.test(s.trim());

  // Label vor " - " / " – ".
  let m = rest.match(/^(.{1,28}?)(\s[–-]\s)([\s\S]+)$/);
  if (m && looksLikeLabel(m[1]) && restStartsStrong(m[3])) {
    return `${bulletHtml}<strong>${escapeHtml(m[1])}</strong>${escapeHtml(m[2] + m[3])}`;
  }
  // Label vor ":" (z. B. "Hinweis:", "Ausgangslage:").
  m = rest.match(/^([^:]{1,28}?):\s([\s\S]+)$/);
  if (m && looksLikeLabel(m[1]) && restStartsStrong(m[2])) {
    return `${bulletHtml}<strong>${escapeHtml(`${m[1]}:`)}</strong>${escapeHtml(` ${m[2]}`)}`;
  }
  return `${bulletHtml}${escapeHtml(rest)}`;
};

// Wandelt einen E-Mail-Text (mit \n / \n\n) in formatiertes HTML um: Absätze als
// <p> (durch Leerzeilen getrennt), Zeilen innerhalb eines Absatzes via <br>.
// Inline-Styles, damit die Formatierung beim Einfügen in Gmail/Outlook hält.
const emailBodyToHtml = (body) => {
  const norm = String(body || '').replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  if (!norm) return '';
  return norm
    .split(/\n{2,}/)
    .map((para) => {
      const inner = para.split('\n').map(formatEmailLineHtml).join('<br>');
      return `<p style="margin:0 0 12px;">${inner}</p>`;
    })
    .join('');
};

// Wiederverwendbares Panel für generierte E-Mails (Kickoff-Infos & Angebot).
// Zeigt Generieren-Button, Betreff, Text und Copy-Aktionen, edel formatiert.
const EmailPanel = ({ title, email, busy, onGenerate, onCopyText, onCopyRich, emptyText, hint, generateLabel = 'E-Mail generieren', showMeetingInput = false, sentAt = null, onToggleSent, recipientEmail = '', recipientName = '', onDismiss, dismissTitle = 'Diese Mail ausblenden', now = Date.now() }) => {
  // Frische Infos aus dem Meeting (nur Angebots-Mail). Fließen in die Mail ein und
  // werden – kundensicher gefiltert – als Notizen ins Research übernommen. Nach
  // erfolgreicher Generierung wird das Feld geleert (Dump ist verarbeitet).
  const [meetingNotes, setMeetingNotes] = useState('');
  const handleGenerate = () => {
    const dump = meetingNotes.trim();
    const res = onGenerate(dump);
    if (dump && res && typeof res.then === 'function') {
      res.then((ok) => { if (ok) setMeetingNotes(''); });
    }
  };
  return (
  <div className="sb-kmail">
    <div className="sb-kmail-head">
      <div className="sb-kmail-title">
        <span>{title}</span>
        {email && email.generatedAt && (
          <span className="sb-kmail-meta">generiert {new Date(email.generatedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
        )}
      </div>
      <div className="sb-kmail-head-actions">
        {onDismiss && (
          <button
            type="button"
            className="sb-kmail-dismiss"
            onClick={onDismiss}
            title={dismissTitle}
            aria-label={dismissTitle}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            Ausblenden
          </button>
        )}
        {email && (
          <button
            type="button"
            className="sb-kmail-mailapp"
            onClick={() => {
              const to = recipientEmail || '';
              const params = [];
              if (email.subject) params.push(`subject=${encodeURIComponent(email.subject)}`);
              if (email.body) params.push(`body=${encodeURIComponent(email.body)}`);
              const href = `mailto:${encodeURIComponent(to)}${params.length ? `?${params.join('&')}` : ''}`;
              window.location.href = href;
            }}
            title="Öffnet die Mail im Standard-Mailprogramm (z. B. Apple Mail) als Entwurf – mit Empfänger, Betreff & Text. Wird nicht automatisch gesendet."
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
            In Mail öffnen
          </button>
        )}
        <button
          type="button"
          className="sb-kmail-gen"
          onClick={handleGenerate}
          disabled={busy}
          title={email ? 'E-Mail neu generieren (überschreibt die gespeicherte)' : 'E-Mail aus dem Research generieren'}
        >
          {busy ? 'Generiere…' : (email ? 'Neu generieren' : generateLabel)}
        </button>
      </div>
    </div>

    {recipientEmail && (
      <div className="sb-kmail-to">
        <span className="sb-kmail-to-label">An</span>
        <span className="sb-kmail-to-email" title={recipientName ? `${recipientName} (Ansprechpartner)` : 'Ansprechpartner'}>{recipientEmail}</span>
        <button
          type="button"
          className="sb-kmail-to-copy"
          onClick={() => onCopyText(recipientEmail, 'E-Mail-Adresse kopiert.')}
          title="E-Mail-Adresse des Ansprechpartners kopieren"
        >
          kopieren
        </button>
      </div>
    )}

    {showMeetingInput && (
      <div className="sb-kmail-meeting">
        <label className="sb-kmail-meeting-label" htmlFor="sb-kmail-meeting-input">
          Frische Infos aus dem Meeting <span className="sb-kmail-meeting-opt">(optional)</span>
        </label>
        <textarea
          id="sb-kmail-meeting-input"
          className="sb-kmail-meeting-area"
          value={meetingNotes}
          placeholder="Was wurde im Call konkret besprochen? Diese Infos machen das Protokoll exakter und werden gleichzeitig ins Research übernommen …"
          onChange={(e) => setMeetingNotes(e.target.value)}
          disabled={busy}
        />
        <span className="sb-kmail-meeting-hint">
          Bitte keine personenbezogenen Daten. Es werden nur kundensichere, geschäftsrelevante Notizen ins Research übernommen.
        </span>
      </div>
    )}

    {!email ? (
      busy ? (
        <div className="sb-kmail-loading" role="status" aria-live="polite">
          <div className="sb-kmail-skeleton">
            <span className="sb-kmail-skel-line sb-kmail-skel-label" />
            <span className="sb-kmail-skel-line sb-kmail-skel-subject" />
            <span className="sb-kmail-skel-gap" />
            <span className="sb-kmail-skel-line" />
            <span className="sb-kmail-skel-line" />
            <span className="sb-kmail-skel-line sb-kmail-skel-short" />
            <span className="sb-kmail-skel-gap" />
            <span className="sb-kmail-skel-line" />
            <span className="sb-kmail-skel-line sb-kmail-skel-medium" />
          </div>
          <div className="sb-kmail-loading-foot">
            <span className="sb-kmail-loading-spin" aria-hidden="true" />
            <span className="sb-kmail-loading-text">E-Mail wird erstellt …</span>
          </div>
        </div>
      ) : (
        <p className="sb-kmail-empty">{emptyText}</p>
      )
    ) : (
      <>
        <div className="sb-kmail-field">
          <span className="sb-kmail-label">Betreff</span>
          <div className="sb-kmail-subject">
            <span>{email.subject || '(kein Betreff)'}</span>
            <button type="button" className="sb-kmail-copy" onClick={() => onCopyText(email.subject || '', 'Betreff kopiert.')} title="Betreff kopieren">kopieren</button>
          </div>
        </div>
        <div className="sb-kmail-field">
          <span className="sb-kmail-label">E-Mail-Text</span>
          <div
            className="sb-kmail-body sb-kmail-body-rich"
            dangerouslySetInnerHTML={{ __html: emailBodyToHtml(email.body) }}
          />
        </div>
        <div className="sb-kmail-actions">
          <button
            type="button"
            className="sb-kmail-btn sb-kmail-btn-primary"
            onClick={() => onCopyRich(
              `<p style="margin:0 0 12px;"><strong>Betreff:</strong> ${escapeHtml(email.subject || '')}</p>${emailBodyToHtml(email.body)}`,
              `Betreff: ${email.subject || ''}\n\n${email.body || ''}`.trim(),
              'E-Mail mit Formatierung kopiert.',
            )}
          >
            Betreff + Text kopieren
          </button>
          <button
            type="button"
            className="sb-kmail-btn"
            onClick={() => onCopyRich(emailBodyToHtml(email.body), email.body || '', 'Text mit Formatierung kopiert.')}
          >
            Nur Text kopieren
          </button>
        </div>
        {hint && <span className="sb-kmail-hint">{hint}</span>}
        {onToggleSent && (
          <div className="sb-kmail-sent">
            <button
              type="button"
              className={`sb-kmail-sent-toggle ${sentAt ? 'is-on' : ''}`}
              onClick={onToggleSent}
              title={sentAt ? 'Markierung entfernen (doch nicht gesendet)' : 'Mail als gesendet markieren'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              {sentAt ? 'Als gesendet markiert' : 'Als gesendet markieren'}
            </button>
            {sentAt && (
              <span className="sb-kmail-sent-meta">
                gesendet {new Date(sentAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        )}
        {sentAt && (now - sentAt) > FOLLOWUP_WARN_MS && (
          <div className="sb-kmail-followup" role="status">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
            <span>Vor {Math.floor((now - sentAt) / 86400000)} Tagen gesendet und noch keine Reaktion – Zeit zum Nachhaken.</span>
          </div>
        )}
      </>
    )}
  </div>
  );
};

// Wiederverwendbarer, vorlagen-gesteuerter E-Mail-Generator (E-Mail-Vorlagen aus
// den Settings). Zeigt die Drop-Felder der Vorlage, generiert mehrere Versionen
// zum Auswählen/Copy-Pasten und verwaltet pro Instanz den Erledigt-Status
// (durchgestrichen). Beliebig oft pro Kontakt nutzbar (Update 1, 2, 3 …).
// Stabiler Schlüssel je Kontakt (E-Mail bevorzugt, sonst Name) – für die Auswahl.
const contactKey = (c, i) => (c && (c.email || c.name)) || `c${i}`;

const EmailGeneratorPanel = ({ template, instances = [], busy, contacts = [], recipientEmail = '', recipientName = '', onGenerate, onAddContact, onToggleDone, onSelectVersion, onDelete, onCopyText, onCopyRich }) => {
  const sections = Array.isArray(template.inputSections) ? template.inputSections : [];
  const [inputs, setInputs] = useState({});
  // Eingabe ist offen, solange noch nichts generiert wurde – danach eingeklappt,
  // damit der Fokus auf den Ergebnissen liegt („Weitere generieren" klappt wieder auf).
  const [open, setOpen] = useState(instances.length === 0);

  // ── Empfänger-Auswahl (für ALLE Vorlagen gleich) ──
  // Standard = Owner (sonst erster Kontakt). Auswahl per stabilem Key, damit sie
  // auch nach dem Hinzufügen einer Person bestehen bleibt.
  const [recipientSel, setRecipientSel] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newPerson, setNewPerson] = useState({ name: '', email: '', role: '' });
  // Ansprache (Du/Sie) wird beim Generieren gewählt, da kontaktabhängig.
  const [tone, setTone] = useState('du');

  const defaultContact = contacts.find((c) => c.owner) || contacts[0] || null;
  const selectedContact =
    (recipientSel && contacts.find((c, i) => contactKey(c, i) === recipientSel)) || defaultContact;
  const recipient = selectedContact
    ? { name: contactLabel(selectedContact), role: selectedContact.role || '', email: selectedContact.email || '' }
    : (recipientEmail || recipientName ? { name: recipientName, role: '', email: recipientEmail } : null);

  const missingRequired = sections.some((s) => s.required && !String(inputs[s.id] || '').trim());

  const saveNewPerson = async () => {
    const name = newPerson.name.trim();
    const email = newPerson.email.trim();
    if (!name && !email) return;
    const contact = { name, email, role: newPerson.role.trim(), owner: false };
    if (onAddContact) await onAddContact(contact);
    setRecipientSel(contactKey(contact, contacts.length));
    setNewPerson({ name: '', email: '', role: '' });
    setAdding(false);
  };

  const handleGenerate = () => {
    if (busy || missingRequired) return;
    const res = onGenerate(inputs, recipient, tone);
    if (res && typeof res.then === 'function') {
      res.then((ok) => { if (ok) { setInputs({}); setOpen(false); } });
    }
  };

  return (
    <div className="sb-gen">
      <div className="sb-gen-head">
        <div className="sb-gen-title">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>
          <span>{template.name || 'E-Mail'}</span>
          {instances.length > 0 && <span className="sb-gen-count">{instances.length}</span>}
        </div>
        <button type="button" className="sb-gen-toggle" onClick={() => setOpen((o) => !o)}>
          {open ? 'Eingabe ausblenden' : (instances.length ? '+ Weitere generieren' : 'Eingabe öffnen')}
        </button>
      </div>

      {open && (
        <div className="sb-gen-form">
          <div className="sb-gen-recipient">
            <span className="sb-gen-label">Empfänger</span>
            {contacts.length > 0 && (
              <div className="sb-gen-recipient-row">
                <select
                  className="sb-gen-select"
                  value={selectedContact ? contactKey(selectedContact, contacts.indexOf(selectedContact)) : ''}
                  onChange={(e) => setRecipientSel(e.target.value)}
                  disabled={busy}
                >
                  {contacts.map((c, i) => (
                    <option key={contactKey(c, i)} value={contactKey(c, i)}>
                      {contactLabel(c)}{c.role ? ` · ${c.role}` : ''}{c.email ? ` · ${c.email}` : ''}{c.owner ? ' · Owner' : ''}
                    </option>
                  ))}
                </select>
                <button type="button" className="sb-gen-recipient-add" onClick={() => setAdding((a) => !a)} title="Person hinzufügen" aria-label="Person hinzufügen">+</button>
              </div>
            )}
            {contacts.length === 0 && !adding && (
              <button type="button" className="sb-gen-recipient-add-full" onClick={() => setAdding(true)}>+ Empfänger hinzufügen</button>
            )}
            {adding && (
              <div className="sb-gen-addperson">
                <input className="sb-gen-addinput" placeholder="Name" value={newPerson.name} onChange={(e) => setNewPerson((p) => ({ ...p, name: e.target.value }))} />
                <input className="sb-gen-addinput" placeholder="E-Mail" value={newPerson.email} onChange={(e) => setNewPerson((p) => ({ ...p, email: e.target.value }))} />
                <input className="sb-gen-addinput" placeholder="Rolle (optional)" value={newPerson.role} onChange={(e) => setNewPerson((p) => ({ ...p, role: e.target.value }))} />
                <div className="sb-gen-addperson-foot">
                  <button type="button" className="sb-gen-btn sb-gen-btn-primary" onClick={saveNewPerson} disabled={!newPerson.name.trim() && !newPerson.email.trim()}>Hinzufügen</button>
                  <button type="button" className="sb-gen-btn" onClick={() => { setAdding(false); setNewPerson({ name: '', email: '', role: '' }); }}>Abbrechen</button>
                </div>
              </div>
            )}
            {recipient && !recipient.email && (
              <span className="sb-gen-recipient-warn">Keine E-Mail hinterlegt – „In Mail öffnen" ist dann nicht verfügbar.</span>
            )}
          </div>

          <div className="sb-gen-tone">
            <span className="sb-gen-label">Ansprache</span>
            <div className="sb-gen-seg" role="group" aria-label="Ansprache">
              <button type="button" className={`sb-gen-seg-btn ${tone !== 'sie' ? 'is-active' : ''}`} onClick={() => setTone('du')} disabled={busy}>Locker · per Du</button>
              <button type="button" className={`sb-gen-seg-btn ${tone === 'sie' ? 'is-active' : ''}`} onClick={() => setTone('sie')} disabled={busy}>Höflich · per Sie</button>
            </div>
          </div>
          {template.aiHint && <p className="sb-gen-aihint">{template.aiHint}</p>}
          {sections.length === 0 ? (
            <p className="sb-gen-empty">Diese Vorlage hat keine Eingabe-Felder – klick auf „Generieren", um eine E-Mail aus dem Kontakt-Kontext zu erstellen.</p>
          ) : sections.map((s) => (
            <label key={s.id} className="sb-gen-field">
              <span className="sb-gen-label">
                {s.label}{s.required ? <span className="sb-gen-req"> *</span> : <span className="sb-gen-opt"> (optional)</span>}
              </span>
              <textarea
                className="sb-gen-area"
                value={inputs[s.id] || ''}
                onChange={(e) => setInputs((p) => ({ ...p, [s.id]: e.target.value }))}
                placeholder="Infos hier reindroppen …"
                disabled={busy}
              />
            </label>
          ))}
          <div className="sb-gen-form-foot">
            <span className="sb-gen-form-meta">{template.versions} {template.versions === 1 ? 'Version' : 'Versionen'} werden erstellt</span>
            <button
              type="button"
              className="sb-gen-btn sb-gen-btn-primary"
              onClick={handleGenerate}
              disabled={busy || missingRequired}
              title={missingRequired ? 'Bitte Pflichtfelder (*) ausfüllen' : 'E-Mail-Versionen generieren'}
            >
              {busy ? 'Generiere…' : (instances.length ? 'Generieren' : 'E-Mail generieren')}
            </button>
          </div>
        </div>
      )}

      {busy && instances.length === 0 && (
        <div className="sb-gen-loading" role="status" aria-live="polite">
          <span className="sb-gen-spin" aria-hidden="true" /> E-Mail wird erstellt …
        </div>
      )}

      {instances.map((inst) => {
        const vCount = inst.versions.length;
        const sel = Math.max(0, Math.min(vCount - 1, inst.selectedVersion || 0));
        const version = inst.versions[sel] || { subject: '', body: '' };
        const rcpEmail = (inst.recipient && inst.recipient.email) || recipientEmail;
        const rcpName = (inst.recipient && inst.recipient.name) || recipientName;
        return (
          <div key={inst.id} className={`sb-gen-result ${inst.done ? 'sb-gen-result-done' : ''}`}>
            <div className="sb-gen-result-head">
              <span className="sb-gen-result-title">{template.numbered ? `${template.name} ${inst.number}` : template.name}</span>
              {rcpName && <span className="sb-gen-result-to" title={rcpEmail ? `An ${rcpName} (${rcpEmail})` : `An ${rcpName}`}>an {rcpName}</span>}
              {inst.generatedAt > 0 && (
                <span className="sb-gen-result-meta">{new Date(inst.generatedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
              )}
              <div className="sb-gen-result-actions">
                <button
                  type="button"
                  className={`sb-gen-done ${inst.done ? 'is-on' : ''}`}
                  onClick={() => onToggleDone(inst.id, !inst.done)}
                  title={inst.done ? 'Wieder als offen markieren' : 'Als erledigt markieren'}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  {inst.done ? 'Erledigt' : 'Erledigt setzen'}
                </button>
                <button type="button" className="sb-gen-del" onClick={() => onDelete(inst.id)} title="Diese Mail löschen" aria-label="Diese Mail löschen">×</button>
              </div>
            </div>

            {vCount > 1 && (
              <div className="sb-gen-versions">
                {inst.versions.map((_, i) => (
                  <button key={i} type="button" className={`sb-gen-version ${i === sel ? 'is-active' : ''}`} onClick={() => onSelectVersion(inst.id, i)}>
                    Version {i + 1}
                  </button>
                ))}
              </div>
            )}

            <div className="sb-gen-field-view">
              <span className="sb-gen-view-label">Betreff</span>
              <div className="sb-gen-subject">
                <span>{version.subject || '(kein Betreff)'}</span>
                <button type="button" className="sb-gen-copy" onClick={() => onCopyText(version.subject || '', 'Betreff kopiert.')} title="Betreff kopieren">kopieren</button>
              </div>
            </div>
            <div className="sb-gen-field-view">
              <span className="sb-gen-view-label">E-Mail-Text</span>
              <div className="sb-gen-body sb-kmail-body-rich" dangerouslySetInnerHTML={{ __html: emailBodyToHtml(version.body) }} />
            </div>

            <div className="sb-gen-result-foot">
              <button
                type="button"
                className="sb-gen-btn sb-gen-btn-primary"
                onClick={() => onCopyRich(
                  `<p style="margin:0 0 12px;"><strong>Betreff:</strong> ${escapeHtml(version.subject || '')}</p>${emailBodyToHtml(version.body)}`,
                  `Betreff: ${version.subject || ''}\n\n${version.body || ''}`.trim(),
                  'E-Mail mit Formatierung kopiert.',
                )}
              >
                Betreff + Text kopieren
              </button>
              <button type="button" className="sb-gen-btn" onClick={() => onCopyRich(emailBodyToHtml(version.body), version.body || '', 'Text mit Formatierung kopiert.')}>
                Nur Text kopieren
              </button>
              {rcpEmail && (
                <button
                  type="button"
                  className="sb-gen-btn"
                  onClick={() => {
                    const params = [];
                    if (version.subject) params.push(`subject=${encodeURIComponent(version.subject)}`);
                    if (version.body) params.push(`body=${encodeURIComponent(version.body)}`);
                    window.location.href = `mailto:${encodeURIComponent(rcpEmail)}${params.length ? `?${params.join('&')}` : ''}`;
                  }}
                  title={`In Mail öffnen (an ${rcpName || rcpEmail})`}
                >
                  In Mail öffnen
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Call-Info-Drop: ausklappbares Feld, um rohe Infos aus einem Call zu „droppen".
// Die Infos werden serverseitig zu kundensicheren Anmerkungen organisiert und mit
// der aktuellen Stage markiert im Research (Setup-Details) abgelegt.
// ── Ansprechpartner-Panel: Owner + Kontakte eines Kunden (rein intern) ──
// Zeigt und bearbeitet die direkten Kontakte (Name, Rolle, E-Mail, Telefon,
// Notiz). Genau ein Owner ("mit wem reden wir primär"). Mehrere Personen
// möglich. Diese personenbezogenen Daten leben NUR hier in der Pipeline,
// niemals im (kundensichtbaren) Research-Report.
const emptyContact = () => ({ name: '', role: '', email: '', phone: '', note: '', owner: false });

const ContactsPanel = ({ contacts, onChange }) => {
  const [list, setList] = useState(() => normalizeContacts(contacts));

  // Liste säubern (leere Einträge raus) und genau einen Owner sicherstellen.
  const clean = (arr) => {
    const filtered = arr
      .map((c) => ({
        name: (c.name || '').slice(0, 160),
        role: (c.role || '').slice(0, 160),
        email: (c.email || '').slice(0, 200),
        phone: (c.phone || '').slice(0, 80),
        note: (c.note || '').slice(0, 600),
        owner: !!c.owner,
      }))
      .filter((c) => c.name || c.email || c.phone || c.role || c.note);
    if (filtered.length && !filtered.some((c) => c.owner)) filtered[0].owner = true;
    return filtered;
  };

  // Bei Strukturänderungen (hinzufügen, entfernen, Owner) sofort speichern;
  // Texteingaben werden lokal gehalten und beim Verlassen des Felds gesichert.
  const commit = (next) => {
    const cleaned = clean(next);
    setList(next);
    onChange(cleaned);
  };

  const updateField = (idx, field, value) => {
    setList((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  const saveNow = () => onChange(clean(list));
  const setOwner = (idx) => commit(list.map((c, i) => ({ ...c, owner: i === idx })));
  const remove = (idx) => commit(list.filter((_, i) => i !== idx));
  const add = () => {
    setList((prev) => [...prev, { ...emptyContact(), owner: prev.length === 0 }]);
  };

  const count = list.length;
  return (
    <div className="sb-contacts">
      <div className="sb-contacts-head">
        <div className="sb-contacts-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
          <span>Ansprechpartner</span>
          {count > 0 && <span className="sb-contacts-count">{count}</span>}
        </div>
        <button type="button" className="sb-contacts-add" onClick={add} title="Ansprechpartner hinzufügen">+ Person</button>
      </div>

      {count === 0 ? (
        <p className="sb-contacts-empty">
          Noch keine Kontakte. Aus Info-Dump/API erkannte Ansprechpartner (Name, E-Mail, Telefon) landen automatisch hier – oder manuell ergänzen. Diese Daten sind nur intern und erscheinen nicht im Research.
        </p>
      ) : (
        <div className="sb-contacts-list">
          {list.map((c, idx) => (
            <div key={idx} className={`sb-contact ${c.owner ? 'sb-contact-owner' : ''}`}>
              <div className="sb-contact-row">
                <input
                  className="sb-contact-input sb-contact-name"
                  value={c.name}
                  placeholder="Name"
                  onChange={(e) => updateField(idx, 'name', e.target.value)}
                  onBlur={saveNow}
                />
                <input
                  className="sb-contact-input sb-contact-role"
                  value={c.role}
                  placeholder="Rolle / Funktion"
                  onChange={(e) => updateField(idx, 'role', e.target.value)}
                  onBlur={saveNow}
                />
                <button
                  type="button"
                  className={`sb-contact-owner-btn ${c.owner ? 'is-owner' : ''}`}
                  onClick={() => setOwner(idx)}
                  title={c.owner ? 'Owner – primärer Ansprechpartner' : 'Als Owner markieren'}
                >
                  {c.owner ? '★ Owner' : '☆ Owner'}
                </button>
                <button type="button" className="sb-contact-del" onClick={() => remove(idx)} title="Entfernen">×</button>
              </div>
              <div className="sb-contact-row">
                <input
                  className="sb-contact-input"
                  type="email"
                  value={c.email}
                  placeholder="E-Mail"
                  onChange={(e) => updateField(idx, 'email', e.target.value)}
                  onBlur={saveNow}
                />
                <input
                  className="sb-contact-input"
                  value={c.phone}
                  placeholder="Telefon"
                  onChange={(e) => updateField(idx, 'phone', e.target.value)}
                  onBlur={saveNow}
                />
              </div>
              {(c.email || c.phone) && (
                <div className="sb-contact-quick">
                  {c.email && <a href={`mailto:${c.email}`} className="sb-contact-link" onClick={(e) => e.stopPropagation()}>{c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone.replace(/\s+/g, '')}`} className="sb-contact-link" onClick={(e) => e.stopPropagation()}>{c.phone}</a>}
                </div>
              )}
              <input
                className="sb-contact-input sb-contact-note"
                value={c.note}
                placeholder="Notiz (z. B. bevorzugter Kanal, Durchwahl …)"
                onChange={(e) => updateField(idx, 'note', e.target.value)}
                onBlur={saveNow}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CallInfoDropPanel = ({ stageLabel, callLabel, busy, onDrop }) => {
  const [open, setOpen] = useState(false);
  const [dump, setDump] = useState('');
  const canSubmit = dump.trim().length >= 10 && !busy;
  const submit = () => {
    if (!canSubmit) return;
    const res = onDrop(dump.trim());
    if (res && typeof res.then === 'function') {
      res.then((ok) => { if (ok) { setDump(''); setOpen(false); } });
    }
  };
  // Titel je nach Phase: spezifischer Call-Name, falls vorhanden, sonst generisch.
  const title = callLabel ? `${callLabel}: Infos festhalten` : 'Call-Infos festhalten';
  // Im Kick-off-Call werden zusätzlich offene nächste Schritte erkannt und in die
  // Warteliste übernommen (ohne Timer – den setzt das Team bei Bedarf selbst).
  const isKickoff = callLabel === 'Kick-off-Call';
  return (
    <div className={`sb-kdrop ${open ? 'sb-kdrop-open' : ''}`}>
      {/* Eingeklappt bewusst leise: eine unauffällige Zeile statt prominenter Box,
          damit die visuelle Hierarchie (Nächste Schritte etc.) nicht gestört wird. */}
      <button
        type="button"
        className="sb-kdrop-toggle"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-expanded={open}
      >
        <svg className="sb-kdrop-ic" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
        <span className="sb-kdrop-label">{title}</span>
        <span className="sb-kdrop-opt">optional</span>
        {open && <span className="sb-kdrop-close">schließen</span>}
      </button>

      {open && (
        <div className="sb-kdrop-form">
          <p className="sb-kdrop-empty">
            {callLabel ? `Notizen aus dem ${callLabel} hier reindumpen` : 'Notizen aus dem Call hier reindumpen'}. Sie werden automatisch zu sauberen Anmerkungen sortiert und im Research unter „Weitere wichtige Anmerkungen" abgelegt{stageLabel ? ` (markiert mit „${stageLabel}")` : ''}.{isKickoff ? ' Erkannte offene nächste Schritte werden zudem automatisch in die Kick-off-Warteliste übernommen (ohne Timer).' : ''}
          </p>
          <textarea
            className="sb-kdrop-area"
            value={dump}
            placeholder="Was ist im Call aufgekommen? Einfach alles reinschreiben – die KI filtert das Wichtige heraus und organisiert es in Titel + Anmerkung …"
            onChange={(e) => setDump(e.target.value)}
            disabled={busy}
          />
          <span className="sb-kdrop-hint">
            Bitte keine personenbezogenen Daten. Es werden nur kundensichere, geschäftsrelevante Anmerkungen übernommen (die Research-Seite sieht auch der Kunde).{isKickoff ? ' Offene nächste Schritte landen zusätzlich intern in der Kick-off-Warteliste.' : ''}
          </span>
          <div className="sb-kdrop-actions">
            <button type="button" className="sb-kdrop-submit" onClick={submit} disabled={!canSubmit}>
              {busy ? 'Übernehme…' : 'Ins Research übernehmen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Setup-Info-Drop (Vorbereitungsphase) ──
// Statt freier Call-Notizen werden hier die SETUP-INFOS gedroppt: typischerweise
// die per Mail gelieferten Antworten des Kunden auf unsere Setup-Checkliste vor
// dem Kick-off. Der Dump wird über die Cloud Function den benötigten Infos
// (Setup-Details) im Research zugeordnet – leere Felder werden befüllt,
// vorhandene ergänzt. Erkennt die KI im Text einen Anhang/eine Liste (z. B. eine
// mitgeschickte Blacklist), wird das betroffene Feld als „per Mail übermittelt"
// vermerkt (die Datei selbst laden wir hier bewusst NICHT hoch).
const SetupInfoDropPanel = ({ busy, onDrop }) => {
  const [open, setOpen] = useState(false);
  const [dump, setDump] = useState('');
  const canSubmit = dump.trim().length >= 10 && !busy;
  const submit = () => {
    if (!canSubmit) return;
    const res = onDrop(dump.trim());
    if (res && typeof res.then === 'function') {
      res.then((ok) => { if (ok) { setDump(''); setOpen(false); } });
    }
  };
  return (
    <div className={`sb-kdrop ${open ? 'sb-kdrop-open' : ''}`}>
      <button
        type="button"
        className="sb-kdrop-toggle"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-expanded={open}
      >
        <svg className="sb-kdrop-ic" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
        <span className="sb-kdrop-label">Setup-Infos droppen</span>
        <span className="sb-kdrop-opt">optional</span>
        {open && <span className="sb-kdrop-close">schließen</span>}
      </button>

      {open && (
        <div className="sb-kdrop-form">
          <p className="sb-kdrop-empty">
            Antworten des Kunden auf die Setup-Checkliste (z. B. aus einer E-Mail) hier reindumpen. Die KI ordnet sie automatisch den <strong>benötigten Infos</strong> in den Setup-Details zu: leere Felder werden befüllt, bestehende ergänzt. Erkennt sie, dass eine Liste/Datei (z. B. eine Blacklist) mitgeschickt wurde, wird das Feld als „per Mail übermittelt" vermerkt (die Datei laden wir hier nicht hoch).
          </p>
          <textarea
            className="sb-kdrop-area"
            value={dump}
            placeholder="Mail-Text / Setup-Antworten des Kunden hier reinkopieren – die KI sortiert die Infos in die richtigen Setup-Felder ein …"
            onChange={(e) => setDump(e.target.value)}
            disabled={busy}
          />
          <span className="sb-kdrop-hint">
            Es werden nur die im Text erkennbaren Infos den vorhandenen Setup-Feldern zugeordnet. Nichts wird blind überschrieben.
          </span>
          <div className="sb-kdrop-actions">
            <button type="button" className="sb-kdrop-submit" onClick={submit} disabled={!canSubmit}>
              {busy ? 'Übernehme…' : 'In die Setup-Details übernehmen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Erzeugt eine kurze ID für einen Schritt (kein externes uuid nötig).
const kickoffStepId = () => `ks_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

// Normalisiert das gespeicherte Warten-Objekt auf eine Schritt-Liste. Migriert
// dabei das alte Format (einzelner String `nextStep`) auf die neue Liste mit
// Titel + Beschreibung, damit bestehende Kontakte nichts verlieren.
const normalizeKickoffSteps = (w) => {
  if (!w) return [];
  if (Array.isArray(w.steps)) {
    return w.steps.map((s) => ({
      id: s.id || kickoffStepId(),
      title: s.title || '',
      desc: s.desc || '',
      done: !!s.done,
    }));
  }
  if (w.nextStep) return [{ id: kickoffStepId(), title: w.nextStep, desc: '', done: false }];
  return [];
};

// ── Kick-off-Status-Panel: nur in der Kick-off-Call-Phase ──
// Der Kick-off-Call hat laut Status schon stattgefunden. Hier entscheidet das Team:
//  (a) Kontakt ist startklar -> weiter in die operative Phase, ODER
//  (b) noch NICHT startklar -> festhalten, was fehlt, einen oder MEHRERE selbst
//      definierte nächste Schritte (Titel + Beschreibung) setzen und einen Timer,
//      bis wann es losgeht. Der Kontakt bleibt im Kick-off-Call und steht auf
//      „Warten", bis die offenen Dinge erledigt sind. Bewusst KEIN „Zu Lost".
const KickoffStatusPanel = ({ waiting, contractActive, monthlySuggestion, onStart, onSetWaiting, onClearWaiting }) => {
  const isWaiting = !!(waiting && waiting.active);
  const [showForm, setShowForm] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [missing, setMissing] = useState('');
  const [steps, setSteps] = useState([{ id: kickoffStepId(), title: '', desc: '', done: false }]);
  const [dueAt, setDueAt] = useState(null);
  const [amount, setAmount] = useState(monthlySuggestion ? String(Math.round(monthlySuggestion)) : '');
  const amountNum = Math.max(0, Math.round(Number(String(amount).replace(/[^\d]/g, '')) || 0));

  const openForm = () => {
    setMissing(waiting?.missing || '');
    const existing = normalizeKickoffSteps(waiting);
    setSteps(existing.length ? existing : [{ id: kickoffStepId(), title: '', desc: '', done: false }]);
    setDueAt(typeof waiting?.dueAt === 'number' ? waiting.dueAt : null);
    setShowForm(true);
  };

  const addStep = () => setSteps((prev) => [...prev, { id: kickoffStepId(), title: '', desc: '', done: false }]);
  const removeStep = (id) => setSteps((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.id !== id)));
  const updateStep = (id, patch) => setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const cleanSteps = steps.map((s) => ({ ...s, title: s.title.trim(), desc: s.desc.trim() })).filter((s) => s.title);
  const canSave = cleanSteps.length > 0;

  const save = () => {
    if (!canSave) return; // Mindestens ein nächster Schritt ist Pflicht – sonst wüssten wir nicht, worauf wir warten.
    onSetWaiting({ missing: missing.trim(), steps: cleanSteps, dueAt: typeof dueAt === 'number' ? dueAt : null });
    setShowForm(false);
  };

  // Einzelnen Schritt im Warten-Modus abhaken (ohne Formular). Aktualisiert das
  // gespeicherte Objekt direkt, damit der Fortschritt sofort sichtbar ist.
  const toggleStepDone = (id) => {
    const current = normalizeKickoffSteps(waiting);
    const next = current.map((s) => (s.id === id ? { ...s, done: !s.done } : s));
    onSetWaiting({ missing: waiting?.missing || '', steps: next, dueAt: typeof waiting?.dueAt === 'number' ? waiting.dueAt : null });
  };

  // „Startklar": läuft schon ein Retainer, geht es direkt weiter. Sonst zuerst den
  // Retainer-Betrag bestätigen (startet die monatliche Abrechnung im Zahlungscenter).
  const handleStartClick = () => {
    if (contractActive) { onStart(null); return; }
    setAmount(monthlySuggestion ? String(Math.round(monthlySuggestion)) : '');
    setShowStart(true);
  };

  const waitSteps = normalizeKickoffSteps(waiting);
  const doneCount = waitSteps.filter((s) => s.done).length;
  const allDone = waitSteps.length > 0 && doneCount === waitSteps.length;
  const dueCd = isWaiting ? formatCountdown(waiting?.dueAt, Date.now()) : null;

  return (
    <div className={`sb-kickoff ${isWaiting ? 'sb-kickoff-waiting' : ''}`}>
      <div className="sb-kickoff-head">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" /></svg>
        <span>Kick-off-Call</span>
        {isWaiting && <span className="sb-kickoff-badge">wartet</span>}
      </div>

      {showStart ? (
        <div className="sb-kickoff-form">
          <p className="sb-kickoff-intro">Mit dem Start beginnt der <strong>monatliche Retainer</strong>. Der Betrag wird ab jetzt automatisch jeden Monat im Zahlungscenter fällig gestellt – inkl. Erinnerung & Abfrage, ob Rechnung gesendet bzw. bezahlt wurde. Das ist <strong>dieselbe Zahlungsaktivierung</strong> wie unter „Deal &amp; Team" – nur hier direkt mit dem Wechsel in die operative Phase verbunden.</p>
          <label className="sb-kickoff-label">Monatlicher Retainer (netto)<span className="sb-kickoff-req"> *</span></label>
          <div className="sb-kickoff-money">
            <input
              className="sb-kickoff-input"
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="z. B. 2000"
            />
            <span className="sb-kickoff-money-cur">€ / Monat</span>
          </div>
          {amountNum <= 0 && (
            <p className="sb-kickoff-warn">Bitte einen Betrag &gt; 0 eintragen – sonst werden keine monatlichen Rechnungen erzeugt.</p>
          )}
          <div className="sb-kickoff-actions">
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-primary" onClick={() => onStart(amountNum)} disabled={amountNum <= 0}>
              Retainer starten & in operative Phase
            </button>
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-ghost" onClick={() => setShowStart(false)}>
              Abbrechen
            </button>
          </div>
        </div>
      ) : showForm ? (
        <div className="sb-kickoff-form">
          <p className="sb-kickoff-intro">Der Kick-off war – aber der Kontakt ist noch nicht startklar. Halte fest, was noch fehlt, definiere die nächsten Schritte und setze einen Termin, bis wann es losgeht.</p>
          <label className="sb-kickoff-label">Was fehlt noch / warum noch nicht startklar?</label>
          <textarea
            className="sb-kickoff-area"
            value={missing}
            onChange={(e) => setMissing(e.target.value)}
            placeholder="z. B. CRM-Zugang fehlt noch, Freigabe vom Kunden offen, Tracking nicht eingerichtet …"
          />

          <label className="sb-kickoff-label">Nächste Schritte (selbst definieren)<span className="sb-kickoff-req"> *</span></label>
          <div className="sb-kickoff-steps">
            {steps.map((s, i) => (
              <div className="sb-kickoff-step-edit" key={s.id}>
                <div className="sb-kickoff-step-edit-head">
                  <span className="sb-kickoff-step-num">{i + 1}</span>
                  <input
                    className="sb-kickoff-input sb-kickoff-step-title"
                    type="text"
                    value={s.title}
                    onChange={(e) => updateStep(s.id, { title: e.target.value })}
                    placeholder="Titel – z. B. CRM-Zugang anfordern"
                  />
                  <button
                    type="button"
                    className="sb-kickoff-step-remove"
                    onClick={() => removeStep(s.id)}
                    disabled={steps.length <= 1}
                    title="Schritt entfernen"
                    aria-label="Schritt entfernen"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  </button>
                </div>
                <textarea
                  className="sb-kickoff-area sb-kickoff-step-desc"
                  value={s.desc}
                  onChange={(e) => updateStep(s.id, { desc: e.target.value })}
                  placeholder="Kurze Beschreibung (optional) – worum geht es konkret?"
                />
              </div>
            ))}
          </div>
          <button type="button" className="sb-kickoff-add-step" onClick={addStep}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Schritt hinzufügen
          </button>

          <label className="sb-kickoff-label">Bis wann erledigt? (Timer bis zum Start)</label>
          <div className="sb-kickoff-due-edit">
            <input
              className="sb-kickoff-input"
              type="datetime-local"
              value={toLocalInput(typeof dueAt === 'number' ? dueAt : null)}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) { setDueAt(null); return; }
                const ms = new Date(v).getTime();
                setDueAt(isNaN(ms) ? null : ms);
              }}
            />
            {typeof dueAt === 'number' && (
              <button type="button" className="sb-kickoff-due-clear" onClick={() => setDueAt(null)} title="Termin entfernen">entfernen</button>
            )}
          </div>

          <div className="sb-kickoff-actions">
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-primary" onClick={save} disabled={!canSave}>
              Auf Warten setzen
            </button>
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-ghost" onClick={() => setShowForm(false)}>
              Abbrechen
            </button>
          </div>
        </div>
      ) : isWaiting ? (
        <>
          <div className="sb-kickoff-wait">
            {waiting.missing && (
              <div className="sb-kickoff-wait-row">
                <span className="sb-kickoff-wait-key">Es fehlt noch</span>
                <span className="sb-kickoff-wait-val">{waiting.missing}</span>
              </div>
            )}

            {typeof waiting.dueAt === 'number' && dueCd && (
              <div className="sb-kickoff-wait-row">
                <span className="sb-kickoff-wait-key">Startklar bis</span>
                <span className="sb-kickoff-wait-val sb-kickoff-due">
                  {new Date(waiting.dueAt).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  <span className={`sb-kickoff-due-cd sb-cd-${dueCd.tone}`}>{dueCd.tone === 'over' ? `überfällig (${dueCd.label})` : dueCd.label}</span>
                </span>
              </div>
            )}

            <div className="sb-kickoff-wait-row">
              <span className="sb-kickoff-wait-key">
                Nächste Schritte
                {waitSteps.length > 0 && <span className="sb-kickoff-progress">{doneCount}/{waitSteps.length} erledigt</span>}
              </span>
              <ul className="sb-kickoff-step-list">
                {waitSteps.map((s) => (
                  <li key={s.id} className={`sb-kickoff-step ${s.done ? 'sb-kickoff-step-done' : ''}`}>
                    <button
                      type="button"
                      className="sb-kickoff-step-check"
                      onClick={() => toggleStepDone(s.id)}
                      title={s.done ? 'Als offen markieren' : 'Als erledigt markieren'}
                      aria-label={s.done ? 'Als offen markieren' : 'Als erledigt markieren'}
                    >
                      {s.done
                        ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        : null}
                    </button>
                    <div className="sb-kickoff-step-body">
                      <span className="sb-kickoff-step-title-text">{s.title}</span>
                      {s.desc && <span className="sb-kickoff-step-desc-text">{s.desc}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="sb-kickoff-actions">
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-primary" onClick={handleStartClick}>
              {allDone ? 'Alles erledigt – jetzt starten' : 'Erledigt – jetzt starten'}
            </button>
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-ghost" onClick={openForm}>
              Bearbeiten
            </button>
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-ghost" onClick={onClearWaiting}>
              Warten beenden
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="sb-kickoff-intro">
            Der Kick-off-Call hat stattgefunden. Ist der Kontakt startklar für die operative Phase?
            {contractActive
              ? ' Der monatliche Retainer läuft bereits.'
              : ' Mit dem Start beginnt der monatliche Retainer (automatische Abrechnung im Zahlungscenter).'}
          </p>
          <div className="sb-kickoff-actions">
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-primary" onClick={handleStartClick}>
              Startklar – jetzt starten
            </button>
            <button type="button" className="sb-kickoff-btn sb-kickoff-btn-ghost" onClick={openForm}>
              Noch nicht startklar …
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Prüft, ob ein Daten-Briefing vorliegt (neue Form: fertige E-Mail mit body).
const hasDataBriefing = (b) => !!(b && typeof b.body === 'string' && b.body.trim());

// Daten-Briefing-Panel (Setup-Phase): erzeugt per KI eine fertig vorgeschriebene
// E-Mail an den Outreach-Spezialisten – wen wir suchen, wie groß die Firmen sein
// sollen (min/max) und wo man sie findet. Sprache (DE/EN) wählbar. Ohne Briefing
// steht hier ein auffälliger Call-to-Action-Button.
// Fester Empfänger des Daten-Briefings: unser Outreach-/Daten-Spezialist.
const DATA_BRIEFING_RECIPIENT = { name: 'Abdullah', email: 'abdullah@orientanalytics.com' };

const DataBriefingPanel = ({ briefing, busy, done, ccEmails = [], onToggleDone, onGenerate, onCopyText, onCopyRich }) => {
  const [lang, setLang] = useState(briefing && briefing.lang === 'en' ? 'en' : 'de');
  const has = hasDataBriefing(briefing);
  const cc = Array.isArray(ccEmails) ? ccEmails.filter(Boolean) : [];
  const openInMail = () => {
    const params = [];
    // CC = die diesem Kunden zugewiesenen Teammitglieder (aus den Team-Settings).
    if (cc.length) params.push(`cc=${encodeURIComponent(cc.join(','))}`);
    if (briefing && briefing.subject) params.push(`subject=${encodeURIComponent(briefing.subject)}`);
    if (briefing && briefing.body) params.push(`body=${encodeURIComponent(briefing.body)}`);
    const href = `mailto:${encodeURIComponent(DATA_BRIEFING_RECIPIENT.email)}${params.length ? `?${params.join('&')}` : ''}`;
    window.location.href = href;
  };
  return (
    <div className="sb-brief">
      <div className="sb-brief-head">
        <div className="sb-brief-title">
          <span>Daten-Briefing · Outreach-Spezialist</span>
          {has && briefing.generatedAt && (
            <span className="sb-brief-meta">
              generiert {new Date(briefing.generatedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · {(briefing.lang || 'de').toUpperCase()}
            </span>
          )}
        </div>
        <div className="sb-brief-controls">
          {has && onToggleDone && (
            <button
              type="button"
              className={`sb-kmail-sent-toggle ${done ? 'is-on' : ''}`}
              onClick={onToggleDone}
              title={done ? 'Erledigt-Haken entfernen (Briefing bleibt gespeichert)' : 'Als erledigt markieren'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              {done ? 'Erledigt' : 'Als erledigt markieren'}
            </button>
          )}
          <div className="sb-brief-lang" role="group" aria-label="Sprache">
            <button type="button" className={lang === 'de' ? 'is-on' : ''} onClick={() => setLang('de')} disabled={busy}>DE</button>
            <button type="button" className={lang === 'en' ? 'is-on' : ''} onClick={() => setLang('en')} disabled={busy}>EN</button>
          </div>
          {has && (
            <button type="button" className="sb-kmail-mailapp" onClick={openInMail} title="Öffnet die Mail im Standard-Mailprogramm (z. B. Apple Mail) als Entwurf – mit Empfänger, Betreff & Text. Wird nicht automatisch gesendet.">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
              In Mail öffnen
            </button>
          )}
          {has && (
            <button type="button" className="sb-brief-regen" onClick={() => onGenerate(lang)} disabled={busy} title="E-Mail neu generieren (überschreibt die gespeicherte)">
              {busy ? 'Generiere…' : 'Neu generieren'}
            </button>
          )}
        </div>
      </div>

      {busy ? (
        <div className="sb-brief-loading" role="status" aria-live="polite">
          <span className="sb-brief-spin" aria-hidden="true" />
          <span>E-Mail wird erstellt – Research wird ausgewertet …</span>
        </div>
      ) : !has ? (
        <div className="sb-brief-cta">
          <div className="sb-brief-cta-text">
            <strong>Noch keine Briefing-E-Mail erstellt.</strong>
            <span>Schreibt aus dem Research eine fertige E-Mail an den Outreach-Spezialisten: wen wir suchen, wie groß die Firmen sein sollen (Minimum/Maximum) und wo man sie findet.</span>
          </div>
          <button type="button" className="sb-brief-cta-btn" onClick={() => onGenerate(lang)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="m4 6 8 6 8-6" /></svg>
            E-Mail generieren ({lang.toUpperCase()})
          </button>
        </div>
      ) : (
        <div className="sb-brief-body">
          <div className="sb-kmail-to">
            <span className="sb-kmail-to-label">An</span>
            <span className="sb-kmail-to-email" title={`${DATA_BRIEFING_RECIPIENT.name} · Outreach-/Daten-Spezialist`}>{DATA_BRIEFING_RECIPIENT.email}</span>
            <button
              type="button"
              className="sb-kmail-to-copy"
              onClick={() => onCopyText(DATA_BRIEFING_RECIPIENT.email, 'E-Mail-Adresse kopiert.')}
              title="E-Mail-Adresse kopieren"
            >
              kopieren
            </button>
          </div>
          {cc.length > 0 && (
            <div className="sb-kmail-to">
              <span className="sb-kmail-to-label">CC</span>
              <span className="sb-kmail-to-email" title="Zugewiesene Teammitglieder (aus den Team-Settings)">{cc.join(', ')}</span>
              <button
                type="button"
                className="sb-kmail-to-copy"
                onClick={() => onCopyText(cc.join(', '), 'CC-Adresse(n) kopiert.')}
                title="CC-Adresse(n) kopieren"
              >
                kopieren
              </button>
            </div>
          )}
          <div className="sb-kmail-field">
            <span className="sb-kmail-label">Betreff</span>
            <div className="sb-kmail-subject">
              <span>{briefing.subject || '(kein Betreff)'}</span>
              <button type="button" className="sb-kmail-copy" onClick={() => onCopyText(briefing.subject || '', 'Betreff kopiert.')} title="Betreff kopieren">kopieren</button>
            </div>
          </div>
          <div className="sb-kmail-field">
            <span className="sb-kmail-label">E-Mail-Text</span>
            <div
              className="sb-kmail-body sb-kmail-body-rich"
              dangerouslySetInnerHTML={{ __html: emailBodyToHtml(briefing.body) }}
            />
          </div>
          <div className="sb-kmail-actions">
            <button
              type="button"
              className="sb-kmail-btn sb-kmail-btn-primary"
              onClick={() => onCopyRich(
                `<p style="margin:0 0 12px;"><strong>Betreff:</strong> ${escapeHtml(briefing.subject || '')}</p>${emailBodyToHtml(briefing.body)}`,
                `Betreff: ${briefing.subject || ''}\n\n${briefing.body || ''}`.trim(),
                'E-Mail mit Formatierung kopiert.',
              )}
            >
              Betreff + Text kopieren
            </button>
            <button
              type="button"
              className="sb-kmail-btn"
              onClick={() => onCopyRich(emailBodyToHtml(briefing.body), briefing.body || '', 'Text mit Formatierung kopiert.')}
            >
              Nur Text kopieren
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const hasSalesBriefing = (b) => !!(b && typeof b.body === 'string' && b.body.trim());

// Präsentations-Briefing-Panel (Kennenlern-Phase): erzeugt per KI eine interne
// E-Mail an das Sales-Teammitglied, das den Präsentations-Call führt – mit den
// wichtigsten Daten & Fakten plus Link + Passwort zur Research-Seite. Empfänger
// (ein Teammitglied) ist auswählbar. Logik & Aufbau analog zum DataBriefingPanel.
const SalesBriefingPanel = ({ briefing, busy, members = [], defaultRecipientId = '', hasPresentationDate = false, sentAt = null, onGenerate, onToggleSent, onCopyText, onCopyRich }) => {
  const [recipientId, setRecipientId] = useState(defaultRecipientId);
  const has = hasSalesBriefing(briefing);
  // Gewählter Empfänger für eine neue Generierung; greift auf die aktuelle
  // Auswahl zurück, sonst auf den Vorschlag (Sales-Mitglied / zugewiesen / erstes).
  const selectedId = recipientId || defaultRecipientId;
  const recipient = members.find((m) => m.id === selectedId) || null;
  // Empfänger der bereits generierten Mail (am Kundenobjekt gespeichert) – wird
  // beim „In Mail öffnen" als To verwendet, damit es exakt zur Mail passt.
  const savedEmail = has && briefing.recipientEmail ? briefing.recipientEmail : (recipient && recipient.email) || '';
  const savedName = has && briefing.recipientName ? briefing.recipientName : (recipient && recipient.name) || '';
  const openInMail = () => {
    const params = [];
    if (briefing && briefing.subject) params.push(`subject=${encodeURIComponent(briefing.subject)}`);
    if (briefing && briefing.body) params.push(`body=${encodeURIComponent(briefing.body)}`);
    const href = `mailto:${encodeURIComponent(savedEmail)}${params.length ? `?${params.join('&')}` : ''}`;
    window.location.href = href;
  };
  return (
    <div className="sb-brief">
      <div className="sb-brief-head">
        <div className="sb-brief-title">
          <span>Präsentation an Sales senden</span>
          {has && briefing.generatedAt && (
            <span className="sb-brief-meta">
              generiert {new Date(briefing.generatedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <div className="sb-brief-controls">
          {has && onToggleSent && (
            <button
              type="button"
              className={`sb-kmail-sent-toggle ${sentAt ? 'is-on' : ''}`}
              onClick={onToggleSent}
              title={sentAt ? 'Markierung entfernen (doch nicht gesendet)' : 'Als gesendet markieren'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              {sentAt ? 'Als gesendet markiert' : 'Als gesendet markieren'}
            </button>
          )}
          {has && (
            <button type="button" className="sb-kmail-mailapp" onClick={openInMail} title="Öffnet die Mail im Standard-Mailprogramm (z. B. Apple Mail) als Entwurf – mit Empfänger, Betreff & Text. Wird nicht automatisch gesendet.">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
              In Mail öffnen
            </button>
          )}
          {has && (
            <button type="button" className="sb-brief-regen" onClick={() => onGenerate(recipient)} disabled={busy || !recipient} title={recipient ? 'E-Mail neu generieren (überschreibt die gespeicherte)' : 'Erst ein Teammitglied auswählen'}>
              {busy ? 'Generiere…' : 'Neu generieren'}
            </button>
          )}
        </div>
      </div>

      {/* Empfänger-Auswahl: alle Teammitglieder. Vorausgewählt ist – falls vorhanden –
          ein als Sales/Vertrieb definiertes bzw. ein diesem Kunden zugewiesenes Mitglied. */}
      <div className="sb-brief-recipient">
        <label className="sb-brief-recipient-label" htmlFor="sb-sales-recipient">An (Teammitglied)</label>
        {members.length > 0 ? (
          <select
            id="sb-sales-recipient"
            className="sb-brief-recipient-select"
            value={selectedId}
            onChange={(e) => setRecipientId(e.target.value)}
            disabled={busy}
          >
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || '(ohne Name)'}{m.role ? ` · ${m.role}` : ''}{m.email ? ` · ${m.email}` : ''}
              </option>
            ))}
          </select>
        ) : (
          <span className="sb-brief-recipient-empty">Noch kein Teammitglied angelegt – unter „Deal &amp; Team" hinzufügen.</span>
        )}
      </div>

      {busy ? (
        <div className="sb-brief-loading" role="status" aria-live="polite">
          <span className="sb-brief-spin" aria-hidden="true" />
          <span>E-Mail wird erstellt – Research wird ausgewertet …</span>
        </div>
      ) : !has ? (
        <div className="sb-brief-cta">
          <div className="sb-brief-cta-text">
            <strong>Noch keine Briefing-E-Mail erstellt.</strong>
            <span>Schreibt aus dem Research eine fertige, interne E-Mail an die ausgewählte Person, die den Präsentations-Call führt: die wichtigsten Daten &amp; Fakten plus Link &amp; Passwort zur Research-Seite. {hasPresentationDate ? 'Der Präsentationstermin wird als Fixpunkt erwähnt.' : 'Da noch kein Präsentationstermin steht, fragt die Mail aktiv danach.'}</span>
          </div>
          <button type="button" className="sb-brief-cta-btn" onClick={() => onGenerate(recipient)} disabled={!recipient} title={recipient ? '' : 'Erst ein Teammitglied auswählen'}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="m4 6 8 6 8-6" /></svg>
            E-Mail generieren
          </button>
        </div>
      ) : (
        <div className="sb-brief-body">
          <div className="sb-kmail-to">
            <span className="sb-kmail-to-label">An</span>
            <span className="sb-kmail-to-email" title={savedName ? `${savedName} · Teammitglied` : 'Teammitglied'}>{savedEmail || '(keine E-Mail hinterlegt)'}</span>
            {savedEmail && (
              <button
                type="button"
                className="sb-kmail-to-copy"
                onClick={() => onCopyText(savedEmail, 'E-Mail-Adresse kopiert.')}
                title="E-Mail-Adresse kopieren"
              >
                kopieren
              </button>
            )}
          </div>
          <div className="sb-kmail-field">
            <span className="sb-kmail-label">Betreff</span>
            <div className="sb-kmail-subject">
              <span>{briefing.subject || '(kein Betreff)'}</span>
              <button type="button" className="sb-kmail-copy" onClick={() => onCopyText(briefing.subject || '', 'Betreff kopiert.')} title="Betreff kopieren">kopieren</button>
            </div>
          </div>
          <div className="sb-kmail-field">
            <span className="sb-kmail-label">E-Mail-Text</span>
            <div
              className="sb-kmail-body sb-kmail-body-rich"
              dangerouslySetInnerHTML={{ __html: emailBodyToHtml(briefing.body) }}
            />
          </div>
          <div className="sb-kmail-actions">
            <button
              type="button"
              className="sb-kmail-btn sb-kmail-btn-primary"
              onClick={() => onCopyRich(
                `<p style="margin:0 0 12px;"><strong>Betreff:</strong> ${escapeHtml(briefing.subject || '')}</p>${emailBodyToHtml(briefing.body)}`,
                `Betreff: ${briefing.subject || ''}\n\n${briefing.body || ''}`.trim(),
                'E-Mail mit Formatierung kopiert.',
              )}
            >
              Betreff + Text kopieren
            </button>
            <button
              type="button"
              className="sb-kmail-btn"
              onClick={() => onCopyRich(emailBodyToHtml(briefing.body), briefing.body || '', 'Text mit Formatierung kopiert.')}
            >
              Nur Text kopieren
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Protocool-Übersicht (read-only) im Pipeline-Drawer ──
// Zeigt pro aktivem Protokoll, OB und WAS bereits eingereicht wurde. Quelle ist
// das Kundenobjekt (customers/<slug>/protocool/<linkKey>) – dieselben Daten wie
// im Protocool-Tab. Bearbeitet/geteilt wird ausschließlich im Protocool-Tab;
// hier nur ein Status- + Inhalts-Spiegel. Wird nur ab der Setup-Phase angezeigt.
const ProtocoolSummary = ({ customer, slug, team, onPatch }) => {
  const activeLinks = PROTOCOOL_LINKS.filter((l) => l.active);
  const [shareBusy, setShareBusy] = useState('');
  const [shareErr, setShareErr] = useState('');
  const [copied, setCopied] = useState('');
  // Karten standardmäßig eingeklappt, sobald ein Protokoll ausgefüllt ist – die
  // fertigen Antworten + Nachrichten würden den Überblick sonst sprengen. Pro
  // Link einzeln auf-/zuklappbar; offene/geteilte Karten bleiben offen, damit der
  // nächste Schritt (Starten/Nachricht kopieren) direkt sichtbar ist.
  const [expanded, setExpanded] = useState({});
  const isCardOpen = (linkKey, state) => {
    if (expanded[linkKey] !== undefined) return expanded[linkKey];
    return state !== 'filled'; // ausgefüllt -> standardmäßig zu, sonst offen
  };
  const toggleCard = (linkKey, currentlyOpen) => setExpanded((prev) => ({ ...prev, [linkKey]: !currentlyOpen }));
  const savedCallMs = typeof customer.detailingCallAt === 'number' ? customer.detailingCallAt : null;
  const [callDraft, setCallDraft] = useState(toLocalInput(savedCallMs));
  useEffect(() => { setCallDraft(toLocalInput(savedCallMs)); }, [savedCallMs]);
  const [researchKey, setResearchKey] = useState('');

  const reportSlug = customer.reportSlug || slug;

  // Research-Key (client-unlesbar) einmalig nachladen – fuer den Secret-Key in
  // der Copy-Paste-Nachricht. Best-effort: ohne Key bleibt im Text {KEY} stehen.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const resp = await fetch(RESEARCH_PASSWORDS_FN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: '{}',
        });
        const data = await resp.json().catch(() => ({}));
        if (alive && resp.status === 200 && data.ok && data.passwords) {
          setResearchKey(String(data.passwords[reportSlug] || ''));
        }
      } catch (e) { /* Research-Key optional */ }
    })();
    return () => { alive = false; };
  }, [reportSlug]);

  if (activeLinks.length === 0) return null;

  const callDirty = toLocalInput(savedCallMs) !== callDraft;
  const saveCall = () => {
    if (!callDraft) { onPatch && onPatch({ detailingCallAt: null }); return; }
    const ms = new Date(callDraft).getTime();
    if (!isNaN(ms)) onPatch && onPatch({ detailingCallAt: ms });
  };
  const clearCall = () => { setCallDraft(''); onPatch && onPatch({ detailingCallAt: null }); };

  const shareUrlFor = (linkKey) => `${window.location.origin}/protocool/${slug}/${linkKey}`;
  const researchUrl = `${window.location.origin}/research/${reportSlug}`;

  // Datum des Calls fuer die Nachricht: bevorzugt der gesetzte Detailing-Termin,
  // sonst der Kennenlern-Termin, sonst Platzhalter.
  const callMsForMsg = savedCallMs || (typeof customer.meetingAt === 'number' ? customer.meetingAt : null);
  const callDateText = callMsForMsg
    ? new Date(callMsForMsg).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';
  const deadlineText = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  })();
  // Zugewiesene Team-Mitglieder dieses Projekts -> ersten Outreach-Manager und
  // ersten Team-Copywriter anhand ihrer Position finden (intelligente Namen).
  const teamMap = team || {};
  const assignedIds = Object.keys(customer.team || {});
  const assignedMembers = assignedIds.map((id) => teamMap[id]).filter(Boolean);
  const firstByRole = (role) => assignedMembers.find((m) => roleMatches(m.role, role)) || null;
  const outreachManager = firstByRole('Outreach-Manager');
  const teamCopywriter = firstByRole('Team-Copywriter');

  // Wen man im Call trifft – aus Sicht der jeweiligen Person die ANDERE Rolle.
  // Echter Name falls zugewiesen, sonst neutrale Rollen-Bezeichnung.
  const PARTNER_FALLBACK = { brainstorming: 'dem Outreach-Manager', outreach_prep: 'dem Team-Copywriter' };
  const partnerNameFor = (linkKey) => {
    const member = linkKey === 'brainstorming' ? outreachManager : teamCopywriter;
    return (member && member.name) || PARTNER_FALLBACK[linkKey] || '';
  };

  const messageFor = (linkKey, password) => buildProtocoolShareMessage({
    business: customer.companyName || '',
    researchUrl,
    researchKey,
    researchEmailUrl: `${researchUrl}?tab=emails`,
    feedbackDeadline: deadlineText,
    callDate: callDateText,
    otherName: partnerNameFor(linkKey),
    protocolUrl: shareUrlFor(linkKey),
    protocolKey: password,
    // NEXT STEPS nur in der Outreach-Manager-Nachricht (outreach_prep): er soll
    // die Teams-Einladung an den zugewiesenen Team-Copywriter senden.
    nextSteps: linkKey === 'outreach_prep'
      ? {
          copywriterName: (teamCopywriter && teamCopywriter.name) || '',
          copywriterEmail: (teamCopywriter && teamCopywriter.email) || '',
        }
      : null,
  });

  const createShare = async (linkKey) => {
    if (shareBusy) return;
    setShareBusy(linkKey);
    setShareErr('');
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(PROTOCOOL_SHARE_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug, linkKey, regenerate: false }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status !== 200 || !data.ok) throw new Error(data.error || 'Link konnte nicht erstellt werden.');
      // Kein lokaler State noetig: customers ist live-gebunden, der Spiegel
      // aktualisiert sich automatisch sobald der Share gespeichert ist.
    } catch (e) {
      setShareErr(e.message || 'Fehler beim Erstellen.');
    } finally {
      setShareBusy('');
    }
  };

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(''), 1600);
    } catch (e) { /* clipboard evtl. blockiert */ }
  };

  const renderAnswer = (field, value) => {
    if (field.type === 'rating') return <span className="sb-proto-rating">{value} / {field.max || 10}</span>;
    if (field.type === 'choice' || field.type === 'segment') {
      const opt = (field.options || []).find((o) => o.value === value);
      const tone = opt && opt.tone ? `sb-proto-tone-${opt.tone}` : '';
      return <span className={`sb-proto-pill ${tone}`}>{opt ? opt.label : value}</span>;
    }
    return String(value);
  };

  const ROLE_ICON = { brainstorming: '✍️', outreach_prep: '📣' };

  return (
    <div className="sb-info sb-proto">
      <div className="sb-info-head">Protocool</div>
      <p className="sb-proto-intro">Termin setzen, Links erstellen und fertige Nachrichten an Team-Copywriter & Outreach-Manager kopieren. 🚀</p>

      {/* Detailing-Call-Termin: Datum + Uhrzeit des ersten Calls festlegen.
          Bewusst mit explizitem Speichern, damit nichts versehentlich abgelegt wird. */}
      <div className="sb-proto-calldate">
        <span className="sb-proto-calldate-label">📅 Detailing-Call · Datum & Uhrzeit</span>
        <div className="sb-proto-calldate-row">
          <input
            type="datetime-local"
            className="sb-proto-calldate-input"
            value={callDraft}
            onChange={(e) => setCallDraft(e.target.value)}
          />
          <button
            type="button"
            className={`sb-proto-calldate-save ${callDirty && callDraft ? '' : 'sb-proto-calldate-save-done'}`}
            onClick={saveCall}
            disabled={!callDirty || !callDraft}
          >
            {callDirty ? 'Speichern' : 'Gespeichert ✓'}
          </button>
        </div>
        {savedCallMs ? (
          <span className="sb-proto-calldate-saved">
            🗓️ {new Date(savedCallMs).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr
            <button type="button" className="sb-proto-calldate-clear" onClick={clearCall}>entfernen</button>
          </span>
        ) : (
          <span className="sb-proto-calldate-hint">Noch kein Termin gesetzt.</span>
        )}
      </div>

      {activeLinks.map((link) => {
        const node = (customer.protocool && customer.protocool[link.key]) || {};
        const form = getProtocoolForm(link.key);
        const responses = node.responses || {};
        const completion = formCompletion(form, responses);
        const state = node.submittedAt ? 'filled' : (node.share && node.share.password ? 'shared' : 'empty');
        const stateLabel = state === 'filled'
          ? `Ausgefüllt · ${completion.filled}/${completion.total}`
          : state === 'shared' ? 'Geteilt' : 'Offen';
        const when = node.updatedAt || node.submittedAt;
        const answered = allFormFields(form).filter((f) => isFieldFilled(responses[f.id]));
        const hasShare = !!(node.share && node.share.password);
        const open = isCardOpen(link.key, state);
        return (
          <div key={link.key} className={`sb-proto-card sb-proto-card-${state} ${open ? 'sb-proto-card-open' : 'sb-proto-card-collapsed'}`}>
            <button
              type="button"
              className="sb-proto-link-head sb-proto-link-toggle"
              onClick={() => toggleCard(link.key, open)}
              aria-expanded={open}
            >
              <span className="sb-proto-link-titles">
                <span className="sb-proto-link-role"><span className="sb-proto-role-ic" aria-hidden>{ROLE_ICON[link.key] || '🔗'}</span>{link.stage}</span>
                <span className="sb-proto-link-name">{link.label}</span>
              </span>
              <span className="sb-proto-head-right">
                <span className={`sb-proto-badge sb-proto-${state}`}>{stateLabel}</span>
                <svg className="sb-proto-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>

            {open && (
              <>
                {/* Nachricht + Zugang: sobald ein Link existiert (geteilt ODER ausgefüllt). */}
                {hasShare && (
                  <div className="sb-proto-actions">
                    <button
                      type="button"
                      className="sb-proto-msg-btn"
                      onClick={() => copy(messageFor(link.key, node.share.password), `msg-${link.key}`)}
                    >
                      {copied === `msg-${link.key}` ? '✓ Nachricht kopiert' : '✉️ Nachricht kopieren'}
                    </button>
                    <div className="sb-proto-chiprow">
                      <button type="button" className="sb-proto-chip" onClick={() => copy(shareUrlFor(link.key), `url-${link.key}`)}>
                        {copied === `url-${link.key}` ? '✓ kopiert' : '🔗 Link'}
                      </button>
                      <button type="button" className="sb-proto-chip" onClick={() => copy(node.share.password, `pw-${link.key}`)}>
                        {copied === `pw-${link.key}` ? '✓ kopiert' : '🔑 Passwort'}
                      </button>
                      <a className="sb-proto-chip" href={shareUrlFor(link.key)} target="_blank" rel="noopener noreferrer">↗ Öffnen</a>
                    </div>
                  </div>
                )}

                {state === 'filled' ? (
                  <div className="sb-proto-filled-box">
                    <div className="sb-proto-meta">
                      {node.filledBy ? `von ${node.filledBy} · ` : ''}
                      {when ? new Date(when).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                    <div className="sb-proto-answers">
                      {answered.map((f) => (
                        <div key={f.id} className="sb-proto-answer">
                          <span className="sb-proto-q">{f.label}</span>
                          <span className="sb-proto-a">{renderAnswer(f, responses[f.id])}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : state === 'shared' ? (
                  <span className="sb-proto-share-hint">Link erstellt – wartet auf das Ausfüllen.</span>
                ) : (
                  <div className="sb-proto-start">
                    <p className="sb-proto-empty">Noch nicht gestartet.</p>
                    <button
                      type="button"
                      className="sb-proto-start-btn"
                      onClick={() => createShare(link.key)}
                      disabled={shareBusy === link.key}
                    >
                      {shareBusy === link.key ? 'Wird erstellt…' : '✨ Starten & Link erstellen'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      {shareErr && <div className="sb-proto-err">{shareErr}</div>}
    </div>
  );
};

// ── Launch-Phase-Überblick: kompakte Tages-Leiste für den Detail-Drawer ──
// Zeigt auf einen Blick, welche der WARMUP_RAMP_DAYS Werktage des Hochlaufs bereits
// reportet wurden, welche fehlen (überfällig) und ob der heutige Tag schon erledigt
// ist. Daten kommen aus dem warmups-Knoten (Launch Phase im Research Center).
const WarmupOverview = ({ warmup, now }) => {
  if (!warmup || typeof warmup.startAt !== 'number') return null;

  const startAt = warmup.startAt;
  const days = warmup.days || {};
  const pending = now < startAt; // Startschuss gegeben, läuft aber noch nicht
  const todayKey = warmupDateKey(now);
  const todayIndex = warmupDayIndex(startAt, now);
  const todayWeekend = isWarmupWeekend(now);
  const finished = todayIndex > WARMUP_RAMP_DAYS;

  // Pro Werktag des Hochlaufs den Status bestimmen (Reihenfolge = Tag 1…20).
  const segments = Array.from({ length: WARMUP_RAMP_DAYS }, (_, i) => {
    const d = i + 1;
    const dk = warmupDateKey(warmupWorkdayDate(startAt, d));
    const entry = days[dk] || null;
    const submitted = !!(entry && entry.submittedAt);
    let state;
    if (submitted) state = warmupSubmittedLate(entry) ? 'late' : 'done';
    else if (dk < todayKey) state = 'missed';
    else if (dk === todayKey) state = 'today';
    else state = 'future';
    return { d, dk, state, entry };
  });

  const doneCount = segments.filter((s) => s.state === 'done' || s.state === 'late').length;
  const lateCount = segments.filter((s) => s.state === 'late').length;
  const missedCount = segments.filter((s) => s.state === 'missed').length;

  const todayEntry = days[todayKey] || null;
  const todaySubmitted = !!(todayEntry && todayEntry.submittedAt);

  // Status-Badge für den heutigen Tag.
  let todayBadge;
  if (pending) todayBadge = { cls: 'wait', label: '⏳ Start steht aus' };
  else if (finished) todayBadge = { cls: 'done', label: '🏁 Hochlauf abgeschlossen' };
  else if (todayWeekend) todayBadge = { cls: 'wait', label: '☕ Wochenende – kein Versand' };
  else if (todaySubmitted) {
    todayBadge = warmupSubmittedLate(todayEntry)
      ? { cls: 'late', label: '✓ Heute erledigt (verspätet)' }
      : { cls: 'done', label: '✓ Heute erledigt' };
  } else todayBadge = { cls: 'open', label: '● Heute noch offen' };

  const progressLabel = finished
    ? `Alle ${WARMUP_RAMP_DAYS} Tage durchlaufen`
    : pending
      ? 'Startschuss gegeben'
      : `Tag ${Math.max(1, Math.min(todayIndex, WARMUP_RAMP_DAYS))} von ${WARMUP_RAMP_DAYS}`;

  const stateTitle = (s) => {
    if (s.state === 'done') return `Tag ${s.d} (${fmtWarmupDate(s.dk)}) · pünktlich reportet`;
    if (s.state === 'late') return `Tag ${s.d} (${fmtWarmupDate(s.dk)}) · verspätet nachgetragen`;
    if (s.state === 'missed') return `Tag ${s.d} (${fmtWarmupDate(s.dk)}) · fehlt noch!`;
    if (s.state === 'today') return `Tag ${s.d} (${fmtWarmupDate(s.dk)}) · heute fällig`;
    return `Tag ${s.d} (${fmtWarmupDate(s.dk)}) · noch offen`;
  };

  return (
    <div className="sb-info sb-wu">
      <div className="sb-wu-head">
        <div className="sb-wu-titles">
          <span className="sb-info-head sb-wu-title">🚀 Launch Phase</span>
          <span className="sb-wu-progress">{progressLabel}</span>
        </div>
        <span className={`sb-wu-today sb-wu-today-${todayBadge.cls}`}>{todayBadge.label}</span>
      </div>

      <div className="sb-wu-bar" role="img" aria-label={`${doneCount} von ${WARMUP_RAMP_DAYS} Tagen reportet`}>
        {segments.map((s) => (
          <span key={s.d} className={`sb-wu-seg sb-wu-seg-${s.state}`} title={stateTitle(s)}>
            {s.state === 'today' && <span className="sb-wu-seg-pulse" />}
          </span>
        ))}
      </div>

      <div className="sb-wu-legend">
        <span className="sb-wu-stat sb-wu-stat-done">{doneCount}/{WARMUP_RAMP_DAYS} reportet</span>
        {missedCount > 0 && <span className="sb-wu-stat sb-wu-stat-missed">{missedCount} fehlen</span>}
        {lateCount > 0 && <span className="sb-wu-stat sb-wu-stat-late">{lateCount} verspätet</span>}
      </div>
    </div>
  );
};

// ── Detail-Drawer: Checkliste, Status, Termin, Tracking, Notizen eines Kunden ──
// Modal zum Entsperren eines Research-Previews: fragt einen 2. Info-Dump ab, aus
// dem der komplette Report nachgeneriert wird. Der Analyse-Tab (signal-basierte
// Kontaktliste, Mail-Konzept, Video) sowie Slug und Passwort bleiben erhalten.
const ResearchUnlockModal = ({ companyName, busy, onClose, onSubmit }) => {
  const [dump, setDump] = useState('');
  return (
    <div className="sb-unlock-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="sb-confirm sb-unlock-card" role="dialog" aria-modal="true">
        <h3 className="sb-confirm-title">Komplette Research generieren</h3>
        <p className="sb-confirm-text">
          Für <strong>{companyName}</strong> wurde bisher nur der Analyse-Tab (Preview) erstellt.
          Gib jetzt einen zweiten Info-Dump mit allen Details ein – daraus wird der komplette
          Report generiert und die gesperrten Ansichten werden freigeschaltet.
          Analyse-Tab, Link und Passwort bleiben erhalten.
        </p>
        <textarea
          className="sb-unlock-textarea"
          value={dump}
          disabled={busy}
          placeholder="Info-Dump für die komplette Research: Angebot, USPs, Zielgruppe, Preise, Vertriebs-Setup, bisherige Ergebnisse, Website …"
          onChange={(e) => setDump(e.target.value)}
          autoFocus
        />
        <div className="sb-confirm-actions">
          <button type="button" className="sb-confirm-cancel" onClick={onClose} disabled={busy}>Abbrechen</button>
          <button
            type="button"
            className="sb-unlock-generate"
            onClick={() => onSubmit(dump)}
            disabled={busy || dump.trim().length < 20}
          >
            {busy ? 'Generiere …' : 'Generieren & freischalten'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Reporting-Erinnerungen: wer wird an das fällige Reporting erinnert? ──
//
// Am Fälligkeitstag und danach täglich geht eine Erinnerung an die hier
// ausgewählten Empfänger, bis das Reporting eingereicht ist – danach eine
// Bestätigung. Ausgewählt werden Teammitglieder (Adresse kommt aus den
// Team-Einstellungen) und/oder freie Adressen.
const ReportingMailPanel = ({ customer, team, reporting, settings, now, onPatch }) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const mailSettings = reportingMail.reportingMailSettings(settings);
  const cfg = reportingMail.normalizeMailConfig(customer.reportingMail);
  const resolved = reportingMail.resolveRecipients(customer, team, cfg);
  const assigned = customer.team || {};
  const selected = new Set(cfg.memberIds);

  // Auswahlliste: Teammitglieder mit E-Mail-Adresse. Die diesem Kunden
  // zugewiesenen stehen oben, danach alle übrigen.
  const memberList = Object.keys(team || {})
    .map((id) => ({ id, ...(team[id] || {}) }))
    .filter((m) => reportingMail.isEmail(m.email) && (m.active !== false || selected.has(m.id)))
    .sort((a, b) => {
      const rank = (m) => (selected.has(m.id) ? 0 : (assigned[m.id] != null ? 1 : 2));
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      return (a.name || '').localeCompare(b.name || '');
    });
  const withoutMail = Object.keys(team || {})
    .map((id) => ({ id, ...(team[id] || {}) }))
    .filter((m) => assigned[m.id] != null && m.active !== false && !reportingMail.isEmail(m.email));

  // Jede Änderung schreibt updatedAt: damit gilt die Empfängerliste als bewusst
  // gepflegt und der Outreach-Manager-Fallback greift nicht mehr.
  const patchMail = (patch) => onPatch({ ...patch, 'reportingMail/updatedAt': Date.now() });
  const toggleMember = (id) => patchMail({ [`reportingMail/members/${id}`]: selected.has(id) ? null : true });
  const setEnabled = (on) => patchMail({ 'reportingMail/enabled': on });
  const removeEmail = (mail) => {
    const next = cfg.emails.filter((m) => m !== mail);
    patchMail({ 'reportingMail/emails': next.length ? next : null });
  };
  const addEmail = () => {
    const mails = reportingMail.splitEmails(draft);
    if (!mails.length) { setError('Bitte eine E-Mail-Adresse eingeben.'); return; }
    const bad = mails.filter((m) => !reportingMail.isEmail(m));
    if (bad.length) { setError(`Keine gültige Adresse: ${bad.join(', ')}`); return; }
    const next = reportingMail.uniqueEmails(cfg.emails.concat(mails));
    patchMail({ 'reportingMail/emails': next });
    setDraft('');
    setError('');
  };

  const plan = reporting ? reportingMail.reminderPlan(reporting, customer, mailSettings, now) : null;
  const submitted = !!(reporting && (reporting.status === 'submitted' || typeof reporting.submittedAt === 'number'));
  const count = reporting ? Math.max(0, Math.round(Number(reporting.reminderCount) || 0)) : 0;
  const globalOff = !mailSettings.enabled;
  const noRecipients = resolved.to.length === 0;

  // Eine Zeile Klartext: was passiert als Nächstes (oder warum nichts)?
  const statusText = (() => {
    if (globalOff) return 'Erinnerungen sind global deaktiviert (Settings → Reporting-Mails).';
    if (!resolved.enabled) return 'Für diesen Kunden sind Erinnerungen ausgeschaltet.';
    if (noRecipients) return 'Kein Empfänger ausgewählt – es geht keine Erinnerung raus.';
    if (submitted) {
      return reporting.confirmationSentAt
        ? `Reporting eingereicht, Bestätigung ${fmtDay(reporting.confirmationSentAt)} verschickt.`
        : 'Reporting eingereicht – keine weiteren Erinnerungen.';
    }
    if (!reporting) return 'Erinnerungen starten am Fälligkeitstag des nächsten Reportings.';
    if (plan.daysOverdue < 0) return `Erste Erinnerung am ${fmtDay(reporting.availableAt)} (Fälligkeitstag).`;
    if (plan.reason === 'max-erinnerungen') return `Erinnerungs-Obergrenze erreicht (${count}×) – es geht keine weitere raus.`;
    if (plan.reason === 'vertrag-inaktiv') return 'Vertrag ist nicht aktiv – es gehen keine Erinnerungen raus.';
    const days = plan.daysOverdue;
    const base = days === 0 ? 'Heute fällig' : `Seit ${days} ${days === 1 ? 'Tag' : 'Tagen'} offen`;
    return `${base} · ${count > 0 ? `${count}× erinnert, täglich weiter` : 'Erinnerung geht heute raus'}.`;
  })();

  const stateClass = globalOff || noRecipients || !resolved.enabled
    ? 'sb-rmail-status-warn'
    : (submitted ? 'sb-rmail-status-ok' : (plan && plan.daysOverdue > 0 ? 'sb-rmail-status-due' : 'sb-rmail-status-idle'));

  return (
    <div className="sb-rmail">
      <div className="sb-rmail-head">
        <span>Reporting-Erinnerungen</span>
        <label className="sb-rmail-switch">
          <input type="checkbox" checked={resolved.enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>{resolved.enabled ? 'aktiv' : 'aus'}</span>
        </label>
      </div>
      <p className="sb-rmail-intro">
        Am Fälligkeitstag des Reportings und danach jeden Tag geht eine Erinnerung an die
        ausgewählten Empfänger – bis das Reporting eingereicht ist. Danach kommt eine Bestätigung.
      </p>

      <div className={`sb-rmail-status ${stateClass}`}>{statusText}</div>

      {memberList.length === 0 ? (
        <p className="sb-rmail-empty">
          Kein Teammitglied hat eine E-Mail-Adresse hinterlegt. Trage sie im Tab <strong>Settings → Team</strong> nach
          oder nutze unten eine freie Adresse.
        </p>
      ) : (
        <div className="sb-rmail-members">
          {memberList.map((m) => {
            const on = selected.has(m.id);
            return (
              <button
                type="button"
                key={m.id}
                className={`sb-rmail-member ${on ? 'sb-rmail-member-on' : ''}`}
                onClick={() => toggleMember(m.id)}
              >
                <span className={`sb-team-check ${on ? 'sb-team-check-on' : ''}`} aria-hidden="true">{on ? '✓' : ''}</span>
                <span className="sb-rmail-member-info">
                  <span className="sb-rmail-member-name">
                    {m.name || '(ohne Name)'}
                    {m.role && <span className="sb-team-role">{m.role}</span>}
                    {assigned[m.id] != null && <span className="sb-rmail-member-tag">zugewiesen</span>}
                  </span>
                  <span className="sb-rmail-member-mail">{m.email}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {resolved.fallback && (
        <p className="sb-rmail-hint">
          Noch nichts ausgewählt – ersatzweise wird der zugewiesene Outreach-Manager
          ({resolved.to.join(', ')}) erinnert. Wähle jemanden aus, um das festzulegen.
        </p>
      )}
      {withoutMail.length > 0 && (
        <p className="sb-rmail-hint">
          Ohne E-Mail-Adresse und daher nicht auswählbar: {withoutMail.map((m) => m.name || m.id).join(', ')}.
        </p>
      )}

      <div className="sb-rmail-extra">
        <span className="sb-rmail-extra-label">Weitere Adressen</span>
        {cfg.emails.length > 0 && (
          <div className="sb-rmail-chips">
            {cfg.emails.map((mail) => (
              <span key={mail} className="sb-rmail-chip">
                {mail}
                <button type="button" onClick={() => removeEmail(mail)} aria-label={`${mail} entfernen`}>×</button>
              </span>
            ))}
          </div>
        )}
        <div className="sb-rmail-add">
          <input
            type="email"
            value={draft}
            placeholder="name@firma.de"
            onChange={(e) => { setDraft(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
          />
          <button type="button" onClick={addEmail} disabled={!draft.trim()}>Hinzufügen</button>
        </div>
        {error && <span className="sb-rmail-error">{error}</span>}
      </div>
    </div>
  );
};

// ── Regler mit exakter Eingabe (Konditionen) ──────────────────
// Beträge stellt man am schnellsten mit dem Regler ein – man sieht sofort, wo
// man im Rahmen liegt. Die krumme Zahl aus dem Angebot (4.120 €) bleibt
// trotzdem tippbar: der Wert im Kopf der Zeile ist ein echtes Eingabefeld.
// Getippt wird in einen lokalen Puffer und erst bei Enter/Verlassen übernommen,
// damit nicht jede Zwischenzahl in den Entwurf wandert.
const SliderField = ({ label, value, onChange, min = 0, max = 100, step = 1, unit = '€', caption, warn = false }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  // Liegt der gespeicherte Wert über dem üblichen Rahmen, wächst der Regler mit
  // – sonst würde das Ziehen den Betrag stillschweigend kappen.
  const hi = Math.max(max, value || 0);
  const clamped = Math.min(Math.max(value, min), hi);
  const pct = hi > min ? ((clamped - min) / (hi - min)) * 100 : 0;
  const commit = () => {
    const next = draft.trim() === '' ? min : Math.max(min, Math.round(parseNum(draft)));
    if (next !== value) onChange(next); else setDraft(String(value));
  };
  return (
    <div className={`sb-slider ${warn ? 'sb-slider-warn' : ''}`}>
      <div className="sb-slider-top">
        <span className="sb-slider-lbl">{label}</span>
        <span className="sb-slider-valwrap">
          <input
            className="sb-slider-num"
            type="text"
            inputMode="numeric"
            value={draft}
            aria-label={label}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') { setDraft(String(value)); e.currentTarget.blur(); }
            }}
          />
          {unit && <span className="sb-slider-unit">{unit}</span>}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={hi}
        step={step}
        value={clamped}
        style={{ '--sb-fill': `${pct}%` }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {caption && <span className="sb-slider-cap">{caption}</span>}
    </div>
  );
};

const CustomerDrawer = ({ slug, customer, report, warmup, team, setupPackages = {}, reportings = {}, mailSettings, reportUrl, now, initialTab = 'overview', emailBusy, dropBusy, briefingBusy, emailTemplates = [], tplBusy = {}, onGenerateTemplatedEmail, onPatchGeneratedInstance, onDeleteGeneratedInstance, onGenerateEmail, onDropCallInfo, onDropSetupInfo, onGenerateBriefing, onGenerateSalesBriefing, onCopyText, onCopyRich, onClose, onPatch, onRequestResearchUnlock, onChangeStatus, onDeclareLost, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const superStatus = customer.superStatus || DEFAULT_SUPER_STATUS;
  const isOfferSigned = superStatus === OFFER_SIGNED_SUPER_STATUS;
  const isPresentation = superStatus === PRESENTATION_SUPER_STATUS;
  const isSetup = superStatus === SETUP_SUPER_STATUS;
  const isLost = isLostSuperStatus(superStatus);
  // Protocool-Spiegel im Drawer erst ab der Setup-Phase einblenden (in oder nach Setup).
  const reachedSetup = hasReachedSuperStatus(superStatus, SETUP_SUPER_STATUS);
  const dealFlags = deriveDealFlags(customer);
  // Vertragsfreigabe erst ab dem Kick-off-Call: vorher wird keine Zahlung gestartet.
  const reachedKickoff = hasReachedSuperStatus(superStatus, KICKOFF_SUPER_STATUS);
  // Land des Kunden automatisch aus der Website (Länder-TLD) erkennen – steuert die
  // umsatzsteuerliche Behandlung (IVA / Reverse Charge / Drittland).
  const reportWebsite = (report && report.company && report.company.website) || '';
  const detectedCountry = countryFromWebsite(reportWebsite);
  // Lost-Markierung: eigener Dialog mit Pflicht-Grund (ersetzt den Löschen-Button,
  // solange der Kontakt nicht in der Lost-Phase ist).
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostStatusId, setLostStatusId] = useState(suggestedLostStatus(superStatus));
  const [lostReason, setLostReason] = useState('');
  // Freitext für „Sonstiger Grund …" – greift nur, wenn im Dropdown der Sentinel
  // gewählt wurde. Der eingegebene Text wird ganz normal als Substatus gespeichert.
  const [lostReasonCustom, setLostReasonCustom] = useState('');
  const lostColumn = LOST_PHASE.superStatuses.find((s) => s.id === lostStatusId) || LOST_PHASE.superStatuses[0];
  // Effektiver Grund: bei Custom der getrimmte Freitext, sonst der gewählte Substatus.
  const effectiveLostReason = lostReason === LOST_REASON_CUSTOM ? lostReasonCustom.trim() : lostReason;
  const openLostModal = () => {
    setLostStatusId(suggestedLostStatus(superStatus));
    setLostReason('');
    setLostReasonCustom('');
    setShowLostModal(true);
  };
  // Nachträgliches Bearbeiten des Lost-Grunds im Drawer: ist der gespeicherte Grund
  // ein Freitext (kein vordefinierter Substatus), zeigen wir direkt das Textfeld.
  const savedReasonIsCustom = isCustomLostReason(superStatus, customer.subStatus);
  const [reasonCustomMode, setReasonCustomMode] = useState(false);
  const showCustomReasonField = reasonCustomMode || savedReasonIsCustom;
  const kEmail = customer.kickoffEmail && customer.kickoffEmail.body ? customer.kickoffEmail : null;
  const aEmail = customer.accessEmail && customer.accessEmail.body ? customer.accessEmail : null;
  const sfEmail = customer.setupFeeEmail && customer.setupFeeEmail.body ? customer.setupFeeEmail : null;
  const sections = CHECKLISTS[superStatus] || [];
  const checklist = customer.checklist || {};
  const checklistAuto = customer.checklistAuto || {};
  const column = COLUMNS.find((c) => c.superStatusId === superStatus);
  // Label der aktuellen Stage (z. B. "Präsentations-Call") – markiert gedroppte
  // Call-Infos im Research, damit nachvollziehbar ist, woher sie stammen.
  const stageLabel = column ? column.superStatusLabel : '';
  const inSalesPhase = phaseForSuperStatus(superStatus) === SALES_PHASE_ID;
  const inPrepPhase = phaseForSuperStatus(superStatus) === PREPARATION_PHASE_ID;
  // Operative Phase (Experimentalphase + „erster Kunde generiert"): hier ist der
  // Kunde laufender Zahler. Wir zeigen prominent den Retainer-/Zahler-Status und
  // erlauben das Pausieren (stoppt die monatliche Rechnungsstellung).
  const inOperativePhase = phaseForSuperStatus(superStatus) === OPERATIVE_PHASE_ID;
  // Welcher Call steckt hinter der aktuellen Phase? Damit der Call-Info-Drop klar
  // benannt ist (Präsentations-Call-Infos vs. Kick-off-Call-Infos …) statt nur
  // generisch "Call-Info-Drop". Phasen ohne echten Call fallen auf null zurück.
  const CALL_LABELS = {
    [DEFAULT_SUPER_STATUS]: 'Kennenlern-Call',
    [PRESENTATION_SUPER_STATUS]: 'Präsentations-Call',
    [KICKOFF_SUPER_STATUS]: 'Kick-off-Call',
  };
  const callLabel = CALL_LABELS[superStatus] || null;

  // Erledigte Phasen: alle regulären Spalten VOR der aktuellen. Deren
  // Checklisten wandern als read-only Archiv in den Verlauf, damit der
  // Fokus auf den aktuellen Schritten bleibt (lean).
  const regularColumns = COLUMNS.filter((c) => !isLostSuperStatus(c.superStatusId));
  const currentColIndex = regularColumns.findIndex((c) => c.superStatusId === superStatus);
  const previousColumns = currentColIndex > 0 ? regularColumns.slice(0, currentColIndex) : [];

  const [notes, setNotes] = useState(customer.notes || '');
  const [offerUrl, setOfferUrl] = useState(customer.offerUrl || '');
  // Angebot-Datei-Upload (Firebase Storage): Status für laufenden Upload, Fortschritt & Fehler.
  const [offerUploadBusy, setOfferUploadBusy] = useState(false);
  const [offerUploadPct, setOfferUploadPct] = useState(0);
  const [offerUploadError, setOfferUploadError] = useState('');
  const offerFileInputRef = useRef(null);
  const [monthlyInput, setMonthlyInput] = useState(typeof customer.monthlyOverride === 'number' ? String(customer.monthlyOverride) : '');
  const [tab, setTab] = useState(initialTab || 'overview');
  // Ref auf das Termin-Eingabefeld, damit der "Termin festlegen"-Button direkt
  // den nativen Date-Picker öffnet (Aufmerksamkeit, wenn noch kein Termin steht).
  const presInputRef = useRef(null);
  const openPresPicker = useCallback(() => {
    const el = presInputRef.current;
    if (!el) return;
    el.focus();
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); } catch (_) { /* showPicker ggf. nicht erlaubt */ }
    }
  }, []);
  // Analog für den Kick-off-Termin: "Next Step"-Button öffnet direkt den Picker.
  const kickoffInputRef = useRef(null);
  const openKickoffPicker = useCallback(() => {
    const el = kickoffInputRef.current;
    if (!el) return;
    el.focus();
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); } catch (_) { /* showPicker ggf. nicht erlaubt */ }
    }
  }, []);
  // Checklisten-Verlauf (abgeschlossene Phasen) im Checkliste-Tab: standardmäßig
  // eingeklappt, damit die aktuelle Phase oben den Fokus behält.
  const [showArchive, setShowArchive] = useState(false);
  // ── Frei anlegbare Inhalte: eigene Checklisten-Abschnitte, Notizen & Alerts ──
  // Add-Menue (Plus-Button in der Checkliste) + Add-Modal je Typ.
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addType, setAddType] = useState(null); // 'section' | 'alert' | 'note' | null
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftShowAt, setDraftShowAt] = useState('');
  // Inline-Eingabe „Punkt hinzufuegen" je eigenem Abschnitt.
  const [addItemSection, setAddItemSection] = useState(null);
  const [addItemLabel, setAddItemLabel] = useState('');
  const buildContractDraft = (c) => ({
    startDate: toDateInput(c.contract?.startAt) || toDateInput(Date.now()),
    monthlyAmount: typeof c.contract?.monthlyAmount === 'number' ? String(c.contract.monthlyAmount) : '',
    setupFee: typeof c.contract?.setupFee === 'number' && c.contract.setupFee > 0 ? String(c.contract.setupFee) : '',
    durationMonths: typeof c.contract?.durationMonths === 'number' && c.contract.durationMonths > 0 ? String(c.contract.durationMonths) : '',
    country: c.contract?.country || detectedCountry || '',
    commissionEnabled: !!c.contract?.commissionEnabled,
    appointmentPrice: typeof c.contract?.appointmentPrice === 'number' ? String(c.contract.appointmentPrice) : '',
    commissionBase: typeof c.contract?.commissionBase === 'number' ? String(c.contract.commissionBase) : '',
    includedAppointments: typeof c.contract?.includedAppointments === 'number' ? String(c.contract.includedAppointments) : '',
  });
  const [contractDraft, setContractDraft] = useState(buildContractDraft(customer));
  const [showContractForm, setShowContractForm] = useState(false);
  // Nur Anzeige: Mit wie vielen Terminen soll die Team-Vorschau rechnen?
  const [apptPreview, setApptPreview] = useState(10);
  // Wessen komplette Staffel ist aufgeklappt? (memberId oder null) – immer nur
  // eine, damit der Block ruhig bleibt.
  const [openTier, setOpenTier] = useState(null);
  useEffect(() => {
    setNotes(customer.notes || '');
    setOfferUrl(customer.offerUrl || '');
    setOfferUploadBusy(false);
    setOfferUploadPct(0);
    setOfferUploadError('');
    setMonthlyInput(typeof customer.monthlyOverride === 'number' ? String(customer.monthlyOverride) : '');
    setContractDraft(buildContractDraft(customer));
    setShowContractForm(false);
    setShowLostModal(false);
    setShowArchive(false);
    setAddMenuOpen(false);
    setAddType(null);
    setDraftTitle('');
    setDraftBody('');
    setDraftShowAt('');
    setAddItemSection(null);
    setAddItemLabel('');
    setApptPreview(10);
    setOpenTier(null);
    setTab(initialTab || 'overview');
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-Save (debounced, im Hintergrund) ──
  // Freie Textfelder (Notizen, Angebot-Link, Wert/Monat) werden zusätzlich zum
  // Speichern bei Fokusverlust automatisch ~1s nach der letzten Eingabe nach
  // Firebase geschrieben – so geht beim Refreshen/Tab-Schließen nichts verloren.
  // (Status, Häkchen, Flags, Termin, Vertrag & Team speichern ohnehin sofort.)
  // customerRef hält stets den aktuellen Stand, damit der verzögerte Save gegen
  // den frischen Wert vergleicht und keine veralteten Daten zurückschreibt.
  const customerRef = useRef(customer);
  useEffect(() => { customerRef.current = customer; });
  const saveTimers = useRef({});
  const scheduleSave = useCallback((key, fn, delay = 1000) => {
    const timers = saveTimers.current;
    if (timers[key]) clearTimeout(timers[key].id);
    timers[key] = { fn, id: setTimeout(() => { timers[key] = null; fn(); }, delay) };
  }, []);
  // Beim Schließen/Slug-Wechsel ausstehende Saves sofort ausführen.
  useEffect(() => {
    const timers = saveTimers.current;
    return () => {
      Object.keys(timers).forEach((key) => {
        const t = timers[key];
        if (t) { clearTimeout(t.id); t.fn(); timers[key] = null; }
      });
    };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = checklistItems(superStatus);
  const doneCount = allItems.filter((it) => checklist[it.id]).length;
  const allDone = allItems.length > 0 && doneCount === allItems.length;

  // Kumulierte Checkliste über ALLE Phasen bis zum aktuellen Status: zeigt im
  // Überblick, wie viele Punkte insgesamt anstehen, wie viele erledigt sind und
  // wie viele noch offen sind.
  const cumChecklistItems = cumulativeChecklistItems(superStatus);
  const cumChecklistTotal = cumChecklistItems.length;
  const cumChecklistDone = cumChecklistItems.filter((it) => checklist[it.id]).length;
  const cumChecklistOpen = cumChecklistTotal - cumChecklistDone;

  const presentationAt = typeof customer.presentationAt === 'number' ? customer.presentationAt : null;
  const cd = formatCountdown(presentationAt, now);

  // Kick-off-Termin (Vorbereitungsphase): treibt Countdown + Auto-Move ins Setup
  // und – bei Ablauf – in den Kick-off-Call.
  const kickoffAt = typeof customer.kickoffAt === 'number' ? customer.kickoffAt : null;
  const kcd = formatCountdown(kickoffAt, now);
  const setKickoff = (e) => {
    const v = e.target.value;
    if (!v) { onPatch({ kickoffAt: null, kickoffSource: null }); return; }
    const ms = new Date(v).getTime();
    if (!isNaN(ms)) onPatch({ kickoffAt: ms, kickoffSource: 'manual' });
  };

  // Zeit-Tracking
  const createdAt = typeof customer.createdAt === 'number' ? customer.createdAt : null;
  const firstCustomerAt = typeof customer.firstCustomerAt === 'number' ? customer.firstCustomerAt : null;
  const timeToCustomer = createdAt ? (firstCustomerAt ? firstCustomerAt - createdAt : now - createdAt) : null;
  const durations = computeStatusDurations(customer, now);
  const longest = durations.length ? durations[0] : null;
  const setPresentation = (e) => {
    const v = e.target.value;
    if (!v) { onPatch({ presentationAt: null, presentationSource: null }); return; }
    const ms = new Date(v).getTime();
    if (!isNaN(ms)) onPatch({ presentationAt: ms, presentationSource: 'manual' });
  };

  const toggleItem = (itemId) => {
    const next = !checklist[itemId];
    onPatch({ [`checklist/${itemId}`]: next, [`checklistAuto/${itemId}`]: false });
  };

  // ── Eigene Checklisten-Abschnitte & Notizen/Alerts ──
  const customSections = normalizeCustomSections(customer.customSections);
  const noteEntries = normalizeNoteEntries(customer.noteEntries);
  // Alerts fuer den Ueberblick: ALLE Alerts bleiben sichtbar (auch erledigte –
  // sie verschwinden nicht, sondern werden nur als erledigt dargestellt und sind
  // jederzeit wieder aktivierbar). Offene zuerst, erledigte ans Ende; innerhalb
  // nach Datum sortiert.
  const overviewAlerts = noteEntries
    .filter((n) => n.kind === 'alert')
    .sort((a, b) => {
      if (!!a.done !== !!b.done) return a.done ? 1 : -1;
      return (a.showAt || a.createdAt || 0) - (b.showAt || b.createdAt || 0);
    });
  // Notizen fuer den Ueberblick (ganz unten), neueste zuerst.
  const overviewNotes = noteEntries
    .filter((n) => n.kind === 'note')
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  // Chronologischer Feed im Verlauf (alle Eintraege, neueste zuerst).
  const noteLog = [...noteEntries].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const persistSections = (list) => onPatch({ customSections: list.length ? list : null });
  const persistNotes = (list) => onPatch({ noteEntries: list.length ? list : null });

  const openAdd = (type) => {
    setAddType(type);
    setAddMenuOpen(false);
    setDraftTitle('');
    setDraftBody('');
    setDraftShowAt('');
  };
  const closeAdd = () => { setAddType(null); setDraftTitle(''); setDraftBody(''); setDraftShowAt(''); };

  const submitAdd = () => {
    if (addType === 'section') {
      const title = draftTitle.trim();
      if (!title) return;
      persistSections([...customSections, { id: genId(), title, items: [] }]);
    } else if (addType === 'alert' || addType === 'note') {
      const title = draftTitle.trim();
      const body = draftBody.trim();
      if (!title && !body) return;
      const entry = { id: genId(), kind: addType, title, body, createdAt: Date.now() };
      if (addType === 'alert' && draftShowAt) {
        const ms = new Date(draftShowAt).getTime();
        if (!isNaN(ms)) entry.showAt = ms;
      }
      persistNotes([entry, ...noteEntries]);
    }
    closeAdd();
  };

  const removeNoteEntry = (id) => persistNotes(noteEntries.filter((n) => n.id !== id));
  const toggleAlertDone = (id) => persistNotes(noteEntries.map((n) => (n.id === id ? { ...n, done: !n.done } : n)));

  const addCustomItem = (sectionId, label) => {
    const lbl = (label || '').trim();
    if (!lbl) return;
    persistSections(customSections.map((s) => (
      s.id === sectionId ? { ...s, items: [...s.items, { id: genId(), label: lbl }] } : s
    )));
  };
  const removeCustomItem = (sectionId, itemId) => {
    persistSections(customSections.map((s) => (
      s.id === sectionId ? { ...s, items: s.items.filter((it) => it.id !== itemId) } : s
    )));
    if (checklist[itemId]) onPatch({ [`checklist/${itemId}`]: null });
  };
  const removeCustomSection = (sectionId) => {
    const sec = customSections.find((s) => s.id === sectionId);
    persistSections(customSections.filter((s) => s.id !== sectionId));
    if (sec) {
      const patch = {};
      sec.items.forEach((it) => { if (checklist[it.id]) patch[`checklist/${it.id}`] = null; });
      if (Object.keys(patch).length) onPatch(patch);
    }
  };

  const saveNotes = () => {
    if ((customerRef.current.notes || '') !== notes) onPatch({ notes });
  };
  const saveOfferUrl = () => {
    const v = offerUrl.trim();
    if ((customerRef.current.offerUrl || '') !== v) onPatch({ offerUrl: v });
  };

  // ── Angebot als Datei hochladen (Firebase Storage) ──
  // Lädt die gewählte Datei nach Storage, speichert URL + Metadaten am Kunden und
  // räumt eine evtl. vorher hochgeladene Datei auf. Max. 25 MB.
  const MAX_OFFER_FILE_BYTES = 25 * 1024 * 1024;
  const uploadOfferFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_OFFER_FILE_BYTES) {
      setOfferUploadError('Datei ist zu groß (max. 25 MB).');
      return;
    }
    setOfferUploadError('');
    setOfferUploadBusy(true);
    setOfferUploadPct(0);
    const safeName = (file.name || 'angebot').replace(/[^\w.\-]+/g, '_').slice(0, 120);
    const path = `salesgod-research/offers/${slug}/${Date.now()}_${safeName}`;
    const sRef = storageRef(storage, path);
    try {
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(sRef, file, {
          contentType: file.type || 'application/octet-stream',
        });
        task.on(
          'state_changed',
          (snap) => setOfferUploadPct(snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0),
          reject,
          resolve,
        );
      });
      const url = await getDownloadURL(sRef);
      const prev = customerRef.current.offerFile;
      onPatch({
        offerFile: {
          url,
          name: (file.name || safeName).slice(0, 200),
          size: file.size,
          type: (file.type || '').slice(0, 120),
          path,
          uploadedAt: Date.now(),
        },
      });
      // Vorherige Datei (falls vorhanden) im Hintergrund entfernen.
      if (prev && prev.path && prev.path !== path) {
        deleteObject(storageRef(storage, prev.path)).catch(() => {});
      }
    } catch (err) {
      setOfferUploadError('Upload fehlgeschlagen: ' + (err?.message || 'Unbekannter Fehler'));
    } finally {
      setOfferUploadBusy(false);
      setOfferUploadPct(0);
    }
  };
  const onOfferFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // gleiche Datei erneut wählbar machen
    uploadOfferFile(file);
  };
  const removeOfferFile = () => {
    const prev = customerRef.current.offerFile;
    onPatch({ offerFile: null });
    if (prev && prev.path) deleteObject(storageRef(storage, prev.path)).catch(() => {});
    setOfferUploadError('');
  };

  // Report-Infos für die Vorab-Ansicht
  const ri = report || {};
  const metrics = computeReportMetrics(report);
  const revenue = typeof ri?.config?.revenuePerClose === 'number' ? ri.config.revenuePerClose : null;
  const website = ri?.company?.website || '';
  const reportFacts = [
    { label: 'Branche', value: ri?.company?.industry || '' },
    { label: 'Zielgruppe', value: ri?.company?.audience || '' },
    { label: 'Ø Umsatz / Abschluss', value: revenue ? `${revenue.toLocaleString('de-DE')} €` : '' },
  ].filter((f) => f.value);

  // Wert / Monat: manueller Override schlägt den aus dem Report berechneten Wert.
  const reportMonthly = metrics ? metrics.ourMonthly : null;
  const effectiveMonthly = typeof customer.monthlyOverride === 'number' ? customer.monthlyOverride : reportMonthly;
  const isMonthlyManual = typeof customer.monthlyOverride === 'number';
  const persistMonthly = (str) => {
    const raw = String(str).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.');
    if (raw.trim() === '') {
      if (customerRef.current.monthlyOverride != null) onPatch({ monthlyOverride: null });
      return;
    }
    const n = Math.round(Number(raw));
    if (!isNaN(n) && n >= 0 && customerRef.current.monthlyOverride !== n) onPatch({ monthlyOverride: n });
  };
  const saveMonthly = () => persistMonthly(monthlyInput);
  const clearMonthly = () => { setMonthlyInput(''); if (customer.monthlyOverride != null) onPatch({ monthlyOverride: null }); };

  // Vertrag & Abrechnung: bei Unterschrift aktivieren = startet den Payment-Counter.
  const contract = customer.contract || null;
  const contractActive = !!(contract && contract.active);
  const contractStillRunning = contractRunning(contract, now);
  // Drei Zustände statt zwei: Konditionen können ab sofort festgelegt werden,
  // die Abrechnung startet aber weiterhin erst beim Kick-off-Call.
  //   entwurf   -> Konditionen stehen, es wurde noch nie abgerechnet
  //   pausiert  -> lief schon einmal, ist aber gestoppt
  const contractEverStarted = !!(contract && typeof contract.activatedAt === 'number');
  const contractIsDraft = !!contract && !contractActive && !contractEverStarted;
  const nextDue = contractActive ? nextDuePeriod(contract, now) : null;
  // Für die Reporting-Erinnerungen relevantes Reporting: das älteste fällige,
  // noch nicht eingereichte – sonst das nächste anstehende, sonst das letzte
  // eingereichte (damit die Bestätigung sichtbar bleibt).
  const currentReporting = (() => {
    const periods = reportingPeriods(contract, now);
    let upcoming = null;
    let lastDone = null;
    for (let i = 0; i < periods.length; i++) {
      const p = periods[i];
      const rec = reportings[reportingId(slug, p.periodKey)] || null;
      const done = !!(rec && (rec.status === 'submitted' || typeof rec.submittedAt === 'number'));
      // Noch nicht angelegte Perioden simulieren wir – der Cron legt sie morgens an.
      const shown = rec || { ...p, customerSlug: slug, companyName: customer.companyName, status: p.availableAt > now ? 'scheduled' : 'open' };
      if (p.availableAt <= now && !done) return shown;
      if (done) lastDone = shown;
      else if (!upcoming && p.availableAt > now) upcoming = shown;
    }
    return upcoming || lastDone;
  })();
  // Steuer-Aufschlüsselung des aktiven Vertrags (Brutto = was der Kunde zahlt).
  const contractTaxInfo = taxModeInfo(contract?.taxMode);
  const setupTax = taxBreakdown(contract?.setupFee || 0, contract);
  const setCDraft = (field, value) => setContractDraft((p) => ({ ...p, [field]: value }));
  // Automatisch erkannte Steuer-Behandlung aus dem (erkannten/gewählten) Kundenland.
  const draftCountry = contractDraft.country || detectedCountry || '';
  const draftTax = taxModeForCountry(draftCountry);
  const draftTaxInfo = taxModeInfo(draftTax.taxMode);
  // Leeres Feld heißt „noch nichts festgelegt" und übernimmt den Wert aus dem
  // Report. Eine getippte/geschobene 0 ist dagegen eine Aussage – reines
  // Provisionsmodell ohne Retainer – und darf nicht zurückspringen.
  const draftMonthlyNet = String(contractDraft.monthlyAmount).trim() === ''
    ? Math.max(0, Math.round(effectiveMonthly || 0))
    : Math.max(0, Math.round(parseNum(contractDraft.monthlyAmount)));
  const draftSetupNet = Math.max(0, Math.round(parseNum(contractDraft.setupFee)));
  const draftContractForTax = { taxMode: draftTax.taxMode, vatRate: draftTax.vatRate };
  const draftMonthlyTax = taxBreakdown(draftMonthlyNet, draftContractForTax);
  const draftSetupTax = taxBreakdown(draftSetupNet, draftContractForTax);
  const draftApptPrice = Math.max(0, Math.round(parseNum(contractDraft.appointmentPrice)));
  const draftCommissionBase = String(contractDraft.commissionBase).trim() === ''
    ? draftApptPrice
    : Math.max(0, Math.round(parseNum(contractDraft.commissionBase)));
  const draftDurationMonths = Math.max(0, Math.round(parseNum(contractDraft.durationMonths)));
  const draftIncludedAppts = String(contractDraft.includedAppointments).trim() === ''
    ? (contractDraft.commissionEnabled && draftMonthlyNet === 0 ? 0 : includedAppointmentsFor(null))
    : Math.max(0, Math.round(parseNum(contractDraft.includedAppointments)));
  // Konditionen dürfen jederzeit festgelegt werden – auch lange vor dem
  // Kick-off, denn der Terminpreis ist eine Entscheidung aus der Angebotsphase.
  // Gesperrt ist nur der START der Abrechnung: solange `active` false ist,
  // erzeugt billingPeriods() keine einzige Rechnung.
  const saveContract = (activate) => {
    const startMs = contractDraft.startDate
      ? new Date(contractDraft.startDate + 'T09:00:00').getTime()
      : Date.now();
    const monthlyAmount = draftMonthlyNet;
    const setupFee = draftSetupNet;
    const durationMonths = draftDurationMonths;
    const country = contractDraft.country || detectedCountry || '';
    const tax = taxModeForCountry(country);
    // Provisionsmodell: Ohne eigene Angabe ist der gesamte Terminpreis
    // provisionsfähig und der Retainer deckt weiterhin die Standard-Termine ab.
    const commissionEnabled = !!contractDraft.commissionEnabled;
    const appointmentPrice = draftApptPrice;
    const commissionBase = draftCommissionBase;
    const includedAppointments = draftIncludedAppts;
    // Ein bereits laufender Vertrag bleibt beim Speichern laufend.
    const nextActive = activate || !!(contract && contract.active);
    const next = {
      active: nextActive,
      startAt: startMs,
      monthlyAmount,
      setupFee,
      durationMonths,
      country,
      taxMode: tax.taxMode,
      vatRate: tax.vatRate,
      commissionEnabled,
      appointmentPrice,
      commissionBase,
      includedAppointments,
    };
    // `activatedAt` markiert den echten Abrechnungsstart – reines Festlegen von
    // Konditionen darf ihn nicht setzen, sonst wäre später nicht mehr
    // unterscheidbar, ob ein Vertrag pausiert oder nie gestartet wurde.
    if (contract && typeof contract.activatedAt === 'number') next.activatedAt = contract.activatedAt;
    else if (nextActive) next.activatedAt = Date.now();
    onPatch({ contract: next });
    setShowContractForm(false);
  };
  const deactivateContract = () => { if (contract) onPatch({ 'contract/active': false }); };
  const reactivateContract = () => { if (contract) onPatch({ 'contract/active': true }); };
  // Startet die Abrechnung für bereits festgelegte Konditionen. Bewusst ohne
  // den Formular-Entwurf: Aktivieren ist eine eigene Handlung und darf keine
  // ungespeicherten Eingaben mitschreiben.
  const startContract = () => {
    if (!contract) return;
    const patch = { 'contract/active': true };
    if (typeof contract.activatedAt !== 'number') patch['contract/activatedAt'] = Date.now();
    onPatch(patch);
  };
  // Abbrechen verwirft Eingaben – sonst landen sie beim nächsten Speichern
  // unbemerkt in der Datenbank.
  const toggleContractForm = () => {
    setShowContractForm((open) => {
      if (open) setContractDraft(buildContractDraft(customer));
      return !open;
    });
  };

  // Team & Provision: Mitarbeiter diesem Kunden zuweisen, Auszahlungen berechnen.
  const teamMap = team || {};
  const assigned = customer.team || {}; // { memberId: assignedAt(ms) }
  const teamList = Object.keys(teamMap)
    .map((id) => ({ id, ...teamMap[id] }))
    .filter((m) => m.active !== false || assigned[m.id] != null)
    .sort((a, b) => {
      const aOn = assigned[a.id] != null ? 0 : 1;
      const bOn = assigned[b.id] != null ? 0 : 1;
      if (aOn !== bOn) return aOn - bOn;
      return (a.name || '').localeCompare(b.name || '');
    });
  const toggleMember = (id) => {
    onPatch({ [`team/${id}`]: assigned[id] != null ? null : Date.now() });
  };
  const assignedMembers = teamList.filter((m) => assigned[m.id] != null);
  // E-Mail-Adressen der zugewiesenen Mitarbeiter:innen – werden bei generierten
  // Mails (z. B. Daten-Briefing) automatisch als CC vorbelegt. Doppelte/leere raus.
  const assignedEmails = Array.from(new Set(
    assignedMembers.map((m) => (m.email || '').trim()).filter(Boolean)
  ));
  // Präsentations-Briefing (Kennenlern-Phase): auswählbare Empfänger = alle
  // Teammitglieder mit E-Mail. Vorausgewählt wird – falls vorhanden – ein als
  // Sales/Vertrieb definiertes Mitglied, sonst ein diesem Kunden zugewiesenes,
  // sonst das erste in der Liste. So muss man meist nichts umstellen.
  const briefingMembers = teamList.filter((m) => (m.email || '').trim());
  const salesBriefingObj = customer.salesBriefing && customer.salesBriefing.body ? customer.salesBriefing : null;
  const defaultBriefingRecipientId = (() => {
    const byRole = briefingMembers.find((m) => /sales|vertrieb|präsentation|presentation|closer/i.test(m.role || ''));
    if (byRole) return byRole.id;
    const assignedWithMail = briefingMembers.find((m) => assigned[m.id] != null);
    if (assignedWithMail) return assignedWithMail.id;
    return briefingMembers[0] ? briefingMembers[0].id : '';
  })();
  // Provisionsbasis: bei aktivem Vertrag der vertraglich fixierte Betrag (= Quelle der
  // Wahrheit im Finanzen-Dashboard), sonst der aktuell angesetzte Monatswert (Preview).
  const payoutBase = contractActive && typeof contract.monthlyAmount === 'number' ? contract.monthlyAmount : effectiveMonthly;
  // Termin-Provision: Vorschau für ein Szenario mit `apptPreview` Terminen im Monat.
  // Rechnet mit exakt derselben Logik wie die spätere Abrechnung.
  const commissionOn = !!(contract && contract.commissionEnabled);
  // Provisionsrolle wird PRO KUNDE gewählt: dieselbe Person kann hier Outreach
  // Manager und beim nächsten Kunden Teamleiter sein. Ohne Auswahl gilt die
  // Vorgabe aus den Team-Settings bzw. die Ableitung aus der Position.
  const memberRoleAt = (m) => commissionRoleForMember(m, customerRoleOverride(customer, m.id));
  const setMemberRole = (memberId, roleId) => { onPatch({ [`teamRoles/${memberId}`]: roleId }); };
  const fmtEur = (n) => (!isFinite(n) ? '–' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(n)) + ' €');

  // Solange die Konditionen offen im Formular stehen, rechnet die Team-Vorschau
  // mit den EINGESTELLTEN Werten: man zieht am Regler und sieht sofort, was das
  // für jede:n Einzelne:n bedeutet. Ohne offenes Formular gilt der Vertrag.
  const previewContract = showContractForm
    ? {
      ...(contract || {}),
      monthlyAmount: draftMonthlyNet,
      commissionEnabled: !!contractDraft.commissionEnabled,
      appointmentPrice: draftApptPrice,
      commissionBase: draftCommissionBase,
      includedAppointments: draftIncludedAppts,
      durationMonths: draftDurationMonths,
    }
    : contract;
  const previewCommissionOn = !!(previewContract && previewContract.commissionEnabled);
  const previewApptPrice = appointmentPriceFor(previewContract, 0);
  const previewCommissionBase = previewContract ? commissionBaseFor(previewContract) : 0;
  const previewPayoutBase = showContractForm ? draftMonthlyNet : payoutBase;

  // Steuer für die Kopfzahl: im offenen Formular die eingestellte, sonst die
  // des Vertrags – damit der Bruttowert oben nie zum Regler widersprüchlich ist.
  const kpiMonthlyTax = taxBreakdown(previewPayoutBase || 0, showContractForm ? draftContractForTax : (contract || draftContractForTax));
  // Status in EINEM Satz statt verteilt über drei Karten.
  const dealState = contractActive
    ? (contractStillRunning ? { tone: 'on', label: 'Abrechnung läuft' } : { tone: 'end', label: 'Laufzeit beendet' })
    : contractIsDraft ? { tone: 'draft', label: 'Konditionen stehen · Abrechnung startet beim Kick-off' }
      : contract ? { tone: 'off', label: 'pausiert' }
        : { tone: 'none', label: 'noch keine Konditionen' };

  const memberShare = (m) => commissionForMember(m, previewContract, apptPreview, customerRoleOverride(customer, m.id));
  const memberApptPayout = (m) => {
    const share = memberShare(m);
    return share ? share.amount : 0;
  };
  const memberTotalPayout = (m) => memberMonthlyPayout(m, previewPayoutBase) + memberApptPayout(m);
  const totalPayout = assignedMembers.reduce((s, m) => s + memberTotalPayout(m), 0);
  const apptRevenue = previewCommissionOn ? apptPreview * previewApptPrice : 0;
  // null nur, wenn wir gar keinen Monatswert kennen UND keine Termine rechnen.
  const monthRevenue = previewPayoutBase == null && !previewCommissionOn ? null : (previewPayoutBase || 0) + apptRevenue;
  const roleHolders = (roleId) => assignedMembers.filter((m) => memberRoleAt(m) === roleId);
  // Reihenfolge der Karten: erst der Outreach Manager, dann der Teamleiter, dann
  // der Rest. Bewusst NICHT nach Betrag – sonst springen die Karten beim
  // Umstellen der Rolle durcheinander.
  const ROLE_SORT = { outreach_manager: 0, team_leader: 1 };
  const assignedSorted = assignedMembers.slice().sort((a, b) => {
    const ra = ROLE_SORT[memberRoleAt(a)] != null ? ROLE_SORT[memberRoleAt(a)] : 2;
    const rb = ROLE_SORT[memberRoleAt(b)] != null ? ROLE_SORT[memberRoleAt(b)] : 2;
    return ra - rb || (a.name || '').localeCompare(b.name || '');
  });
  const unassignedMembers = teamList.filter((m) => assigned[m.id] == null);
  const tierScale = (roleId) => (previewCommissionBase / COMMISSION_PLAN_BASE) * COMMISSION_ROLES[roleId].factor;
  const tierRange = (t) => (t.to == null ? `${t.from}+` : `${t.from}–${t.to}`);
  // In welcher Stufe steht die Rolle gerade und was kommt als Nächstes? Beides
  // gehört auf die Karte: „wie viel" und „wie geht es weiter" ist dieselbe Frage.
  const tierStatus = (roleId, count) => {
    const role = COMMISSION_ROLES[roleId];
    const scale = tierScale(roleId);
    return {
      scale,
      current: role.tiers.find((t) => count >= t.from && (t.to == null || count <= t.to)) || null,
      next: role.tiers.find((t) => t.from > count) || null,
      nextUnlock: role.unlocks.find((u) => u.at > count) || null,
    };
  };
  // Aufteilung des Monatsumsatzes: erst die Firma, dann jede Person – als Balken
  // sieht man in einer Sekunde, wer welchen Anteil zieht.
  const splitRows = assignedMembers
    .map((m) => {
      const share = memberShare(m);
      return {
        id: m.id,
        name: m.name || '(ohne Name)',
        roleId: memberRoleAt(m),
        amount: memberMonthlyPayout(m, previewPayoutBase) + (share ? share.amount : 0),
      };
    })
    .filter((r) => r.amount > 0);
  const splitTotal = monthRevenue || 0;
  const splitPct = (v) => (splitTotal > 0 ? Math.max(0, (v / splitTotal) * 100) : 0);

  // Text, den man dem Mitarbeiter direkt schickt: was er bei diesem Kunden
  // verdient und wie die Staffel dahin führt. Bewusst OHNE Kundenpreis,
  // Provisionsbasis und Marge – das sind interne Zahlen.
  const provMessage = (m) => {
    const roleId = memberRoleAt(m);
    const share = memberShare(m);
    const retainer = memberMonthlyPayout(m, previewPayoutBase);
    const L = [`Provision – ${customer.companyName || slug}`, ''];
    if (!share || !roleId) {
      L.push(m.payType === 'fixed'
        ? `Vergütung: ${fmtEur(Number(m.fixedAmount) || 0)} fix / Monat`
        : `Vergütung: ${Number(m.commissionPercent) || 0}% = ${fmtEur(retainer)} / Monat`);
      if (Number(m.appointmentBonus) > 0) L.push(`Bonus: ${fmtEur(Number(m.appointmentBonus))} je Extra-Termin`);
      return L.join('\n');
    }
    const role = COMMISSION_ROLES[roleId];
    const scale = tierScale(roleId);
    L.push(`Deine Rolle: ${role.label}`, '');
    L.push(`Beispiel bei ${apptPreview} ${apptPreview === 1 ? 'Termin' : 'Terminen'} im Monat:`);
    L.push(`• Termin-Provision: ${fmtEur(share.amount)} (${fmtEur(share.ratePerAppointment)} je Termin)`);
    if (share.minimumApplied) L.push(`• Minimum ${fmtEur(share.minimum)} greift`);
    if (retainer > 0) L.push(`• ${m.payType === 'fixed' ? 'Fixum' : 'Grundprovision'}: ${fmtEur(retainer)} / Monat`);
    L.push(`• Gesamt: ${fmtEur(retainer + share.amount)} / Monat`, '');
    L.push(`${role.tierHeading} → Satz je Termin:`);
    role.tiers.forEach((t) => L.push(`  ${t.to == null ? `${t.from}+` : `${t.from}–${t.to}`}   ${fmtEur(t.rate * scale)}`));
    L.push('', `${role.unlockHeading}:`);
    role.unlocks.forEach((u) => L.push(`  ab ${u.at} Terminen   +${fmtEur(u.bonus * scale)}`));
    return L.join('\n');
  };

  const openCount = allItems.length - doneCount;
  const contactList = normalizeContacts(customer.contacts);
  // Primärer Ansprechpartner (Owner, sonst erster Kontakt) – seine E-Mail-Adresse
  // blenden wir oben in der Mail-Box ein, damit sie nur kopiert werden muss.
  const ownerContact = contactList.find((c) => c.owner) || contactList[0] || null;
  const ownerEmail = ownerContact && ownerContact.email ? ownerContact.email : '';
  const ownerName = ownerContact ? contactLabel(ownerContact) : '';
  const TABS = [
    { id: 'overview', label: 'Überblick' },
    { id: 'checklist', label: 'Checkliste', badge: allItems.length > 0 ? `${doneCount}/${allItems.length}` : null, badgeOk: allDone },
    { id: 'deal', label: 'Deal & Team' },
    { id: 'contacts', label: 'Ansprechpartner', badge: contactList.length > 0 ? String(contactList.length) : null },
    { id: 'history', label: 'Verlauf' },
  ];

  return (
    <div className="sb-drawer-overlay" onClick={onClose}>
      <aside className="sb-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="sb-drawer-head">
          <div className="sb-drawer-title">
            <h2>{customer.companyName || '(ohne Name)'}</h2>
            <a href={reportUrl} target="_blank" rel="noreferrer" className="sb-drawer-reportlink">
              Report-Details ansehen ↗
            </a>
          </div>
          {inSalesPhase && cd && (
            <span className={`sb-drawer-cd sb-cd-${cd.tone}`} title={`Präsentationstermin: ${new Date(presentationAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
              {cd.tone === 'over' ? 'Termin vorbei' : cd.label}
            </span>
          )}
          {inPrepPhase && kcd && (
            <span className={`sb-drawer-cd sb-cd-${kcd.tone}`} title={`Kick-off-Termin: ${new Date(kickoffAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
              {kcd.tone === 'over' ? 'Kick-off jetzt' : `Kick-off ${kcd.label}`}
            </span>
          )}
          <button className="sb-drawer-close" onClick={onClose} title="Schließen">×</button>
        </header>

        {/* Status-Steuerung – immer sichtbar */}
        <div className="sb-drawer-controls">
          <label className="sb-ctl">
            <span>Phase / Status</span>
            <select
              value={superStatus}
              onChange={(e) => onChangeStatus(e.target.value)}
            >
              {COLUMNS.map((c) => (
                <option key={c.superStatusId} value={c.superStatusId}>{c.phaseLabel} · {c.superStatusLabel}</option>
              ))}
            </select>
          </label>
          {/* Substatus = Lost-Grund. Gibt es nur noch in der Lost-Phase; dort lässt
              sich der konkrete Grund hier jederzeit nachjustieren. */}
          {isLost && column && column.subStatuses.length > 0 && (
            <label className="sb-ctl">
              <span>Lost-Grund</span>
              <select
                value={showCustomReasonField ? LOST_REASON_CUSTOM : (customer.subStatus || '')}
                onChange={(e) => {
                  if (e.target.value === LOST_REASON_CUSTOM) { setReasonCustomMode(true); }
                  else { setReasonCustomMode(false); onPatch({ subStatus: e.target.value }); }
                }}
              >
                <option value="">— Grund wählen —</option>
                {column.subStatuses.map((sub) => <option key={sub} value={sub}>{sub}</option>)}
                <option value={LOST_REASON_CUSTOM}>Sonstiger Grund …</option>
              </select>
              {showCustomReasonField && (
                <input
                  type="text"
                  className="sb-lost-custom-input"
                  defaultValue={savedReasonIsCustom ? (customer.subStatus || '') : ''}
                  placeholder="Grund frei eingeben …"
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== (customer.subStatus || '')) onPatch({ subStatus: v }); }}
                />
              )}
            </label>
          )}
          {/* Kundenmagnet ("Magnetic Treasury") als Zusatzpaket freischalten. Das Flag
              liegt am Kundenobjekt; die Research-Seite blendet damit den Tab-Inhalt frei
              (sonst sieht der Kunde nur den Locked-Screen). */}
          <label className="sb-ctl sb-ctl-magnet">
            <span>Kundenmagnet</span>
            <button
              type="button"
              className={`sb-magnet-toggle ${customer.leadMagnetUnlocked ? 'is-on' : ''}`}
              role="switch"
              aria-checked={!!customer.leadMagnetUnlocked}
              onClick={() => onPatch({ leadMagnetUnlocked: !customer.leadMagnetUnlocked })}
              title={customer.leadMagnetUnlocked ? 'Kundenmagnet ist freigeschaltet – klicken zum Sperren' : 'Kundenmagnet als Zusatzpaket freischalten'}
            >
              <span className="sb-magnet-toggle-knob" />
              <span className="sb-magnet-toggle-text">{customer.leadMagnetUnlocked ? 'Freigeschaltet' : 'Gesperrt'}</span>
            </button>
          </label>
          {/* Research-Preview (Locked-Modus): nur der Analyse-Tab ist beim Kunden
              freigeschaltet. Beim Entsperren wird ein 2. Info-Dump abgefragt und
              daraus der komplette Report nachgeneriert (Analyse + Slug + Passwort
              bleiben erhalten). Zurueck auf „Preview" sperrt nur wieder die Ansichten. */}
          <label className="sb-ctl sb-ctl-magnet">
            <span>Research</span>
            <button
              type="button"
              className={`sb-magnet-toggle ${customer.researchMode !== 'locked' ? 'is-on' : ''}`}
              role="switch"
              aria-checked={customer.researchMode !== 'locked'}
              onClick={() => {
                if (customer.researchMode === 'locked') {
                  // Entsperren: 2. Info-Dump abfragen und Rest nachgenerieren.
                  if (onRequestResearchUnlock) onRequestResearchUnlock();
                  else onPatch({ researchMode: 'full' });
                } else {
                  // Wieder auf Preview: nur die Ansichten sperren (Inhalt bleibt bestehen).
                  onPatch({ researchMode: 'locked' });
                }
              }}
              title={customer.researchMode === 'locked' ? 'Research-Preview: nur Analyse sichtbar – klicken, um per 2. Info-Dump die komplette Research zu generieren und freizuschalten' : 'Komplette Research freigeschaltet – klicken für Research-Preview (nur Analyse)'}
            >
              <span className="sb-magnet-toggle-knob" />
              <span className="sb-magnet-toggle-text">{customer.researchMode === 'locked' ? 'Preview (nur Analyse)' : 'Komplett frei'}</span>
            </button>
          </label>
        </div>

        {/* Tab-Navigation */}
        <nav className="sb-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`sb-tab ${tab === t.id ? 'sb-tab-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.badge && <span className={`sb-tab-badge ${t.badgeOk ? 'sb-tab-badge-ok' : ''}`}>{t.badge}</span>}
            </button>
          ))}
        </nav>

        <div className="sb-drawer-body">
          {/* ── Tab: Überblick ── */}
          {tab === 'overview' && (
            <>
              {/* ── Nächste Schritte: nummerierte, priorisierte To-do-Liste der
                  Action-Items dieser Phase – immer ganz oben, direkt unter den Tabs.
                  Fasst alle „präsenten" Aufgaben zusammen (Termin fehlt, Mail/Briefing
                  erstellen, Checkliste abarbeiten, Phase weiterbewegen). Erledigte
                  Schritte bleiben im Überblick stehen und werden abgehakt dargestellt
                  (wie die Master-Checkliste); abgehakt wird automatisch, sobald die
                  Aufgabe erfüllt ist (Termin gesetzt, Mail/Briefing erstellt …). */}
              {(() => {
                const briefing = customer.dataBriefing;
                const hasBriefing = hasDataBriefing(briefing);
                // Kick-off-Call wird über das eigene KickoffStatusPanel gesteuert
                // (startklar vs. warten), daher hier nur die Präsentations-Quick-Actions.
                const quickActions = (isPresentation && cd && cd.tone === 'over')
                  ? quickActionsFor(superStatus)
                  : [];
                // Offene Checklistenpunkte über ALLE bisherigen Phasen (wie die Karte).
                const cumItems = isSetup ? cumulativeChecklistItems(superStatus) : [];
                const cumDone = cumItems.filter((it) => checklist[it.id]).length;
                const cumTotal = cumItems.length;
                const cumOpen = cumTotal - cumDone;

                // Reihenfolge = Priorität: zuerst Termine (treiben den Countdown),
                // dann Generieren-Aufgaben, dann Checkliste, zuletzt Phasen-Move.
                // Erledigte Schritte verschwinden NICHT, sondern bleiben im Überblick
                // stehen und werden abgehakt dargestellt (wie die Master-Checkliste).
                // Abgehakt wird ausschliesslich automatisch, sobald die jeweilige
                // Aufgabe erfuellt ist (Termin gesetzt, Mail/Briefing erstellt, …).
                const steps = [];
                // Im Präsentations-Call steht der Termin bereits (so kommt der Kontakt
                // ja erst hierher) – dort beginnt die Liste direkt mit der Angebots-Mail.
                if (inSalesPhase && !isPresentation) {
                  steps.push({ key: 'pres-date', label: 'Präsentationstermin festlegen', desc: 'Fixiert den Termin und startet den Countdown – der wichtigste nächste Schritt.', done: !!presentationAt, doneLabel: 'Termin steht', actionLabel: 'Termin setzen', tone: 'primary', onClick: openPresPicker });
                  const defRecip = briefingMembers.find((m) => m.id === defaultBriefingRecipientId) || null;
                  steps.push({ key: 'sales-briefing', label: 'Präsentation an Sales senden', desc: 'Interne Briefing-Mail (Daten & Fakten + Link & Passwort) an die Person, die den Präsentations-Call führt.', done: !!salesBriefingObj, doneLabel: 'Erstellt', actionLabel: briefingBusy ? 'Generiere…' : 'Generieren', tone: 'primary', disabled: !!briefingBusy || !defRecip, onClick: () => onGenerateSalesBriefing(defRecip) });
                }
                if (inPrepPhase) {
                  steps.push({ key: 'kickoff-date', label: 'Kick-off-Termin festlegen', desc: 'Startet den Countdown bis zum Kick-off-Call.', done: !!kickoffAt, doneLabel: 'Termin steht', actionLabel: 'Termin setzen', tone: 'primary', onClick: openKickoffPicker });
                }
                if (isSetup) {
                  const busy = !!(emailBusy && emailBusy.kickoff);
                  steps.push({ key: 'setup-mail', label: 'Setup-Info-Mail erstellen', desc: 'Fragt die noch fehlenden Infos vor dem Kick-off ab.', done: !!kEmail, doneLabel: 'Erstellt', actionLabel: busy ? 'Generiere…' : 'Generieren', tone: 'primary', disabled: busy, onClick: () => onGenerateEmail('kickoff') });
                  steps.push({ key: 'briefing', label: 'Briefing an Daten-Spezialisten senden', desc: 'Briefing-E-Mail an den Outreach-Spezialisten generieren.', done: hasBriefing, doneLabel: 'Erstellt', actionLabel: briefingBusy ? 'Generiere…' : 'Generieren', tone: 'primary', disabled: !!briefingBusy, onClick: () => onGenerateBriefing('de') });
                }
                if (isSetup && cumTotal > 0) {
                  steps.push({ key: 'checklist', label: 'Checkliste abarbeiten', desc: cumOpen > 0 ? `Noch ${cumOpen} von ${cumTotal} Punkten über alle Phasen offen.` : `Alle ${cumTotal} Punkte erledigt.`, done: cumOpen === 0, doneLabel: 'Komplett', actionLabel: 'Zur Checkliste', tone: 'ghost', onClick: () => setTab('checklist') });
                }
                // „Zu Lost" bewusst NICHT als Nächster-Schritt: das ist kein positiver
                // Schritt und steht ohnehin unten im Footer („Als Lost markieren").
                quickActions.filter((a) => a.tone !== 'lost').forEach((a) => {
                  steps.push({ key: a.id, label: a.label, desc: 'Kontakt in die nächste Phase weiterbewegen.', actionLabel: 'Erledigt', tone: a.tone, onClick: () => onChangeStatus(a.to) });
                });

                if (steps.length === 0) return null;
                const openCount = steps.filter((s) => !s.done).length;
                return (
                  <div className="sb-nextsteps">
                    <div className="sb-nextsteps-head">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>
                      <span>Nächste Schritte</span>
                      <span className="sb-nextsteps-count">{openCount > 0 ? `${openCount} offen` : 'alle erledigt'}</span>
                    </div>
                    <ol className="sb-nextsteps-list">
                      {steps.map((st, i) => (
                        <li key={st.key} className={`sb-nextstep sb-nextstep-${st.tone} ${st.done ? 'sb-nextstep-done' : ''}`}>
                          <span className="sb-nextstep-num" aria-hidden="true">
                            {st.done
                              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                              : i + 1}
                          </span>
                          <div className="sb-nextstep-body">
                            <span className="sb-nextstep-label">{st.label}</span>
                            {st.desc && <span className="sb-nextstep-desc">{st.desc}</span>}
                          </div>
                          {st.done ? (
                            <span className="sb-nextstep-tag">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                              {st.doneLabel || 'Erledigt'}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className={`sb-nextstep-btn sb-nextstep-btn-${st.tone}`}
                              onClick={st.onClick}
                              disabled={st.disabled}
                            >
                              {st.actionLabel}
                            </button>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                );
              })()}

              {/* Alerts – datierte Hinweise, die direkt unter „Nächste Schritte"
                  erscheinen. Vor dem gesetzten Datum als „geplant ab …" gedämpft,
                  ab dem Datum aktiv hervorgehoben. Erledigte landen nur im Verlauf. */}
              {overviewAlerts.length > 0 && (
                <div className="sb-alerts">
                  <div className="sb-alerts-head">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                    <span>Alerts</span>
                  </div>
                  {overviewAlerts.map((a) => {
                    const done = !!a.done;
                    const pending = typeof a.showAt === 'number' && a.showAt > now;
                    const dateLabel = typeof a.showAt === 'number'
                      ? new Date(a.showAt).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })
                      : '';
                    return (
                      <div key={a.id} className={`sb-alert ${done ? 'sb-alert-done' : (pending ? 'sb-alert-pending' : 'sb-alert-active')}`}>
                        <button
                          type="button"
                          className={`sb-alert-check ${done ? 'sb-alert-check-on' : ''}`}
                          onClick={() => toggleAlertDone(a.id)}
                          title={done ? 'Wieder aktivieren' : 'Als erledigt markieren'}
                          aria-label={done ? 'Wieder aktivieren' : 'Als erledigt markieren'}
                          aria-pressed={done}
                        >
                          {done && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                        </button>
                        <div className="sb-alert-body">
                          {a.title && <span className="sb-alert-title">{a.title}</span>}
                          {a.body && <span className="sb-alert-text">{a.body}</span>}
                          {(done || dateLabel) && (
                            <span className="sb-alert-date">
                              {done
                                ? (dateLabel ? `erledigt · ${dateLabel}` : 'erledigt')
                                : `${pending ? 'geplant ab ' : 'seit '}${dateLabel}`}
                            </span>
                          )}
                        </div>
                        <button type="button" className="sb-alert-del" onClick={() => removeNoteEntry(a.id)} title="Alert löschen">×</button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Zahler-Status – nur in der operativen Phase (Experimentalphase +
                  „erster Kunde generiert"). Zeigt, ob der Kunde aktiver Zahler ist
                  (laufender Retainer), und erlaubt das Pausieren: stoppt die Cloud-
                  Function/Abrechnung, es werden keine weiteren Rechnungen erzeugt. */}
              {inOperativePhase && (
                <div className={`sb-payer ${contractActive ? 'sb-payer-active' : (contract ? 'sb-payer-paused' : 'sb-payer-none')}`}>
                  <div className="sb-payer-head">
                    <span className="sb-payer-dot" />
                    <span className="sb-payer-title">
                      {contractActive ? 'Aktiver Zahler' : (contract ? 'Zahlungen pausiert' : 'Kein aktiver Retainer')}
                    </span>
                    {contractActive && typeof contract.monthlyAmount === 'number' && (
                      <span className="sb-payer-amount">{fmtEuro(contract.monthlyAmount)} / Monat</span>
                    )}
                  </div>
                  {contractActive ? (
                    <>
                      <p className="sb-payer-note">
                        Monatlicher Retainer läuft – jeden Monat wird automatisch eine Rechnung im Zahlungscenter fällig gestellt.
                        {nextDue && <> Nächste Rechnung: <strong>{fmtDay(nextDue.dueDate)}</strong> · <strong>{fmtEuro(nextDue.amount)}</strong> brutto.</>}
                        {contract.durationMonths > 0 && <> · Monat {Math.min(contract.durationMonths, monthsElapsed(contract.startAt, now) + 1)} von {contract.durationMonths}.</>}
                      </p>
                      <div className="sb-payer-actions">
                        <button type="button" className="sb-payer-btn sb-payer-btn-off" onClick={deactivateContract}>
                          Kunde pausieren
                        </button>
                        <button type="button" className="sb-payer-btn sb-payer-btn-ghost" onClick={() => setTab('deal')}>
                          Vertrag &amp; Rechnungen
                        </button>
                      </div>
                    </>
                  ) : contract ? (
                    <>
                      <p className="sb-payer-note">Pausiert – es werden <strong>keine weiteren Rechnungen</strong> mehr erzeugt. Bei Bedarf den Retainer fortsetzen.</p>
                      <div className="sb-payer-actions">
                        <button type="button" className="sb-payer-btn sb-payer-btn-primary" onClick={reactivateContract}>
                          Zahlungen fortsetzen
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="sb-payer-note">Dieser operative Kunde hat noch keinen laufenden Retainer. Die <strong>Zahlung aktivierst du unter „Deal &amp; Team"</strong> (Vertrag &amp; Abrechnung) – das ist dieselbe Aktivierung, die sonst der Kick-off auslöst. Danach wird automatisch monatlich abgerechnet.</p>
                      <div className="sb-payer-actions">
                        <button type="button" className="sb-payer-btn sb-payer-btn-primary" onClick={() => setTab('deal')}>
                          Zahlung aktivieren (Deal &amp; Team)
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Vorlagen-gesteuerte E-Mail-Generatoren (E-Mail-Vorlagen aus den
                  Settings, gefiltert auf die aktuelle Phase). Drop-Felder rein,
                  mehrere Versionen raus, Erledigt-Status je Mail. */}
              {emailTemplates.length > 0 && emailTemplates.map((tpl) => (
                <EmailGeneratorPanel
                  key={tpl.id}
                  template={tpl}
                  instances={normalizeGeneratedInstances(customer.generatedEmails && customer.generatedEmails[tpl.id])}
                  busy={!!tplBusy[`${slug}:${tpl.id}`]}
                  contacts={contactList}
                  recipientEmail={ownerEmail}
                  recipientName={ownerName}
                  onAddContact={(contact) => onPatch({ contacts: [...contactList, contact] })}
                  onGenerate={(inputs, recipient, tone) => onGenerateTemplatedEmail(tpl, inputs, recipient, tone)}
                  onToggleDone={(instanceId, done) => onPatchGeneratedInstance(tpl.id, instanceId, { done, doneAt: done ? Date.now() : null })}
                  onSelectVersion={(instanceId, idx) => onPatchGeneratedInstance(tpl.id, instanceId, { selectedVersion: idx })}
                  onDelete={(instanceId) => onDeleteGeneratedInstance(tpl.id, instanceId)}
                  onCopyText={onCopyText}
                  onCopyRich={onCopyRich}
                />
              ))}

              {/* Kick-off-Status – nur in der Kick-off-Call-Phase. „Startklar" startet
                  ab hier den monatlichen Retainer (Vertrag aktivieren = Quelle der
                  Wahrheit fürs Zahlungscenter) UND verschiebt in die operative Phase.
                  „Noch nicht startklar" -> selbst definierter nächster Schritt + warten. */}
              {superStatus === KICKOFF_SUPER_STATUS && (
                <KickoffStatusPanel
                  key={slug}
                  waiting={customer.kickoffWaiting}
                  contractActive={contractActive}
                  monthlySuggestion={contractActive && typeof contract?.monthlyAmount === 'number'
                    ? contract.monthlyAmount
                    : (effectiveMonthly || 0)}
                  onStart={(monthlyAmount) => {
                    // Retainer ab jetzt aktivieren (falls noch kein aktiver Vertrag),
                    // damit der Betrag automatisch monatlich im Zahlungscenter fällig wird.
                    if (!contractActive) {
                      const amount = Math.max(0, Math.round(parseNum(monthlyAmount) || effectiveMonthly || 0));
                      const country = (contract && contract.country) || detectedCountry || '';
                      const tax = taxModeForCountry(country);
                      onPatch({
                        contract: {
                          active: true,
                          startAt: Date.now(),
                          monthlyAmount: amount,
                          setupFee: (contract && typeof contract.setupFee === 'number') ? contract.setupFee : 0,
                          durationMonths: (contract && typeof contract.durationMonths === 'number') ? contract.durationMonths : 0,
                          country,
                          taxMode: tax.taxMode,
                          vatRate: tax.vatRate,
                          activatedAt: (contract && contract.activatedAt) || Date.now(),
                        },
                        kickoffWaiting: null,
                      });
                    } else {
                      onPatch({ kickoffWaiting: null });
                    }
                    onChangeStatus('experimental');
                  }}
                  onSetWaiting={({ missing, steps, dueAt }) => onPatch({ kickoffWaiting: { active: true, missing, steps, dueAt: typeof dueAt === 'number' ? dueAt : null, setAt: (customer.kickoffWaiting && customer.kickoffWaiting.setAt) || Date.now() } })}
                  onClearWaiting={() => onPatch({ kickoffWaiting: null })}
                />
              )}

              {/* Präsentations-Briefing an Sales – nur in der Kennenlern-Phase.
                  Interne E-Mail an das Teammitglied, das den Präsentations-Call
                  führt: Daten & Fakten + Link & Passwort zur Research-Seite. */}
              {inSalesPhase && !isPresentation && (
                <SalesBriefingPanel
                  briefing={salesBriefingObj}
                  busy={!!briefingBusy}
                  members={briefingMembers}
                  defaultRecipientId={defaultBriefingRecipientId}
                  hasPresentationDate={!!presentationAt}
                  sentAt={typeof customer.salesBriefingAt === 'number' ? customer.salesBriefingAt : null}
                  onGenerate={(recipient) => onGenerateSalesBriefing(recipient)}
                  onToggleSent={() => onPatch({ salesBriefingAt: typeof customer.salesBriefingAt === 'number' ? null : Date.now() })}
                  onCopyText={onCopyText}
                  onCopyRich={onCopyRich}
                />
              )}

              {/* Hinweis: Die Angebots-E-Mail nach dem Präsentations-Call läuft jetzt
                  über eine dynamische E-Mail-Vorlage (Settings → E-Mail-Vorlagen),
                  die in der Phase „Präsentations-Call" als Generator-Panel erscheint. */}

              {/* Daten-Briefing für den Outreach-Spezialisten – nur im Setup. */}
              {isSetup && (
                <DataBriefingPanel
                  briefing={customer.dataBriefing}
                  busy={!!briefingBusy}
                  done={!!checklist.setup_data_briefing}
                  ccEmails={assignedEmails}
                  onToggleDone={() => toggleItem('setup_data_briefing')}
                  onGenerate={(lang) => onGenerateBriefing(lang)}
                  onCopyText={onCopyText}
                  onCopyRich={onCopyRich}
                />
              )}

              {/* Zugangs-Mail zu Dashboard + Posteingang – nur im Setup. Geht an
                  denselben Ansprechpartner wie die Setup-Info-Mail. */}
              {isSetup && (
                <EmailPanel
                  title="Zugangs-Mail · Dashboard & Posteingang"
                  email={aEmail}
                  busy={!!(emailBusy && emailBusy.access)}
                  onGenerate={() => onGenerateEmail('access')}
                  onCopyText={onCopyText}
                  onCopyRich={onCopyRich}
                  generateLabel="Zugangs-Mail generieren"
                  recipientEmail={ownerEmail}
                  recipientName={ownerName}
                  now={now}
                  sentAt={typeof customer.accessEmailSentAt === 'number' ? customer.accessEmailSentAt : null}
                  onToggleSent={() => onPatch({ accessEmailSentAt: typeof customer.accessEmailSentAt === 'number' ? null : Date.now() })}
                  emptyText="Erzeugt eine kurze Mail an den Ansprechpartner: Zugang zum Management-Dashboard (Status, nächste Schritte, Reportings, erste Mail-Ideen, Feedback) plus Posteingang (BonusClub-Konto oder Postfach via Hostinger). Link & Dashboard-Passwort werden automatisch eingesetzt."
                  hint={<>Platzhalter wie {'{ANREDE}'}, [E-MAIL-KONTO] und [POSTFACH-PASSWORT] vor dem Versand ersetzen.</>}
                />
              )}

              {/* Setup-Info-Mail vor dem Kick-Off – jetzt in der Phase "Setup" */}
              {isSetup && (
                <EmailPanel
                  title="Setup-Info-Mail vor dem Kick-Off"
                  email={kEmail}
                  busy={!!(emailBusy && emailBusy.kickoff)}
                  onGenerate={() => onGenerateEmail('kickoff')}
                  onCopyText={onCopyText}
                  onCopyRich={onCopyRich}
                  recipientEmail={ownerEmail}
                  recipientName={ownerName}
                  now={now}
                  sentAt={typeof customer.kickoffEmailSentAt === 'number' ? customer.kickoffEmailSentAt : null}
                  onToggleSent={() => onPatch({ kickoffEmailSentAt: typeof customer.kickoffEmailSentAt === 'number' ? null : Date.now() })}
                  emptyText="Erzeugt aus dem Research eine fertige E-Mail, die nur die noch fehlenden Infos abfragt (alles, was wir schon haben, wird weggelassen). Danach gespeichert und jederzeit per Copy-Paste abrufbar."
                  hint={<>Platzhalter wie {'{ANREDE}'} vor dem Versand ersetzen.</>}
                />
              )}

              {/* Setup-Fee-Mail – Alternative/Ergänzung zur Setup-Info-Mail in der
                  Phase "Setup". Wegklickbar, falls keine Setup-Fee vereinbart wurde. */}
              {isSetup && (
                customer.setupFeeEmailDismissed ? (
                  <button
                    type="button"
                    className="sb-kmail-reshow"
                    onClick={() => onPatch({ setupFeeEmailDismissed: null })}
                    title="Setup-Fee-Mail wieder einblenden"
                  >
                    + Setup-Fee-Mail einblenden
                  </button>
                ) : (
                  <EmailPanel
                    title="Setup-Fee-Mail · Setup-Rechnung"
                    email={sfEmail}
                    busy={!!(emailBusy && emailBusy.setupfee)}
                    onGenerate={() => onGenerateEmail('setupfee')}
                    onCopyText={onCopyText}
                    onCopyRich={onCopyRich}
                    generateLabel="Setup-Fee-Mail generieren"
                    recipientEmail={ownerEmail}
                    recipientName={ownerName}
                    onDismiss={() => onPatch({ setupFeeEmailDismissed: true })}
                    dismissTitle="Keine Setup-Fee vereinbart? Diese Mail ausblenden"
                    now={now}
                    sentAt={typeof customer.setupFeeEmailSentAt === 'number' ? customer.setupFeeEmailSentAt : null}
                    onToggleSent={() => onPatch({ setupFeeEmailSentAt: typeof customer.setupFeeEmailSentAt === 'number' ? null : Date.now() })}
                    emptyText="Kurze Mail an den Ansprechpartner: Wir starten mit dem Setup und senden deshalb direkt die Setup-Rechnung. Die monatliche Fee startet erst nach dem Kick-off-Call. Der vereinbarte Setup-Betrag (netto) wird automatisch aus dem Vertrag eingesetzt."
                    hint={<>Platzhalter {'{ANREDE}'} vor dem Versand ersetzen. Die Setup-Rechnung als Anhang nicht vergessen.</>}
                  />
                )
              )}

              {metrics && (
                <div className="sb-metrics">
                  <div className="sb-metric">
                    <div className="sb-metric-top">
                      <span className="sb-metric-dot" style={{ background: metrics.verdict.color }} />
                      <span className="sb-metric-num">{metrics.totalScore}<small>/100</small></span>
                    </div>
                    <div className="sb-metric-lbl">SalesGod-Fit</div>
                    <div className="sb-metric-sub" style={{ color: metrics.verdict.color }}>{metrics.verdict.label}</div>
                  </div>
                  <div className={`sb-metric ${isMonthlyManual ? 'sb-metric-manual' : ''}`}>
                    <div className="sb-metric-top"><span className="sb-metric-num">{formatMoneyCompact(effectiveMonthly)}</span></div>
                    <div className="sb-metric-lbl">Wert / Monat</div>
                    <div className="sb-metric-sub">{isMonthlyManual ? 'manuell gesetzt' : `${formatMoneyCompact(metrics.ourAnnual)} / Jahr`}</div>
                  </div>
                  <div className="sb-metric">
                    <div className="sb-metric-top"><span className="sb-metric-num">{metrics.emailCount}</span></div>
                    <div className="sb-metric-lbl">E-Mail-Texte</div>
                    <div className="sb-metric-sub">gespeichert</div>
                  </div>
                  {cumChecklistTotal > 0 && (
                    <div className={`sb-metric ${cumChecklistOpen === 0 ? 'sb-metric-ok' : ''}`}>
                      <div className="sb-metric-top"><span className="sb-metric-num">{cumChecklistDone}<small>/{cumChecklistTotal}</small></span></div>
                      <div className="sb-metric-lbl">Checkliste</div>
                      <div className="sb-metric-sub">{cumChecklistOpen === 0 ? 'alle erledigt' : `${cumChecklistOpen} offen`}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Präsentationstermin & Countdown – nur in der Sales-Phase relevant.
                  Fehlt der Termin, wird der Block hervorgehoben und ein klarer
                  Action-Button bietet das direkte Festlegen an. */}
              {inSalesPhase && (
                <div className={`sb-pres ${cd ? `sb-pres-${cd.tone}` : ''} ${!presentationAt ? 'sb-pres-need' : ''}`}>
                  <div className="sb-pres-head">
                    <span>Präsentationstermin</span>
                    {presentationAt && customer.presentationSource && customer.presentationSource !== 'manual' && (
                      <span className="sb-pres-src" title="Automatisch aus den Infos erkannt">
                        {customer.presentationSource === 'ai' ? 'von KI erkannt' : 'aus Daten erkannt'}
                      </span>
                    )}
                  </div>
                  {presentationAt ? (
                    <div className="sb-pres-info">
                      <span className="sb-pres-date">{new Date(presentationAt).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      <span className={`sb-pres-cd sb-cd-${cd.tone}`}>{cd.tone === 'over' ? 'vorbei · nächster Schritt nötig' : cd.label}</span>
                    </div>
                  ) : (
                    <div className="sb-pres-cta">
                      <div className="sb-pres-cta-text">
                        <strong>Noch kein Präsentationstermin festgelegt.</strong>
                        <span>Der Termin ist der wichtigste nächste Schritt – jetzt fixieren, damit der Countdown startet.</span>
                      </div>
                      <button type="button" className="sb-pres-cta-btn" onClick={openPresPicker}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                        Termin festlegen
                      </button>
                    </div>
                  )}
                  {presentationAt && (
                    <div className={`sb-pres-ready ${allDone ? 'sb-pres-ready-ok' : ''}`}>
                      {allItems.length === 0
                        ? '—'
                        : allDone
                          ? '✓ Alles erledigt – bereit für den Termin'
                          : `Noch offen: ${openCount} von ${allItems.length} Punkten`}
                    </div>
                  )}
                  <div className="sb-pres-edit">
                    <input ref={presInputRef} type="datetime-local" value={toLocalInput(presentationAt)} onChange={setPresentation} />
                    {presentationAt && (
                      <button type="button" className="sb-pres-clear" onClick={() => onPatch({ presentationAt: null, presentationSource: null })} title="Termin entfernen">entfernen</button>
                    )}
                  </div>
                </div>
              )}

              {/* Kick-off-Termin & Countdown – Vorbereitungsphase (Angebot/Setup/Kick-off) */}
              {inPrepPhase && (
                <div className={`sb-pres ${kcd ? `sb-pres-${kcd.tone}` : ''}`}>
                  <div className="sb-pres-head">
                    <span>Kick-off-Termin</span>
                    {kickoffAt && customer.kickoffSource && customer.kickoffSource !== 'manual' && (
                      <span className="sb-pres-src" title="Automatisch erkannt">aus Daten erkannt</span>
                    )}
                  </div>
                  {kickoffAt ? (
                    <div className="sb-pres-info">
                      <span className="sb-pres-date">{new Date(kickoffAt).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      <span className={`sb-pres-cd sb-cd-${kcd.tone}`}>{kcd.tone === 'over' ? 'jetzt · Kick-off-Call' : kcd.label}</span>
                    </div>
                  ) : (
                    <p className="sb-pres-empty">Kein Kick-off-Termin gesetzt. Sobald er vereinbart ist, hier eintragen – dann läuft der Countdown und der Kontakt wandert automatisch ins Setup bzw. in den Kick-off-Call.</p>
                  )}
                  {kickoffAt && superStatus === SETUP_SUPER_STATUS && (
                    <div className={`sb-pres-ready ${allDone ? 'sb-pres-ready-ok' : ''}`}>
                      {allItems.length === 0
                        ? '—'
                        : allDone
                          ? '✓ Setup komplett – bereit für den Kick-off'
                          : `Technisches Setup: noch ${openCount} von ${allItems.length} offen`}
                    </div>
                  )}
                  <div className="sb-pres-edit">
                    <input ref={kickoffInputRef} type="datetime-local" value={toLocalInput(kickoffAt)} onChange={setKickoff} />
                    {kickoffAt && (
                      <button type="button" className="sb-pres-clear" onClick={() => onPatch({ kickoffAt: null, kickoffSource: null })} title="Termin entfernen">entfernen</button>
                    )}
                  </div>
                </div>
              )}

              {/* Setup-Info-Drop – nur bei „Angebot unterzeichnet": hier kommen die
                  Antworten des Kunden auf die Setup-Checkliste (meist per Mail) an und
                  werden direkt den benötigten Infos (Setup-Details) zugeordnet. */}
              {isOfferSigned && (
                <SetupInfoDropPanel
                  busy={!!dropBusy}
                  onDrop={(dump) => onDropSetupInfo(dump)}
                />
              )}

              {/* Call-Info-Drop – optional, daher bewusst weiter unten. Infos aus dem
                  jeweiligen Call (Kennenlern-/Präsentations-/Kick-off-Call) ins
                  Research (Kunden-Brain) ablegen. Im Setup kein Call und bei „Angebot
                  unterzeichnet" der Setup-Info-Drop -> jeweils ausgeblendet. */}
              {!isLost && superStatus !== SETUP_SUPER_STATUS && !isOfferSigned && (
                <CallInfoDropPanel
                  stageLabel={stageLabel}
                  callLabel={callLabel}
                  busy={!!dropBusy}
                  onDrop={(dump) => onDropCallInfo(dump, stageLabel)}
                />
              )}

              {/* Launch-Phase-Überblick: Tages-Status des 4-Wochen-Hochlaufs –
                  welche Tage reportet sind, welche fehlen, ob heute erledigt ist.
                  Erscheint, sobald die Launch Phase im Research Center gestartet wurde. */}
              <WarmupOverview warmup={warmup} now={now} />

              {/* Report-Infos (Vorab-Überblick) */}
              {(reportFacts.length > 0 || website) && (
                <div className="sb-info">
                  <div className="sb-info-head">Infos aus dem Report</div>
                  {website && (
                    <div className="sb-info-row">
                      <span className="sb-info-key">Website</span>
                      <a className="sb-info-link" href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noreferrer">{website.replace(/^https?:\/\//, '')} ↗</a>
                    </div>
                  )}
                  {reportFacts.map((f) => (
                    <div key={f.label} className="sb-info-row">
                      <span className="sb-info-key">{f.label}</span>
                      <span className="sb-info-val">{f.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Protocool-Spiegel: ob & was bereits eingereicht wurde (ab Setup-Phase) */}
              {reachedSetup && <ProtocoolSummary customer={customer} slug={slug} team={team} onPatch={onPatch} />}

              {/* Notizen – frei angelegte Zwischeninfos zum Kontakt, ganz unten im
                  Überblick. Dieselben Einträge erscheinen chronologisch im Verlauf. */}
              {overviewNotes.length > 0 && (
                <div className="sb-pinnotes">
                  <div className="sb-pinnotes-head">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></svg>
                    <span>Notizen</span>
                  </div>
                  {overviewNotes.map((n) => (
                    <div key={n.id} className="sb-pinnote">
                      <div className="sb-pinnote-body">
                        {n.title && <span className="sb-pinnote-title">{n.title}</span>}
                        {n.body && <span className="sb-pinnote-text">{n.body}</span>}
                        {typeof n.createdAt === 'number' && (
                          <span className="sb-pinnote-date">{new Date(n.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                        )}
                      </div>
                      <button type="button" className="sb-pinnote-del" onClick={() => removeNoteEntry(n.id)} title="Notiz löschen">×</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Tab: Checkliste ── */}
          {tab === 'checklist' && (
            <div className="sb-checklist">
              <div className="sb-checklist-head">
                <span>Checkliste · aktuelle Phase</span>
                <div className="sb-cl-head-right">
                  {allItems.length > 0 && <span className={`sb-checklist-prog ${allDone ? 'sb-checklist-prog-ok' : ''}`}>{doneCount}/{allItems.length}</span>}
                  <div className="sb-add">
                    <button type="button" className="sb-add-btn" onClick={() => setAddMenuOpen((v) => !v)} title="Hinzufügen" aria-expanded={addMenuOpen}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    </button>
                    {addMenuOpen && (
                      <>
                        <div className="sb-add-backdrop" onClick={() => setAddMenuOpen(false)} />
                        <div className="sb-add-menu" role="menu">
                          <button type="button" onClick={() => openAdd('section')}>
                            <strong>Checklisten-Abschnitt</strong>
                            <span>Neuer Block mit eigenen Punkten zum Abhaken</span>
                          </button>
                          <button type="button" onClick={() => openAdd('alert')}>
                            <strong>Alert</strong>
                            <span>Hinweis, der ab einem Datum bei „Nächste Schritte" erscheint</span>
                          </button>
                          <button type="button" onClick={() => openAdd('note')}>
                            <strong>Notiz</strong>
                            <span>Zwischeninfo – im Überblick unten &amp; im Verlauf</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {allItems.length > 0 && (
                <div className={`sb-checklist-bar ${allDone ? 'sb-checklist-bar-ok' : ''}`}>
                  <span className="sb-checklist-bar-fill" style={{ width: `${Math.round((doneCount / allItems.length) * 100)}%` }} />
                </div>
              )}

              {sections.length === 0 && customSections.length === 0 && (
                <p className="sb-checklist-empty">Für diese Phase ist noch keine Checkliste hinterlegt. Mit „+" kannst du einen eigenen Abschnitt anlegen.</p>
              )}

              {sections.map((sec) => (
                <div key={sec.title} className="sb-cl-section">
                  <h4>{sec.title}</h4>
                  {sec.items.map((it) => {
                    const done = !!checklist[it.id];
                    const isAuto = !!checklistAuto[it.id];
                    const hasAuto = typeof it.auto === 'function';
                    return (
                      <label key={it.id} className={`sb-cl-item ${done ? 'sb-cl-item-done' : ''}`}>
                        <input type="checkbox" checked={done} onChange={() => toggleItem(it.id)} />
                        <span className="sb-cl-box" aria-hidden="true" />
                        <span className="sb-cl-label">{it.label}</span>
                        {it.shared && <span className="sb-cl-badge sb-cl-badge-client" title="Für den Kunden auf der Research-Seite als Fortschritt sichtbar">kundensichtbar</span>}
                        {isAuto && <span className="sb-cl-badge sb-cl-badge-auto" title="Automatisch aus den Report-Daten erkannt">aus Daten</span>}
                        {hasAuto && !isAuto && !done && <span className="sb-cl-badge sb-cl-badge-missing" title="Information fehlt in den Daten">fehlt</span>}
                      </label>
                    );
                  })}
                </div>
              ))}

              {/* Eigene, frei angelegte Abschnitte – manuell befüllbar, jederzeit
                  löschbar. Erledigt-Status teilt sich dieselbe checklist-Map. */}
              {customSections.map((sec) => {
                const items = sec.items;
                const secDone = items.filter((it) => checklist[it.id]).length;
                return (
                  <div key={sec.id} className="sb-cl-section sb-cl-section-custom">
                    <div className="sb-cl-custom-head">
                      <h4>{sec.title}</h4>
                      {items.length > 0 && <span className={`sb-cl-custom-prog ${secDone === items.length ? 'sb-cl-custom-prog-ok' : ''}`}>{secDone}/{items.length}</span>}
                      <span className="sb-cl-custom-tag">eigener Abschnitt</span>
                      <button type="button" className="sb-cl-section-del" onClick={() => removeCustomSection(sec.id)} title="Abschnitt löschen">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
                      </button>
                    </div>
                    {items.length === 0 && addItemSection !== sec.id && (
                      <p className="sb-cl-custom-empty">Noch keine Punkte – unten hinzufügen.</p>
                    )}
                    {items.map((it) => {
                      const done = !!checklist[it.id];
                      return (
                        <div key={it.id} className="sb-cl-item-row">
                          <label className={`sb-cl-item ${done ? 'sb-cl-item-done' : ''}`}>
                            <input type="checkbox" checked={done} onChange={() => toggleItem(it.id)} />
                            <span className="sb-cl-box" aria-hidden="true" />
                            <span className="sb-cl-label">{it.label}</span>
                          </label>
                          <button type="button" className="sb-cl-item-del" onClick={() => removeCustomItem(sec.id, it.id)} title="Punkt entfernen">×</button>
                        </div>
                      );
                    })}
                    {addItemSection === sec.id ? (
                      <form
                        className="sb-cl-additem"
                        onSubmit={(e) => { e.preventDefault(); addCustomItem(sec.id, addItemLabel); setAddItemLabel(''); }}
                      >
                        <input
                          type="text"
                          autoFocus
                          value={addItemLabel}
                          placeholder="Neuer Punkt …"
                          onChange={(e) => setAddItemLabel(e.target.value)}
                          onBlur={() => { if (!addItemLabel.trim()) { setAddItemSection(null); setAddItemLabel(''); } }}
                          onKeyDown={(e) => { if (e.key === 'Escape') { setAddItemSection(null); setAddItemLabel(''); } }}
                        />
                        <button type="submit" className="sb-cl-additem-save" disabled={!addItemLabel.trim()}>Hinzufügen</button>
                      </form>
                    ) : (
                      <button type="button" className="sb-cl-additem-btn" onClick={() => { setAddItemSection(sec.id); setAddItemLabel(''); }}>
                        + Punkt hinzufügen
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Verlauf der Checklisten: abgeschlossene Phasen unter der aktuellen,
                  read-only und einklappbar – aktuelle Phase bleibt oben im Fokus. */}
              {previousColumns.length > 0 && (
                <div className="sb-archive sb-archive-inline">
                  <button
                    type="button"
                    className={`sb-archive-toggle ${showArchive ? 'sb-archive-toggle-open' : ''}`}
                    onClick={() => setShowArchive((v) => !v)}
                    aria-expanded={showArchive}
                  >
                    <span className="sb-archive-toggle-chevron" aria-hidden="true">›</span>
                    <span className="sb-archive-toggle-text">Verlauf · abgeschlossene Phasen</span>
                    <span className="sb-archive-toggle-count">{previousColumns.length}</span>
                  </button>
                  {showArchive && previousColumns.map((col) => {
                    const items = (CHECKLISTS[col.superStatusId] || []).flatMap((sec) => sec.items);
                    if (items.length === 0) return null;
                    const done = items.filter((it) => checklist[it.id]).length;
                    const full = done === items.length;
                    return (
                      <div key={col.superStatusId} className="sb-archive-phase">
                        <div className="sb-archive-phase-head">
                          <span className="sb-archive-phase-name">{col.superStatusLabel}</span>
                          <span className={`sb-archive-phase-prog ${full ? 'sb-archive-phase-prog-ok' : ''}`}>{done}/{items.length}</span>
                        </div>
                        <div className="sb-archive-items">
                          {items.map((it) => (
                            <label key={it.id} className={`sb-archive-item ${checklist[it.id] ? 'sb-archive-item-done' : ''}`}>
                              <input type="checkbox" className="sb-archive-check" checked={!!checklist[it.id]} onChange={() => toggleItem(it.id)} />
                              <span className="sb-archive-mark" aria-hidden="true">{checklist[it.id] ? '✓' : '○'}</span>
                              <span className="sb-archive-label">{it.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Deal & Team ── */}
          {tab === 'deal' && (
            <>
              {/* ── Auf einen Blick ──
                  Die vier Zahlen, um die es in diesem Tab geht, bevor man
                  irgendetwas aufklappt: Was zahlt der Kunde, was kostet das
                  Team, was bleibt. Alles darunter sind nur die Stellschrauben
                  dazu. */}
              <div className="sb-dealkpi">
                <div className="sb-dealkpi-top">
                  <span className="sb-dealkpi-title">Auf einen Blick</span>
                  <span className={`sb-dealkpi-badge sb-dealkpi-badge-${dealState.tone}`}>{dealState.label}</span>
                  <button type="button" className="sb-dealkpi-edit" onClick={toggleContractForm}>
                    {showContractForm ? 'Regler schließen' : contract ? 'Konditionen ändern' : 'Konditionen festlegen'}
                  </button>
                </div>
                <div className="sb-dealkpi-grid">
                  <div className="sb-dealkpi-item">
                    <span className="sb-dealkpi-k">Retainer / Monat</span>
                    <span className="sb-dealkpi-v">{fmtEur(previewPayoutBase || 0)}</span>
                    <span className="sb-dealkpi-s">
                      {kpiMonthlyTax.rate > 0 ? `${fmtEur(kpiMonthlyTax.gross)} brutto` : 'netto = brutto'}
                    </span>
                  </div>
                  <div className="sb-dealkpi-item">
                    <span className="sb-dealkpi-k">Kunde zahlt / Termin</span>
                    <span className="sb-dealkpi-v">{previewCommissionOn ? fmtEur(previewApptPrice) : '—'}</span>
                    <span className="sb-dealkpi-s">
                      {previewCommissionOn
                        ? `davon ${fmtEur(previewCommissionBase)} provisionsfähig`
                        : 'Provisionsmodell aus'}
                    </span>
                  </div>
                  <div className="sb-dealkpi-item">
                    <span className="sb-dealkpi-k">Team / Monat</span>
                    <span className="sb-dealkpi-v">{fmtEur(totalPayout)}</span>
                    <span className="sb-dealkpi-s">
                      {assignedMembers.length === 0
                        ? 'niemand zugewiesen'
                        : `${assignedMembers.length} ${assignedMembers.length === 1 ? 'Person' : 'Personen'}${previewCommissionOn ? ` · bei ${apptPreview} Terminen` : ''}`}
                    </span>
                  </div>
                  <div className="sb-dealkpi-item">
                    <span className="sb-dealkpi-k">Bleibt / Monat</span>
                    <span className={`sb-dealkpi-v ${monthRevenue != null && monthRevenue - totalPayout < 0 ? 'sb-dealkpi-neg' : ''}`}>
                      {monthRevenue != null ? fmtEur(monthRevenue - totalPayout) : '–'}
                    </span>
                    <span className="sb-dealkpi-s">
                      {monthRevenue > 0
                        ? `${Math.round(((monthRevenue - totalPayout) / monthRevenue) * 100)}% von ${fmtEur(monthRevenue)} Umsatz`
                        : 'noch kein Umsatz hinterlegt'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Vertrag & Abrechnung – Payment-Counter */}
              <div className={`sb-contract ${contractActive ? 'sb-contract-active' : ''}`}>
                <div className="sb-contract-head">
                  <span>Vertrag &amp; Abrechnung</span>
                  {contractActive
                    ? <span className={`sb-contract-badge ${contractStillRunning ? '' : 'sb-contract-badge-end'}`}>{contractStillRunning ? 'aktiv' : 'Laufzeit beendet'}</span>
                    : contractIsDraft ? <span className="sb-contract-badge sb-contract-badge-draft">Konditionen festgelegt</span>
                      : contract ? <span className="sb-contract-badge sb-contract-badge-off">pausiert</span> : null}
                </div>

                {contractActive ? (
                  <>
                    {/* Nur die Fakten, die oben NICHT stehen – die Beträge sind
                        schon im Kopf, hier geht es um Zeit, Setup und Steuer. */}
                    <div className="sb-contract-facts">
                      <span><b>Start</b> {fmtDay(contract.startAt)}</span>
                      <span><b>Laufzeit</b> {contract.durationMonths > 0 ? `${contract.durationMonths} Monate` : 'unbegrenzt'}</span>
                      <span>
                        <b>Setup</b> {contract.setupFee > 0
                          ? `${fmtEuro(contract.setupFee)} netto · ${fmtEuro(setupTax.gross)} brutto`
                          : 'keine'}
                      </span>
                      {commissionOn && (
                        <span><b>Inklusiv-Termine</b> {includedAppointmentsFor(contract)} / Monat</span>
                      )}
                      <span className="sb-contract-tax-badge">{contractTaxInfo.badge}{contract.country ? ` · ${countryName(contract.country)}` : ''}</span>
                    </div>
                    <div className="sb-contract-next">
                      {contract.durationMonths > 0 && (
                        <span>Monat {Math.min(contract.durationMonths, monthsElapsed(contract.startAt, now) + 1)} von {contract.durationMonths}</span>
                      )}
                      {nextDue
                        ? <span>Nächste Rechnung fällig: <strong>{fmtDay(nextDue.dueDate)}</strong> · <strong>{fmtEuro(nextDue.amount)}</strong> brutto</span>
                        : <span>Keine weitere Rechnung fällig.</span>}
                    </div>
                    <div className="sb-contract-actions">
                      <button type="button" className="sb-contract-btn sb-contract-btn-ghost" onClick={toggleContractForm}>
                        {showContractForm ? 'Schließen' : 'Bearbeiten'}
                      </button>
                      <button type="button" className="sb-contract-btn sb-contract-btn-off" onClick={deactivateContract}>Pausieren</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="sb-contract-intro">
                      {contractIsDraft
                        ? (reachedKickoff
                          ? 'Konditionen sind festgelegt, die Abrechnung läuft aber noch nicht. Mit „Zahlung aktivieren“ startest du den Counter – ab dann erscheint monatlich automatisch eine fällige Zahlung im Zahlungen-Dashboard.'
                          : 'Konditionen sind festgelegt – es werden noch keine Rechnungen erzeugt. Die Abrechnung startest du beim Kick-off-Call.')
                        : contract
                          ? 'Vertrag ist pausiert – es werden keine neuen Rechnungen erzeugt.'
                          : reachedKickoff
                            ? 'Beim Kick-off-Call die monatliche Zahlung starten (Setup-Gebühr optional). Beträge netto – Brutto/IVA werden automatisch aus dem Kundenland abgeleitet. Ab Aktivierung erscheint monatlich automatisch eine fällige Zahlung im Zahlungen-Dashboard.'
                            : 'Konditionen – Retainer, Terminpreis und Provisionsmodell – kannst du hier schon jetzt festlegen. Abgerechnet wird erst ab dem Kick-off-Call.'}
                    </p>
                    {contractIsDraft && (
                      <div className="sb-contract-facts">
                        <span><b>Start</b> {fmtDay(contract.startAt)}</span>
                        <span><b>Laufzeit</b> {contract.durationMonths > 0 ? `${contract.durationMonths} Monate` : 'unbegrenzt'}</span>
                        <span><b>Setup</b> {contract.setupFee > 0 ? `${fmtEuro(contract.setupFee)} netto` : 'keine'}</span>
                        <span className="sb-contract-tax-badge">{contractTaxInfo.badge}{contract.country ? ` · ${countryName(contract.country)}` : ''}</span>
                      </div>
                    )}
                    <div className="sb-contract-actions">
                      {contract && contractEverStarted && (
                        <button type="button" className="sb-contract-btn sb-contract-btn-primary" onClick={reactivateContract}>Fortsetzen</button>
                      )}
                      {contractIsDraft && reachedKickoff && (
                        <button type="button" className="sb-contract-btn sb-contract-btn-primary" onClick={startContract}>Zahlung aktivieren</button>
                      )}
                      <button
                        type="button"
                        className={`sb-contract-btn ${contractIsDraft && reachedKickoff ? 'sb-contract-btn-ghost' : 'sb-contract-btn-primary'}`}
                        onClick={toggleContractForm}
                      >
                        {showContractForm
                          ? 'Abbrechen'
                          : contract
                            ? 'Konditionen bearbeiten'
                            : reachedKickoff ? 'Zahlung aktivieren' : 'Konditionen festlegen'}
                      </button>
                    </div>
                  </>
                )}

                {showContractForm && (
                  <div className="sb-contract-form">
                    <div className="sb-contract-form-row">
                      <label className="sb-contract-field">
                        <span>Startdatum</span>
                        <input type="date" value={contractDraft.startDate} onChange={(e) => setCDraft('startDate', e.target.value)} />
                      </label>
                    </div>
                    <div className="sb-slider-grid">
                      <SliderField
                        label="Monatsbetrag netto"
                        value={draftMonthlyNet}
                        min={0}
                        max={10000}
                        step={50}
                        onChange={(n) => setCDraft('monthlyAmount', String(n))}
                        caption={draftMonthlyNet === 0
                          ? 'Kein Retainer – reines Provisionsmodell'
                          : `${fmtEuro(draftMonthlyTax.gross)} brutto${draftMonthlyTax.rate > 0 ? ` inkl. ${draftMonthlyTax.rate}% IVA` : ''}`}
                      />
                      <SliderField
                        label="Setup-Gebühr netto"
                        value={draftSetupNet}
                        min={0}
                        max={10000}
                        step={100}
                        onChange={(n) => setCDraft('setupFee', String(n))}
                        caption={draftSetupNet === 0 ? 'Keine Setup-Gebühr' : 'Einmalig zum Start'}
                      />
                      <SliderField
                        label="Laufzeit"
                        value={draftDurationMonths}
                        min={0}
                        max={36}
                        step={1}
                        unit="Mt."
                        onChange={(n) => setCDraft('durationMonths', String(n))}
                        caption={draftDurationMonths === 0
                          ? 'Unbegrenzt – läuft bis zur Kündigung'
                          : `Endet nach ${draftDurationMonths} Monaten`}
                      />
                    </div>
                    {/* Provisionsmodell: pro Termin abrechnen – mit oder ohne Retainer. */}
                    <div className="sb-comm">
                      <button
                        type="button"
                        className={`sb-comm-toggle ${contractDraft.commissionEnabled ? 'is-on' : ''}`}
                        role="switch"
                        aria-checked={!!contractDraft.commissionEnabled}
                        onClick={() => setCDraft('commissionEnabled', !contractDraft.commissionEnabled)}
                      >
                        <span className="sb-comm-toggle-knob" />
                        <span className="sb-comm-toggle-text">
                          Provisionsmodell {contractDraft.commissionEnabled ? 'aktiv' : 'aus'}
                        </span>
                      </button>
                      <p className="sb-comm-note">
                        {contractDraft.commissionEnabled
                          ? 'Termine werden einzeln abgerechnet. Das Team bekommt die gestaffelte Provision auf die Provisionsbasis – zusätzlich zum Monatsbetrag (0 = reines Provisionsmodell).'
                          : 'Nur Retainer. Extra-Termine laufen weiter über den pauschalen Bonus je Mitarbeiter.'}
                      </p>

                      {contractDraft.commissionEnabled && (
                        <>
                          <div className="sb-slider-grid">
                            <SliderField
                              label="Kunde zahlt / Termin netto"
                              value={draftApptPrice}
                              min={0}
                              max={1000}
                              step={10}
                              onChange={(n) => setCDraft('appointmentPrice', String(n))}
                              caption={draftApptPrice === 0 ? 'Noch kein Terminpreis festgelegt' : 'Das steht auf der Kundenrechnung'}
                            />
                            <SliderField
                              label="Davon provisionsfähig"
                              value={draftCommissionBase}
                              min={0}
                              max={Math.max(1000, draftApptPrice)}
                              step={10}
                              warn={draftCommissionBase > draftApptPrice && draftApptPrice > 0}
                              onChange={(n) => setCDraft('commissionBase', String(n))}
                              caption={draftCommissionBase > draftApptPrice && draftApptPrice > 0
                                ? 'Liegt über dem Kundenpreis – negative Marge'
                                : `${fmtEuro(Math.max(0, draftApptPrice - draftCommissionBase))} bleiben außen vor (Tooling, Leads …)`}
                            />
                            <SliderField
                              label="Im Retainer enthalten"
                              value={draftIncludedAppts}
                              min={0}
                              max={20}
                              step={1}
                              unit="Term."
                              onChange={(n) => setCDraft('includedAppointments', String(n))}
                              caption={draftIncludedAppts === 0
                                ? 'Jeder Termin wird einzeln abgerechnet'
                                : `Ab dem ${draftIncludedAppts + 1}. Termin wird extra abgerechnet`}
                            />
                          </div>
                          <p className="sb-comm-hint">
                            Wer bei diesem Kunden welche Staffel bekommt, legst du unten unter <strong>Provision bei diesem Kunden</strong> fest –
                            die Beträge dort rechnen live mit diesen Reglern.
                          </p>
                        </>
                      )}
                    </div>

                    <div className="sb-contract-form-row">
                      <label className="sb-contract-field">
                        <span>Land des Kunden {detectedCountry ? '· autom. erkannt' : '· bitte wählen'}</span>
                        <select value={contractDraft.country} onChange={(e) => setCDraft('country', e.target.value)}>
                          <option value="">— Land wählen —</option>
                          {COUNTRY_OPTIONS.map((code) => (
                            <option key={code} value={code}>{countryName(code)}{code === detectedCountry ? ' (erkannt)' : ''}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="sb-contract-taxprev">
                      <div className="sb-contract-taxprev-head">
                        <span>Steuer automatisch:</span>
                        <span className="sb-contract-tax-badge">{draftTaxInfo.badge}</span>
                      </div>
                      <div className="sb-contract-taxprev-row">
                        <span>Monatlich</span>
                        <span>
                          {fmtEuro(draftMonthlyNet)} netto
                          {draftMonthlyTax.rate > 0
                            ? ` + ${draftMonthlyTax.rate}% IVA = ${fmtEuro(draftMonthlyTax.gross)} brutto`
                            : ` = ${fmtEuro(draftMonthlyTax.gross)} brutto (keine IVA)`}
                        </span>
                      </div>
                      {draftSetupNet > 0 && (
                        <div className="sb-contract-taxprev-row">
                          <span>Setup</span>
                          <span>
                            {fmtEuro(draftSetupNet)} netto
                            {draftSetupTax.rate > 0
                              ? ` + ${draftSetupTax.rate}% IVA = ${fmtEuro(draftSetupTax.gross)} brutto`
                              : ` = ${fmtEuro(draftSetupTax.gross)} brutto (keine IVA)`}
                          </span>
                        </div>
                      )}
                      <p className="sb-contract-taxprev-note">{draftTaxInfo.note}</p>
                    </div>
                    <button
                      type="button"
                      className="sb-contract-btn sb-contract-btn-primary sb-contract-btn-wide"
                      onClick={() => saveContract(!contractActive && reachedKickoff)}
                    >
                      {contractActive
                        ? 'Änderungen speichern'
                        : reachedKickoff ? 'Zahlung aktivieren & Counter starten' : 'Konditionen speichern'}
                    </button>
                    {!contractActive && !reachedKickoff && (
                      <p className="sb-contract-form-note">
                        Es wird noch nichts abgerechnet. Die Zahlung startest du beim Kick-off-Call – die Konditionen sind dann schon hinterlegt.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Ausgangswert aus dem Report – nur die Vorbelegung für den
                  Retainer-Regler, deshalb bewusst klein und unter dem Vertrag.
                  Sobald Konditionen stehen, zählt der Vertragsbetrag. */}
              <div className="sb-basis">
                <span className="sb-basis-k">Ausgangswert aus dem Report</span>
                <span className="sb-basis-field">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={monthlyInput}
                    placeholder={reportMonthly != null ? String(Math.round(reportMonthly)) : '0'}
                    onChange={(e) => { const v = e.target.value; setMonthlyInput(v); scheduleSave('monthly', () => persistMonthly(v)); }}
                    onBlur={saveMonthly}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                  <span className="sb-basis-cur">€ / Monat</span>
                </span>
                {isMonthlyManual && (
                  <button type="button" className="sb-basis-reset" onClick={clearMonthly} title="Auf berechneten Wert zurücksetzen">zurücksetzen</button>
                )}
                <span className="sb-basis-hint">
                  {reportMonthly != null
                    ? (isMonthlyManual
                        ? `manuell · berechnet wären ${formatMoneyCompact(reportMonthly)}`
                        : `leer = berechnet aus dem Report (${formatMoneyCompact(reportMonthly)})`)
                    : 'kein Report-Wert vorhanden'}
                </span>
              </div>

              {/* Einmalzahlungen (Setup-Fee, Beratung …) – unabhängig vom Vertrag,
                  damit auch ohne laufenden Retainer abgerechnet werden kann. */}
              <OneTimePayments customer={customer} team={teamMap} packages={setupPackages} onPatch={onPatch} />


              {/* Angebot */}
              <div className="sb-offer">
                <div className="sb-offer-head">
                  <span>Angebot</span>
                  <button
                    type="button"
                    className={`sb-offer-toggle ${dealFlags.offerSent ? 'sb-offer-toggle-on' : ''}`}
                    onClick={() => onPatch({ offerSentOverride: !dealFlags.offerSent })}
                    role="switch"
                    aria-checked={dealFlags.offerSent}
                    title="Angebot als gesendet markieren oder wieder zurücknehmen. Überschreibt die automatische Ableitung aus dem Pipeline-Status."
                  >
                    <span className="sb-offer-toggle-track"><span className="sb-offer-toggle-knob" /></span>
                    <span className="sb-offer-toggle-text">{dealFlags.offerSent ? 'gesendet ✓' : 'noch nicht gesendet'}</span>
                  </button>
                </div>
                {dealFlags.offerSentOverridden && (
                  <button
                    type="button"
                    className="sb-offer-auto"
                    onClick={() => onPatch({ offerSentOverride: null })}
                    title="Manuelle Markierung entfernen und wieder automatisch aus dem Pipeline-Status ableiten."
                  >
                    Manuell gesetzt · auf automatisch zurücksetzen (würde: {dealFlags.offerSentDerived ? 'gesendet' : 'nicht gesendet'})
                  </button>
                )}
                <div className="sb-offer-edit">
                  <input
                    type="url"
                    className="sb-offer-input"
                    value={offerUrl}
                    placeholder="Link zum Angebot (PDF / Google Doc / Drive …)"
                    onChange={(e) => { const v = e.target.value; setOfferUrl(v); scheduleSave('offerUrl', () => { const tv = v.trim(); if ((customerRef.current.offerUrl || '') !== tv) onPatch({ offerUrl: tv }); }); }}
                    onBlur={saveOfferUrl}
                  />
                  {customer.offerUrl && (
                    <a className="sb-offer-open" href={customer.offerUrl} target="_blank" rel="noreferrer" title="Angebot öffnen">öffnen ↗</a>
                  )}
                </div>

                {/* Angebot als Datei hochladen & ablegen (Firebase Storage) */}
                <div className="sb-offer-upload">
                  <div className="sb-offer-or">oder Datei hochladen</div>
                  {customer.offerFile ? (
                    <div className="sb-offer-file">
                      <span className="sb-offer-file-icon" aria-hidden="true">📄</span>
                      <a
                        className="sb-offer-file-name"
                        href={customer.offerFile.url}
                        target="_blank"
                        rel="noreferrer"
                        title="Angebot öffnen"
                      >
                        {customer.offerFile.name || 'Angebot'}
                      </a>
                      {typeof customer.offerFile.size === 'number' && (
                        <span className="sb-offer-file-size">{fmtFileSize(customer.offerFile.size)}</span>
                      )}
                      <button
                        type="button"
                        className="sb-offer-file-replace"
                        onClick={() => offerFileInputRef.current && offerFileInputRef.current.click()}
                        disabled={offerUploadBusy}
                        title="Andere Datei hochladen (ersetzt die aktuelle)"
                      >
                        ersetzen
                      </button>
                      <button
                        type="button"
                        className="sb-offer-file-remove"
                        onClick={removeOfferFile}
                        disabled={offerUploadBusy}
                        title="Datei entfernen"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="sb-offer-upload-btn"
                      onClick={() => offerFileInputRef.current && offerFileInputRef.current.click()}
                      disabled={offerUploadBusy}
                    >
                      <span aria-hidden="true">⬆</span> {offerUploadBusy ? 'Lädt hoch…' : 'Angebot hochladen'}
                    </button>
                  )}
                  <input
                    ref={offerFileInputRef}
                    type="file"
                    className="sb-offer-file-input"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,image/*,application/pdf"
                    onChange={onOfferFileChange}
                    hidden
                  />
                  {offerUploadBusy && (
                    <div className="sb-offer-progress" role="progressbar" aria-valuenow={offerUploadPct} aria-valuemin={0} aria-valuemax={100}>
                      <span className="sb-offer-progress-bar" style={{ width: `${offerUploadPct}%` }} />
                      <span className="sb-offer-progress-pct">{offerUploadPct}%</span>
                    </div>
                  )}
                  {offerUploadError && <p className="sb-offer-error">{offerUploadError}</p>}
                </div>

                <span className="sb-offer-hint">Angebot als Link hinterlegen oder als Datei hochladen (PDF, Doc, Bild … max. 25 MB). Die Datei wird sicher gespeichert.</span>
              </div>

              {/* ── Team & Provision (ein Block) ──
                  Hier wird beantwortet, was im Alltag zählt: Wer ist bei DIESEM
                  Kunden Outreach Manager, wer Teamleiter – und was heißt das in
                  Euro? Der Regler oben spielt die Terminzahl durch, die Karten
                  darunter zeigen je Person den Verdienst samt Zusammensetzung,
                  und die Staffel-Tabelle ist das, was man dem Outreach Manager
                  zeigt bzw. weiterschickt. Gerechnet wird mit derselben Logik,
                  die später die echte Abrechnung erzeugt. */}
              <div className="sb-prov">
                  <div className="sb-prov-head">
                    <span>Team &amp; Provision</span>
                    <span className="sb-prov-head-sub">
                      {assignedMembers.length === 0
                        ? 'Noch niemand zugewiesen'
                        : `${assignedMembers.length} ${assignedMembers.length === 1 ? 'Person' : 'Personen'} · ${fmtEur(totalPayout)} / Monat`}
                      {previewCommissionOn ? '' : ' · Provisionsmodell aus, es zählt nur der Retainer'}
                      {showContractForm ? ' · rechnet live mit den Reglern oben' : ''}
                    </span>
                  </div>

                  {previewCommissionOn && (
                    <div className="sb-appt-preview">
                      <div className="sb-appt-preview-top">
                        <span>Vorschau bei</span>
                        <strong>{apptPreview} {apptPreview === 1 ? 'Termin' : 'Terminen'}</strong>
                        <span>im Monat</span>
                      </div>
                      <input
                        type="range" min={0} max={60} step={1} value={apptPreview}
                        style={{ '--sb-fill': `${(apptPreview / 60) * 100}%` }}
                        onChange={(e) => setApptPreview(Number(e.target.value))}
                      />
                      <div className="sb-appt-preview-sum">
                        <span>Umsatz {fmtEur(monthRevenue)}</span>
                        <span>Team {fmtEur(totalPayout)}</span>
                        <span className={monthRevenue - totalPayout < 0 ? 'sb-appt-neg' : 'sb-appt-pos'}>
                          Marge {fmtEur(monthRevenue - totalPayout)}
                        </span>
                      </div>
                    </div>
                  )}

                  {splitTotal > 0 && splitRows.length > 0 && (
                    <div className="sb-prov-split">
                      <div className="sb-prov-split-bar">
                        <span className="sb-prov-seg sb-prov-seg-firma" style={{ width: `${splitPct(splitTotal - totalPayout)}%` }} />
                        {splitRows.map((r) => (
                          <span
                            key={r.id}
                            className={`sb-prov-seg sb-prov-seg-${r.roleId || 'none'}`}
                            style={{ width: `${splitPct(r.amount)}%` }}
                            title={`${r.name}: ${fmtEur(r.amount)}`}
                          />
                        ))}
                      </div>
                      <div className="sb-prov-legend">
                        <span><i className="sb-prov-dot sb-prov-dot-firma" />Firma {fmtEur(splitTotal - totalPayout)}</span>
                        {splitRows.map((r) => (
                          <span key={r.id}>
                            <i className={`sb-prov-dot sb-prov-dot-${r.roleId || 'none'}`} />
                            {r.name} {fmtEur(r.amount)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {teamList.length === 0 ? (
                    <p className="sb-prov-empty">
                      Noch keine Mitarbeiter angelegt. Lege sie im Tab <strong>Settings</strong> an – danach kannst du sie
                      hier diesem Kunden zuweisen.
                    </p>
                  ) : (
                    <>
                      <div className="sb-prov-list">
                        {assignedSorted.map((m) => {
                          const roleId = memberRoleAt(m);
                          const share = memberShare(m);
                          const retainer = memberMonthlyPayout(m, previewPayoutBase);
                          const st = share && apptPreview > 0 ? tierStatus(roleId, apptPreview) : null;
                          const tiersOpen = openTier === m.id;
                          return (
                            <div className={`sb-prov-item ${roleId ? `sb-prov-item-${roleId}` : ''}`} key={m.id}>
                              <div className="sb-prov-item-top">
                                <span className="sb-prov-name">
                                  {m.name || '(ohne Name)'}
                                  {m.active === false && <span className="sb-prov-inactive">inaktiv</span>}
                                </span>
                                <span className="sb-prov-total">
                                  <span className="sb-prov-total-val">{fmtEur(retainer + (share ? share.amount : 0))}</span>
                                  <span className="sb-prov-total-lbl">/ Monat</span>
                                </span>
                              </div>

                              <div className="sb-prov-roles">
                                <span className="sb-prov-roles-lbl">Rolle</span>
                                <div className="sb-prov-roles-pills" role="group" aria-label={`Provisionsrolle von ${m.name || m.id}`}>
                                  {CUSTOMER_ROLE_OPTIONS.map((o) => {
                                    const active = (roleId || 'none') === o.id;
                                    return (
                                      <button
                                        type="button"
                                        key={o.id}
                                        aria-pressed={active}
                                        className={`sb-prov-role-pill ${active ? `sb-prov-role-pill-on sb-prov-role-pill-${o.id}` : ''}`}
                                        onClick={() => setMemberRole(m.id, o.id)}
                                      >
                                        {o.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {share ? (
                                <>
                                  <div className="sb-prov-break">
                                    <span className="sb-prov-chip">Staffel <b>{fmtEur(share.tierAmount)}</b></span>
                                    <span className="sb-prov-chip">Unlocks <b>{fmtEur(share.unlockAmount)}</b></span>
                                    {share.minimumApplied && (
                                      <span className="sb-prov-chip sb-prov-chip-min">Minimum {fmtEur(share.minimum)} greift</span>
                                    )}
                                    {retainer > 0 && (
                                      <span className="sb-prov-chip">
                                        + {fmtEur(retainer)} {m.payType === 'fixed' ? 'Fix' : `Provision (${Number(m.commissionPercent) || 0}%)`}
                                      </span>
                                    )}
                                  </div>
                                  {st ? (
                                    <div className="sb-prov-rate">
                                      Termin-Provision <b>{fmtEur(share.amount)}</b>
                                      {st.current && ` · Stufe ${tierRange(st.current)} = ${fmtEur(share.ratePerAppointment)} je Termin`}
                                      {st.next && (
                                        <span className="sb-prov-next">
                                          {' '}· ab {st.next.from} Terminen {fmtEur(st.next.rate * st.scale)} je Termin
                                          {st.nextUnlock ? ` + Unlock ${fmtEur(st.nextUnlock.bonus * st.scale)} ab ${st.nextUnlock.at}` : ''}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="sb-prov-rate">Noch keine Termine eingestellt – zieh den Regler nach rechts.</div>
                                  )}
                                </>
                              ) : (
                                <div className="sb-prov-rate sb-prov-rate-muted">
                                  {m.payType === 'fixed'
                                    ? `Fixum ${fmtEur(Number(m.fixedAmount) || 0)} / Monat`
                                    : `${Number(m.commissionPercent) || 0}% vom Monatsbetrag = ${fmtEur(retainer)}`}
                                  {Number(m.appointmentBonus) > 0 && ` · ${fmtEur(Number(m.appointmentBonus))} Bonus je Extra-Termin`}
                                  {previewCommissionOn
                                    ? ' · keine Termin-Staffel gewählt'
                                    : ' · Termin-Staffel greift erst mit aktivem Provisionsmodell'}
                                </div>
                              )}

                              <div className="sb-prov-actions">
                                {share && (
                                  <button type="button" className="sb-prov-link" onClick={() => setOpenTier(tiersOpen ? null : m.id)}>
                                    {tiersOpen ? 'Staffel ausblenden' : 'Ganze Staffel ansehen'}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="sb-prov-link"
                                  onClick={() => onCopyText(provMessage(m), `Provisions-Text für ${m.name || 'das Mitglied'} kopiert.`)}
                                  title="Text für die Person kopieren – ohne Kundenpreis und Marge"
                                >
                                  Text kopieren
                                </button>
                                <button type="button" className="sb-prov-link sb-prov-link-off" onClick={() => toggleMember(m.id)}>
                                  entfernen
                                </button>
                              </div>

                              {/* Die komplette Staffel dieser Person – direkt in ihrer
                                  Karte, weil „wie viel" und „wie gestaffelt" dieselbe
                                  Frage sind. Genau das zeigt man der Person auch. */}
                              {tiersOpen && share && (
                                <div className={`sb-prov-tier sb-prov-tier-${roleId}`}>
                                  <div className="sb-prov-tier-head">
                                    <span className="sb-prov-tier-title">{COMMISSION_ROLES[roleId].label}-Staffel</span>
                                    <span className="sb-prov-tier-who">
                                      auf {fmtEur(previewCommissionBase)} Provisionsbasis umgerechnet
                                      {' '}(Plan: {fmtEur(COMMISSION_PLAN_BASE)})
                                    </span>
                                  </div>
                                  <div className="sb-prov-tier-cols">
                                    <table className="sb-prov-tier-table">
                                      <thead>
                                        <tr><th>{COMMISSION_ROLES[roleId].tierHeading}</th><th>Satz / Termin</th></tr>
                                      </thead>
                                      <tbody>
                                        {COMMISSION_ROLES[roleId].tiers.map((t) => {
                                          const active = apptPreview >= t.from && (t.to == null || apptPreview <= t.to);
                                          return (
                                            <tr key={`${t.from}-${t.to}`} className={active ? 'sb-prov-tier-active' : ''}>
                                              <td>{tierRange(t)}</td>
                                              <td>{fmtEur(t.rate * tierScale(roleId))}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                    <div>
                                      <div className="sb-prov-unlock-head">{COMMISSION_ROLES[roleId].unlockHeading}</div>
                                      <div className="sb-prov-unlock-list">
                                        {COMMISSION_ROLES[roleId].unlocks.map((u) => (
                                          <div key={u.at} className={`sb-prov-unlock ${apptPreview >= u.at ? 'sb-prov-unlock-on' : ''}`}>
                                            <span>ab {u.at} Terminen</span>
                                            <span>{apptPreview >= u.at ? '✓ ' : ''}+{fmtEur(u.bonus * tierScale(roleId))}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {previewCommissionOn && assignedMembers.length > 0 && roleHolders('outreach_manager').length === 0 && (
                        <p className="sb-prov-warn">
                          Niemand ist als <strong>{COMMISSION_ROLES.outreach_manager.label}</strong> gesetzt – dann entsteht bei
                          diesem Kunden keine Termin-Provision.
                        </p>
                      )}

                      {unassignedMembers.length > 0 && (
                        <div className="sb-prov-add">
                          <span className="sb-prov-add-lbl">
                            {assignedMembers.length === 0 ? 'Wer arbeitet an diesem Kunden?' : 'Weitere zuweisen'}
                          </span>
                          <div className="sb-prov-add-chips">
                            {unassignedMembers.map((m) => (
                              <button type="button" key={m.id} className="sb-prov-add-chip" onClick={() => toggleMember(m.id)}>
                                + {m.name || '(ohne Name)'}
                                {m.role ? <span className="sb-prov-add-role">{m.role}</span> : null}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

              {/* Reporting-Erinnerungen: wer wird an das fällige Reporting erinnert.
                  Steht direkt unter der Team-Zuweisung, weil die Auswahl auf denselben
                  Teammitgliedern (und ihren Adressen aus den Settings) aufbaut. */}
              <ReportingMailPanel
                customer={customer}
                team={teamMap}
                reporting={currentReporting}
                settings={mailSettings}
                now={now}
                onPatch={onPatch}
              />
            </>
          )}

          {/* ── Tab: Ansprechpartner ── */}
          {tab === 'contacts' && (
            <>
              {/* Ansprechpartner – wer ist der Owner / mit wem kommunizieren wir.
                  Bewusst getrennt vom (kundensichtbaren) Research: personenbezogene
                  Daten leben nur hier in der Pipeline. Editierbar, mehrere möglich. */}
              <ContactsPanel
                key={slug}
                contacts={customer.contacts}
                onChange={(list) => onPatch({ contacts: list.length ? list : null })}
              />
            </>
          )}

          {/* ── Tab: Verlauf ── */}
          {tab === 'history' && (
            <>
              {/* Zeit-Tracking */}
              <div className="sb-track">
                <div className="sb-track-head">Tracking</div>
                <div className="sb-track-row">
                  <span className="sb-track-key">Erstkontakt</span>
                  <span className="sb-track-val">{createdAt ? new Date(createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'}</span>
                </div>
                <div className={`sb-track-row sb-track-highlight ${firstCustomerAt ? 'sb-track-done' : ''}`}>
                  <span className="sb-track-key">{firstCustomerAt ? 'Erstkontakt → erster Kunde' : 'Im Funnel seit'}</span>
                  <span className="sb-track-val">{timeToCustomer != null ? formatDuration(timeToCustomer) : '–'}{firstCustomerAt ? '' : ' (läuft)'}</span>
                </div>

                <div className="sb-track-sub">Verweildauer je Status {longest && durations.length > 1 ? `· am längsten: ${longest.label}` : ''}</div>
                <div className="sb-track-bars">
                  {durations.map((d) => {
                    const pct = longest && longest.ms > 0 ? Math.max(3, Math.round((d.ms / longest.ms) * 100)) : 0;
                    return (
                      <div key={d.id} className="sb-track-bar-row">
                        <span className="sb-track-bar-label">{d.label}{d.active ? ' ·' : ''}</span>
                        <span className="sb-track-bar-track"><span className={`sb-track-bar-fill ${d.active ? 'sb-track-bar-active' : ''}`} style={{ width: `${pct}%` }} /></span>
                        <span className="sb-track-bar-val">{formatDuration(d.ms)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notizen & Alerts · chronologischer Verlauf: wann etwas hinzugefügt
                  wurde. Hier lassen sich Einträge auch nachträglich löschen. */}
              {noteLog.length > 0 && (
                <div className="sb-log">
                  <div className="sb-log-head">Notizen &amp; Alerts</div>
                  <ul className="sb-log-list">
                    {noteLog.map((n) => (
                      <li key={n.id} className={`sb-log-item ${n.done ? 'sb-log-item-done' : ''}`}>
                        <span className={`sb-log-kind sb-log-kind-${n.kind}`}>{n.kind === 'alert' ? 'Alert' : 'Notiz'}</span>
                        <div className="sb-log-body">
                          {n.title && <span className="sb-log-title">{n.title}</span>}
                          {n.body && <span className="sb-log-text">{n.body}</span>}
                          <span className="sb-log-meta">
                            {typeof n.createdAt === 'number'
                              ? `hinzugefügt ${new Date(n.createdAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}`
                              : ''}
                            {n.kind === 'alert' && typeof n.showAt === 'number'
                              ? ` · anzeigen ab ${new Date(n.showAt).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}`
                              : ''}
                            {n.kind === 'alert' && n.done ? ' · erledigt' : ''}
                          </span>
                        </div>
                        <button type="button" className="sb-log-del" onClick={() => removeNoteEntry(n.id)} title="Eintrag löschen">×</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notizen */}
              <div className="sb-notes">
                <div className="sb-notes-head">Freie Notizen</div>
                <textarea
                  className="sb-notes-area"
                  value={notes}
                  placeholder="Freie Notizen aus dem Gespräch …"
                  onChange={(e) => { const v = e.target.value; setNotes(v); scheduleSave('notes', () => { if ((customerRef.current.notes || '') !== v) onPatch({ notes: v }); }); }}
                  onBlur={saveNotes}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer: Solange der Kontakt nicht in der Lost-Phase ist, gibt es keinen
            Lösch-Button – stattdessen "Als Lost markieren" (mit Pflicht-Grund).
            Erst ein Kontakt in der Lost-Phase lässt sich endgültig löschen. */}
        <footer className="sb-drawer-foot">
          {isLost ? (
            <button type="button" className="sb-delete-btn" onClick={() => setConfirmDelete(true)}>
              Kontakt löschen
            </button>
          ) : (
            <button type="button" className="sb-lost-btn" onClick={openLostModal}>
              Als Lost markieren
            </button>
          )}
        </footer>

        {confirmDelete && (
          <div className="sb-confirm-overlay" onClick={() => setConfirmDelete(false)}>
            <div className="sb-confirm" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 className="sb-confirm-title">Kontakt wirklich löschen?</h3>
              <p className="sb-confirm-text">
                <strong>{customer.companyName || slug}</strong> wird endgültig aus der Sales Pipeline
                entfernt – inklusive Research-Report, Passwort und aller zugehörigen Zahlungen.
                Das kann nicht rückgängig gemacht werden.
              </p>
              <div className="sb-confirm-actions">
                <button type="button" className="sb-confirm-cancel" onClick={() => setConfirmDelete(false)}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="sb-confirm-delete"
                  onClick={() => { setConfirmDelete(false); onDelete(); }}
                >
                  Endgültig löschen
                </button>
              </div>
            </div>
          </div>
        )}

        {showLostModal && (
          <div className="sb-confirm-overlay" onClick={() => setShowLostModal(false)}>
            <div className="sb-confirm sb-lost-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 className="sb-confirm-title">Als Lost markieren</h3>
              <p className="sb-confirm-text">
                Wo ging <strong>{customer.companyName || slug}</strong> verloren – und warum?
                Der Kontakt wandert in die Lost-Phase. Erst dort lässt er sich löschen.
              </p>
              <div className="sb-lost-form">
                <label className="sb-ctl">
                  <span>Verloren in</span>
                  <select value={lostStatusId} onChange={(e) => { setLostStatusId(e.target.value); setLostReason(''); setLostReasonCustom(''); }}>
                    {LOST_PHASE.superStatuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </label>
                <label className="sb-ctl">
                  <span>Grund</span>
                  <select value={lostReason} onChange={(e) => setLostReason(e.target.value)}>
                    <option value="">— Grund wählen —</option>
                    {(lostColumn.subStatuses || []).map((r) => <option key={r} value={r}>{r}</option>)}
                    <option value={LOST_REASON_CUSTOM}>Sonstiger Grund …</option>
                  </select>
                </label>
                {lostReason === LOST_REASON_CUSTOM && (
                  <label className="sb-ctl">
                    <span>Eigener Grund</span>
                    <input
                      type="text"
                      className="sb-lost-custom-input"
                      value={lostReasonCustom}
                      placeholder="Grund frei eingeben …"
                      autoFocus
                      onChange={(e) => setLostReasonCustom(e.target.value)}
                    />
                  </label>
                )}
              </div>
              <div className="sb-confirm-actions">
                <button type="button" className="sb-confirm-cancel" onClick={() => setShowLostModal(false)}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="sb-confirm-lost"
                  disabled={!effectiveLostReason}
                  onClick={() => { onDeclareLost(lostStatusId, effectiveLostReason); setShowLostModal(false); onClose(); }}
                >
                  Als Lost markieren
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add-Modal: eigener Abschnitt / Alert / Notiz (über den Plus-Button). */}
        {addType && (
          <div className="sb-confirm-overlay" onClick={closeAdd}>
            <div className="sb-confirm sb-add-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 className="sb-confirm-title">
                {addType === 'section' ? 'Checklisten-Abschnitt hinzufügen' : addType === 'alert' ? 'Alert hinzufügen' : 'Notiz hinzufügen'}
              </h3>
              <p className="sb-confirm-text">
                {addType === 'section' && 'Lege einen eigenen Block an, den du danach mit Punkten zum Abhaken füllst.'}
                {addType === 'alert' && 'Ein Hinweis, der – optional ab einem Datum – im Überblick bei „Nächste Schritte" auftaucht.'}
                {addType === 'note' && 'Eine Zwischeninfo zum Kontakt. Sie erscheint im Überblick ganz unten und im Verlauf.'}
              </p>
              <div className="sb-add-form">
                {addType === 'section' ? (
                  <label className="sb-ctl">
                    <span>Titel des Abschnitts</span>
                    <input
                      type="text"
                      autoFocus
                      value={draftTitle}
                      placeholder="z. B. Onboarding-Unterlagen"
                      onChange={(e) => setDraftTitle(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }}
                    />
                  </label>
                ) : (
                  <>
                    <label className="sb-ctl">
                      <span>Titel <em>(optional)</em></span>
                      <input
                        type="text"
                        autoFocus
                        value={draftTitle}
                        placeholder={addType === 'alert' ? 'z. B. Vertrag nachfassen' : 'z. B. Budget-Hinweis'}
                        onChange={(e) => setDraftTitle(e.target.value)}
                      />
                    </label>
                    <label className="sb-ctl">
                      <span>Text</span>
                      <textarea
                        className="sb-add-textarea"
                        value={draftBody}
                        placeholder={addType === 'alert' ? 'Woran soll erinnert werden?' : 'Was soll festgehalten werden?'}
                        onChange={(e) => setDraftBody(e.target.value)}
                      />
                    </label>
                    {addType === 'alert' && (
                      <label className="sb-ctl">
                        <span>Anzeigen ab <em>(optional)</em></span>
                        <input
                          type="date"
                          value={draftShowAt}
                          onChange={(e) => setDraftShowAt(e.target.value)}
                        />
                      </label>
                    )}
                  </>
                )}
              </div>
              <div className="sb-confirm-actions">
                <button type="button" className="sb-confirm-cancel" onClick={closeAdd}>Abbrechen</button>
                <button
                  type="button"
                  className="sb-confirm-add"
                  disabled={addType === 'section' ? !draftTitle.trim() : (!draftTitle.trim() && !draftBody.trim())}
                  onClick={submitAdd}
                >
                  Hinzufügen
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

export default StructureBoard;
